import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import MobileNav from "../components/MobileNav";
import SignatureSelector from "../components/SignatureSelector";
import AdvancedUploader from "../components/AdvancedUploader";
import PackageReviewEditor from "../components/PackageReviewEditor";
import HealthDashboardModal from "../components/HealthDashboardModal";
import FTCPreviewModal from "../components/FTCPreviewModal";
import SectionClientSelector from "../components/SectionClientSelector";
// REMOVED: UnifiedDisputeCenter - replaced with modular pages
// import UnifiedDisputeCenter from "../components/UnifiedDisputeCenter";
import ScoreInputModal from "../components/ScoreInputModal";
import MultiFileDropzone from "../components/MultiFileDropzone";
import FastCreditUploader from "../components/FastCreditUploader";
// NEW MODULAR PAGES - Per User Spec Messages 423 & 426
import SignDeclarationPage from "../components/SignDeclarationPage";
import DisputeAccountsPageNew from "../components/DisputeAccountsPageNew";
import InquiriesDisputePageNew from "../components/InquiriesDisputePageNew";
import FTCReportBuilderNew from "../components/FTCReportBuilderNew";
import BankruptcyDisputePage from "../components/BankruptcyDisputePage";
import BureauFormEditor from "../components/BureauFormEditor";
import SupportingDocumentsPage from "../components/SupportingDocumentsPage";
import FinalReviewPage from "../components/FinalReviewPage";
import MailPacketsPage from "../components/MailPacketsPage";
import CreditScoresPage from "../components/CreditScoresPage";
import CursiveSignatureSelector from "../components/CursiveSignatureSelector";
import SniperImportPage from "../components/SniperImportPage";
import LatePaymentDisputePage from "../components/LatePaymentDisputePage";
import LatePaymentRemovalPage from "../components/LatePaymentRemovalPage";
import SupplementalLettersPage from "../components/SupplementalLettersPage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ChevronRight,
  ChevronLeft,
  Check,
  User,
  FileText,
  CreditCard,
  Search,
  Shield,
  Upload,
  PenTool,
  Package,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Home,
  X,
  Eye,
  Trash2,
  Plus,
  Calendar,
  Phone,
  Mail,
  Bell,
  Link as LinkIcon,
  FileUp,
  Clock,
  Wrench,
  AlertTriangle,
  RefreshCw,
  GraduationCap,
  HelpCircle,
  Save,
  ListChecks,
  ArrowRight,
  Edit3,
  Zap,
  Lock,
  Type,
  Info,
  Activity,
  Sparkles,
  Truck,
  FolderDown,
  Briefcase,
  ClipboardPaste,
  TrendingUp,
  Scale,
  IdCard,
  ClipboardCheck,
  ClipboardList,
} from "lucide-react";
import CombinedPasteStep from "../components/CombinedPasteStep";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper function to color-code credit scores
const getScoreColor = (score) => {
  if (score >= 740) return 'text-green-400';
  if (score >= 670) return 'text-yellow-400';
  if (score >= 580) return 'text-orange-400';
  return 'text-red-400';
};

// ============ GLOBAL DEDUPLICATION HELPERS ============
// CRITICAL: This is the single source of truth for account deduplication
// Rule: Same canonical name + same date + same balance = SAME account (merge bureaus)

// Master creditor name mappings - ALL variations must map to ONE canonical name
const CREDITOR_CANONICAL_NAMES = {
  // ═══════════════════════════════════════════════════════════════════════════════
  // AVANT / WEBBANK variations - CRITICAL: Must all merge to ONE account
  // ═══════════════════════════════════════════════════════════════════════════════
  'AVANT': 'AVANT WEBBANK',
  'AVANT LLC': 'AVANT WEBBANK',
  'AVANT WEBBANK': 'AVANT WEBBANK',
  'AVANT-WEBBANK': 'AVANT WEBBANK',
  'AVANT - WEBBANK': 'AVANT WEBBANK',
  'AVANT LLC- WEB BANK': 'AVANT WEBBANK',
  'AVANT LLC WEB BANK': 'AVANT WEBBANK',
  'AVANT LLC-WEB BANK': 'AVANT WEBBANK',
  'WEBBANKAVANT': 'AVANT WEBBANK',
  'WEBBANK AVANT': 'AVANT WEBBANK',
  'WEBBANK/AVANT': 'AVANT WEBBANK',
  'WEBBANK-AVANT': 'AVANT WEBBANK',
  'WEB BANK AVANT': 'AVANT WEBBANK',
  'WEB BANK/AVANT': 'AVANT WEBBANK',
  'WEBBANK': 'AVANT WEBBANK',  // If WEBBANK alone, assume AVANT

  // ═══════════════════════════════════════════════════════════════════════════════
  // CELTIC BANK / CONTINENTAL variations - CRITICAL: Must all merge to ONE account
  // ═══════════════════════════════════════════════════════════════════════════════
  'CELTIC': 'CELTIC BANK CONTINENTAL',
  'CELTIC BANK': 'CELTIC BANK CONTINENTAL',
  'CELTIC- CONT': 'CELTIC BANK CONTINENTAL',
  'CELTIC-CONT': 'CELTIC BANK CONTINENTAL',
  'CELTIC CONT': 'CELTIC BANK CONTINENTAL',
  'CELTIC BANK- CONTFINCO': 'CELTIC BANK CONTINENTAL',
  'CELTIC BANK-CONTFINCO': 'CELTIC BANK CONTINENTAL',
  'CELTIC BANK CONTFINCO': 'CELTIC BANK CONTINENTAL',
  'CELTIC BANK CONTINENTAL': 'CELTIC BANK CONTINENTAL',
  'CONTFINCO': 'CELTIC BANK CONTINENTAL',
  'CONT FINCO': 'CELTIC BANK CONTINENTAL',
  'CONTINENTAL FINANCE': 'CELTIC BANK CONTINENTAL',
  'CELTIC/CONTFINCO': 'CELTIC BANK CONTINENTAL',

  // ═══════════════════════════════════════════════════════════════════════════════
  // MERRICK BANK variations - CRITICAL: Must all merge to ONE account
  // ═══════════════════════════════════════════════════════════════════════════════
  'MERRICK': 'MERRICK BANK',
  'MERRICK BANK': 'MERRICK BANK',
  'MERRICK BK': 'MERRICK BANK',
  'MERRICKBANK': 'MERRICK BANK',
  'MERRICK BANK NA': 'MERRICK BANK',

  // First Premier variations
  'FIRST PREMIER': 'FIRST PREMIER',
  'FIRST PREMIER BANK': 'FIRST PREMIER',
  'FST PREMIER': 'FIRST PREMIER',
  'PREMIER BKCRD': 'FIRST PREMIER',
  'PREMIER BK CRD': 'FIRST PREMIER',
  'PREMIER BANKCARD': 'FIRST PREMIER',
  
  // LVNV / Resurgent variations
  'LVNV FUNDING': 'LVNV FUNDING LLC',
  'LVNV FUNDING LLC': 'LVNV FUNDING LLC',
  'RESURGENT': 'LVNV FUNDING LLC',
  'RESURGENT LVNV': 'LVNV FUNDING LLC',
  'RESURGENT-LVNV': 'LVNV FUNDING LLC',
  'RESURGENT – LVNV': 'LVNV FUNDING LLC',
  'RESURGENT LVNV FUNDING': 'LVNV FUNDING LLC',
  
  // Jefferson Capital variations - CRITICAL: Equifax uses "JEFFERSON CAPITAL LL"
  'JEFFERSON CAPITAL': 'JEFFERSON CAPITAL SYSTEMS',
  'JEFFERSON CAPITAL SYSTEMS': 'JEFFERSON CAPITAL SYSTEMS',
  'JEFFERSON CAPITAL SYSTEMS LLC': 'JEFFERSON CAPITAL SYSTEMS',
  'JEFFERSON CAPITAL SYST': 'JEFFERSON CAPITAL SYSTEMS',
  'JEFFERSON CAPITAL SYS': 'JEFFERSON CAPITAL SYSTEMS',
  'JEFFERSON CAPITAL LL': 'JEFFERSON CAPITAL SYSTEMS',  // Equifax truncation
  'JEFFERSON CAPITAL LLC': 'JEFFERSON CAPITAL SYSTEMS',
  'JEFFERSON CAP': 'JEFFERSON CAPITAL SYSTEMS',
  'JCS': 'JEFFERSON CAPITAL SYSTEMS',
  
  // Dept of Ed / Aidvantage variations - CRITICAL: Multiple bureau truncations
  'DEPT OF ED': 'DEPT OF ED / AIDVANTAGE',
  'DEPT OF ED/AIDVANTAGE': 'DEPT OF ED / AIDVANTAGE',
  'DEPT OF ED/AIDVANTAG': 'DEPT OF ED / AIDVANTAGE',  // Equifax truncation (missing E)
  'DEPT OF ED / AIDVANTAGE': 'DEPT OF ED / AIDVANTAGE',
  'DEPT OF EDUCATION': 'DEPT OF ED / AIDVANTAGE',
  'AIDVANTAGE': 'DEPT OF ED / AIDVANTAGE',
  'DEPT OF ED AIDVANTAGE': 'DEPT OF ED / AIDVANTAGE',
  'US DEPT OF ED': 'DEPT OF ED / AIDVANTAGE',
  'DPT ED/AIDV': 'DEPT OF ED / AIDVANTAGE',  // TransUnion abbreviation
  'DPT ED AIDV': 'DEPT OF ED / AIDVANTAGE',
  'DPT ED': 'DEPT OF ED / AIDVANTAGE',
  'DPTED/AIDV': 'DEPT OF ED / AIDVANTAGE',
  'DPTED': 'DEPT OF ED / AIDVANTAGE',
  
  // Mission Federal variations - CRITICAL: Equifax uses "MISSION FEDERAL CRED"
  'MISSION FEDERAL': 'MISSION FEDERAL CREDIT UNION',
  'MISSION FEDERAL CR UN': 'MISSION FEDERAL CREDIT UNION',
  'MISSION FEDERAL CRED': 'MISSION FEDERAL CREDIT UNION',  // Equifax truncation
  'MISSION FEDERAL CREDIT UNION': 'MISSION FEDERAL CREDIT UNION',
  'MISSION FCU': 'MISSION FEDERAL CREDIT UNION',
  
  // Affirm variations
  'AFFIRM': 'AFFIRM INC',
  'AFFIRM INC': 'AFFIRM INC',
  
  // Capital One variations - CRITICAL: Equifax uses "CAPITAL ONE BANK USA"
  'CAPITAL ONE': 'CAPITAL ONE BANK USA',
  'CAPITAL ONE BANK': 'CAPITAL ONE BANK USA',
  'CAPITAL ONE BANK USA': 'CAPITAL ONE BANK USA',  // Equifax variation
  'CAP ONE': 'CAPITAL ONE BANK USA',
  'CAPITALONE': 'CAPITAL ONE BANK USA',
  
  // Additional First Premier variations (add unique ones)
  '1ST PREMIER': 'FIRST PREMIER',
  
  // Chrysler variations
  'CHRYSLERCAP': 'CHRYSLER CAPITAL',
  'CHRYSLER CAPITAL': 'CHRYSLER CAPITAL',
  'CHRYSLER CAP': 'CHRYSLER CAPITAL',
  
  // Exeter variations
  'EXETER FIN': 'EXETER FINANCE',
  'EXETER FINANCE': 'EXETER FINANCE',
  'EXETER FINANCE LLC': 'EXETER FINANCE',
  
  // Santander variations
  'SANTANDER': 'SANTANDER',
  'SANTANDER CONSUMER USA': 'SANTANDER',
  'SANTANDER CONSUMER': 'SANTANDER',
  
  // American Express variations
  'AMEX': 'AMERICAN EXPRESS',
  'AMERICAN EXPRESS': 'AMERICAN EXPRESS',
  'AMEX/DSNB': 'AMERICAN EXPRESS',
  
  // Kikoff variations - CRITICAL: Equifax uses "KIKOFF LENDING, LLC" (with comma)
  'KIKOFF': 'KIKOFF LENDING LLC',
  'KIKOFF LENDING': 'KIKOFF LENDING LLC',
  'KIKOFF LENDING LLC': 'KIKOFF LENDING LLC',
  'KIKOFF LENDING, LLC': 'KIKOFF LENDING LLC',  // Equifax variation with comma
  
  // Yamaha variations
  'YAMAHAMOTOFN': 'YAMAHA MOTOR FINANCE',
  'YAMAHA MOTOR': 'YAMAHA MOTOR FINANCE',
  'YAMAHA MOTOR FINANCE': 'YAMAHA MOTOR FINANCE',
  'YAMAHA': 'YAMAHA MOTOR FINANCE',
};

// Get canonical creditor name - MUST return consistent name for same creditor
// This is the CORE function that ensures DPT ED/AIDV and DEPT OF ED/AIDVANTAGE become the same
const getCanonicalCreditorName = (name) => {
  if (!name) return '';
  
  // Step 1: Uppercase and trim
  let rawName = name.toUpperCase().trim();
  
  // Step 2: Check exact matches BEFORE any modification (catches "DPT ED/AIDV" exactly)
  if (CREDITOR_CANONICAL_NAMES[rawName]) {
    return CREDITOR_CANONICAL_NAMES[rawName];
  }
  
  // Step 3: Check partial matches on raw name (catches variations)
  for (const [variant, canonical] of Object.entries(CREDITOR_CANONICAL_NAMES)) {
    if (rawName.includes(variant) || variant.includes(rawName)) {
      return canonical;
    }
  }
  
  // Step 4: Now normalize punctuation (including commas) and check again
  let cleanName = rawName.replace(/[–—\/\-,]/g, ' ').replace(/\s+/g, ' ').trim();
  
  if (CREDITOR_CANONICAL_NAMES[cleanName]) {
    return CREDITOR_CANONICAL_NAMES[cleanName];
  }
  
  // Step 5: Check partial matches on cleaned name
  for (const [variant, canonical] of Object.entries(CREDITOR_CANONICAL_NAMES)) {
    const cleanVariant = variant.replace(/[–—\/\-]/g, ' ').replace(/\s+/g, ' ').trim();
    if (cleanName.includes(cleanVariant) || cleanVariant.includes(cleanName)) {
      return canonical;
    }
  }
  
  // Step 6: Fallback - remove common suffixes
  const removeWords = ['LLC', 'INC', 'CORP', 'CO', 'NA', 'BANK', 'CREDIT', 'SERVICES', 
                       'FINANCIAL', 'FINANCE', 'USA', 'CONSUMER', 'FIN', 'SYSTEMS', 'SYS'];
  removeWords.forEach(word => {
    cleanName = cleanName.replace(new RegExp(`\\s*\\b${word}\\b\\s*`, 'gi'), ' ');
  });
  
  return cleanName.replace(/\s+/g, ' ').trim();
};

// Legacy function for backward compatibility
const getBaseCreditorName = getCanonicalCreditorName;
const normalizeCreditorName = getCanonicalCreditorName;

// Extract last 4 REAL digits from account number (ignore masks like XXXX, ****)
const extractLast4 = (accNum) => {
  if (!accNum) return '';
  const cleaned = String(accNum).replace(/[^0-9]/g, ''); // Only keep digits
  // If less than 4 real digits, return empty (not usable for dedup)
  if (cleaned.length < 4) return '';
  return cleaned.slice(-4);
};

// Generate canonical key for account deduplication
// CRITICAL RULES (30-year specialist):
// 1. Different account numbers = DIFFERENT accounts (never merge)
// 2. Same creditor + DIFFERENT date_opened = DIFFERENT accounts (student loans!)
// 3. Same creditor + same date + DIFFERENT balance = DIFFERENT accounts
// 4. Same creditor + same date + same balance + different bureaus = SAME account (merge bureaus)
//
// Key hierarchy - ALWAYS use date-based key for consistency:
// The account number last4 often varies or is masked (XXXX, ****) across bureaus
// Using date_opened + balance gives consistent merging across bureaus
const generateAccountCanonicalKey = (account) => {
  const canonicalCreditor = getCanonicalCreditorName(account.creditor_name);
  const balance = Math.round(account.balance || 0);
  const originalBalance = Math.round(account.original_balance || 0);
  const dateOpened = account.date_opened || '';
  
  // Use original_balance if available (critical for same-date student loans)
  const keyBalance = originalBalance > 0 ? originalBalance : balance;
  
  // ALWAYS use date-based key for consistent cross-bureau merging
  // Account numbers are often masked differently by each bureau
  // KEY = canonical creditor name + date opened + balance
  // This ensures:
  // - FIRST PREMIER (EX) + FST PREMIER (EQ) = ONE account (merged via canonical name)
  // - AIDVANTAGE $0 (01-15-2015) + AIDVANTAGE $0 (10-07-2015) = TWO accounts (different dates)
  // - AIDVANTAGE 10-10-2016 (orig $2208) + AIDVANTAGE 10-10-2016 (orig $4500) = TWO accounts (different amounts)
  return `${canonicalCreditor}:${dateOpened}:${keyBalance}`;
};

// Generate canonical key for inquiry deduplication
const generateInquiryCanonicalKey = (inquiry) => {
  const company = getBaseCreditorName(inquiry.company_name);
  const date = inquiry.inquiry_date || '';
  return `${company}:${date}`;
};

// Check if an account is derogatory (late, charge-off, collection, etc.)
const isDerogatory = (account) => {
  const status = (account.status || '').toLowerCase();
  const accountType = (account.account_type || '').toLowerCase();
  const remarks = (account.remarks || '').toLowerCase();
  const paymentStatus = (account.payment_status || '').toLowerCase();
  
  const derogatoryKeywords = [
    'late', 'past due', 'delinquent', 'charge-off', 'chargeoff', 'charged off',
    'collection', 'repo', 'repossession', 'bankruptcy', 'foreclosure',
    '30 days', '60 days', '90 days', '120 days', '150 days', '180 days',
    'written off', 'profit and loss', 'settled', 'paid collection',
    'dispute', 'disputed', 'pending'
  ];
  
  const combinedText = `${status} ${accountType} ${remarks} ${paymentStatus}`.toLowerCase();
  
  if (accountType === 'collection' || accountType === 'charge-off' || accountType === 'chargeoff') {
    return true;
  }
  
  for (const keyword of derogatoryKeywords) {
    if (combinedText.includes(keyword)) {
      return true;
    }
  }
  
  if (status === 'pending') {
    return true;
  }
  
  return false;
};

// Deduplicate accounts and merge bureau info
// CRITICAL RULES:
// 1. Same CANONICAL creditor + same date_opened + same balance = SAME account (merge bureaus)
// 2. Different date_opened = DIFFERENT accounts (even if same creditor/balance)
// 3. Same date but different original_balance = DIFFERENT accounts (student loans!)
// 4. AIDVANTAGE with 5 different loans = 5 separate tradelines
// 5. FIRST PREMIER / FST PREMIER / PREMIER BKCRD = ALL THE SAME (merge!)
// 6. Different CAPITAL ONE accounts (4169 vs 4170) = DIFFERENT accounts (keep separate!)
const deduplicateAccounts = (accounts) => {
  if (!accounts || !Array.isArray(accounts)) return [];
  
  // Use canonical key: CANONICAL creditor + date_opened + balance
  // This ensures consistent merging across bureaus regardless of account number masking
  const grouped = {};
  
  accounts.forEach(acc => {
    // Get CANONICAL creditor name (handles all variations)
    const canonicalCreditor = getCanonicalCreditorName(acc.creditor_name);
    const balance = Math.round(acc.balance || 0);
    const originalBalance = Math.round(acc.original_balance || 0);
    const dateOpened = acc.date_opened || '';
    const bureau = (acc.bureau || '').toLowerCase();
    
    // Parse bureau string
    let bureaus = [];
    if (acc.bureaus_present && Array.isArray(acc.bureaus_present)) {
      bureaus = acc.bureaus_present;
    } else if (bureau) {
      bureaus = bureau.split(',').map(b => b.trim().toLowerCase()).filter(b => b);
    }
    
    // Normalize bureau names
    bureaus = bureaus.map(b => {
      if (b.includes('trans') || b === 'tu') return 'transunion';
      if (b.includes('equi') || b === 'eq') return 'equifax';
      if (b.includes('exper') || b === 'ex') return 'experian';
      return b;
    });
    
    // CANONICAL KEY: canonical creditor + date + balance
    // This ensures FST PREMIER (TU) and FIRST PREMIER BANK (EX) merge into ONE row
    const keyBalance = originalBalance > 0 ? originalBalance : balance;
    const key = `${canonicalCreditor}:${dateOpened}:${keyBalance}`;
    
    if (!grouped[key]) {
      grouped[key] = {
        ...acc,
        creditor_name: canonicalCreditor, // Use canonical name for display
        original_creditor_name: acc.creditor_name, // Keep original for reference
        canonicalCreditor,
        balance,
        original_balance: originalBalance,
        bureaus_present: [...new Set(bureaus)],
        _merged_ids: { [bureau]: acc.id },
        _all_ids: [acc.id].filter(Boolean),
        _canonical_key: key
      };
    } else {
      // Same account from different bureau - MERGE bureau info only
      bureaus.forEach(b => {
        if (b && !grouped[key].bureaus_present.includes(b)) {
          grouped[key].bureaus_present.push(b);
        }
      });
      
      // Track merged IDs
      if (bureau && acc.id) {
        grouped[key]._merged_ids = grouped[key]._merged_ids || {};
        grouped[key]._merged_ids[bureau] = acc.id;
        grouped[key]._all_ids = grouped[key]._all_ids || [];
        if (!grouped[key]._all_ids.includes(acc.id)) {
          grouped[key]._all_ids.push(acc.id);
        }
      }
      
      // Keep the longer/better creditor name for display (but still use canonical for grouping)
      if ((acc.creditor_name || '').length > (grouped[key].original_creditor_name || '').length) {
        grouped[key].original_creditor_name = acc.creditor_name;
      }
      
      // Keep higher balance if different
      if (acc.balance > grouped[key].balance) {
        grouped[key].balance = acc.balance;
      }
      
      // Keep more complete data
      if (!grouped[key].account_number && acc.account_number) {
        grouped[key].account_number = acc.account_number;
      }
      if (!grouped[key].date_opened && acc.date_opened) {
        grouped[key].date_opened = acc.date_opened;
      }
      if (!grouped[key].original_balance && acc.original_balance) {
        grouped[key].original_balance = acc.original_balance;
      }
    }
  });
  
  // Convert to array and sort bureaus consistently
  const result = Object.values(grouped);
  
  result.forEach(acc => {
    if (acc.bureaus_present) {
      acc.bureaus_present.sort((a, b) => {
        const order = { 'equifax': 1, 'experian': 2, 'transunion': 3 };
        return (order[a] || 99) - (order[b] || 99);
      });
    }
  });
  
  // Validation: Check for any remaining duplicates (should be impossible)
  const keySet = new Set();
  result.forEach(acc => {
    if (keySet.has(acc._canonical_key)) {
      console.error(`[DEDUP] VALIDATION FAILED: Duplicate key found: ${acc._canonical_key}`);
    }
    keySet.add(acc._canonical_key);
  });
  
  // Debug logging
  console.log('=== ACCOUNT DEDUPLICATION COMPLETE ===');
  console.log(`Input: ${accounts.length} accounts -> Output: ${result.length} unique accounts`);
  result.forEach(acc => {
    const origBal = acc.original_balance ? ` (orig: $${acc.original_balance})` : '';
    console.log(`  ${acc.creditor_name} | ${acc.date_opened} | $${acc.balance}${origBal} | [${(acc.bureaus_present || []).join(', ')}]`);
  });
  
  return result;
};

// Filter to only derogatory accounts
const filterDerogatoryAccounts = (accounts) => {
  return accounts.filter(isDerogatory);
};

// Deduplicate inquiries and merge bureau info
const deduplicateInquiries = (inquiries) => {
  if (!inquiries || !Array.isArray(inquiries)) return [];
  
  const grouped = {};
  inquiries.forEach(inq => {
    const key = generateInquiryCanonicalKey(inq);
    const bureau = (inq.bureau || '').toLowerCase();
    
    let bureaus = [];
    if (inq.bureaus_present && Array.isArray(inq.bureaus_present)) {
      bureaus = inq.bureaus_present;
    } else if (bureau) {
      bureaus = bureau.split(',').map(b => b.trim().toLowerCase()).filter(b => b);
    }
    
    if (!grouped[key]) {
      grouped[key] = {
        ...inq,
        canonical_key: key,
        bureaus_present: [...bureaus]
      };
    } else {
      bureaus.forEach(b => {
        if (b && !grouped[key].bureaus_present.includes(b)) {
          grouped[key].bureaus_present.push(b);
        }
      });
    }
  });
  
  return Object.values(grouped);
};

// Bureau badge component - Uses full bureau names with colors
// EQUIFAX = RED, EXPERIAN = BLUE, TRANSUNION = YELLOW
const BureauBadges = ({ bureaus }) => {
  if (!bureaus || bureaus.length === 0) return null;
  
  // Map bureau names to colors per user request
  const bureauConfig = {
    'equifax': { name: 'EQUIFAX', bgColor: 'bg-red-500/20', textColor: 'text-red-400', borderColor: 'border-red-500/50' },
    'experian': { name: 'EXPERIAN', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400', borderColor: 'border-blue-500/50' },
    'transunion': { name: 'TRANSUNION', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400', borderColor: 'border-yellow-500/50' },
    'eq': { name: 'EQUIFAX', bgColor: 'bg-red-500/20', textColor: 'text-red-400', borderColor: 'border-red-500/50' },
    'ex': { name: 'EXPERIAN', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400', borderColor: 'border-blue-500/50' },
    'tu': { name: 'TRANSUNION', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400', borderColor: 'border-yellow-500/50' }
  };
  
  return (
    <div className="flex gap-1 flex-wrap items-center">
      {bureaus.map(b => {
        const bureau = b.toLowerCase().trim();
        const config = bureauConfig[bureau];
        
        if (config) {
          return (
            <span
              key={bureau}
              className={`px-2 py-0.5 text-xs font-bold rounded border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
            >
              {config.name}
            </span>
          );
        }
        
        // Fallback for unknown bureaus
        return (
          <span
            key={bureau}
            className="px-2 py-0.5 text-xs font-bold rounded border bg-gray-500/20 text-gray-300 border-gray-500/50"
          >
            {bureau.toUpperCase()}
          </span>
        );
      })}
    </div>
  );
};

// Step definitions - CORRECT WORKFLOW per User Requirements
// Upload → Signature → Declaration → Paste Accounts → Dispute → Supplemental → Inquiries → FTC → Bankruptcy → Supporting → Review → Mail
// STEP ORDER: Client Info → Signature → Declaration → UNIFIED Import/Paste → Dispute → Supplemental → Inquiries → FTC → Bankruptcy → Docs → Review → Mail
const STEPS = [
  { id: 1, title: "Client Information", icon: User, description: "Enter your personal details" },
  { id: 2, title: "Signature Selection", icon: PenTool, description: "Choose your cursive signature style" },
  { id: 3, title: "Handwritten Declaration", icon: FileText, description: "Download your declaration letter" },
  { id: 4, title: "Import & Paste Accounts", icon: ClipboardList, description: "All credit report data in one place" },
  { id: 5, title: "Dispute Accounts", icon: Shield, description: "Select accounts to dispute by bureau" },
  { id: 6, title: "Supplemental Letters", icon: FileText, description: "Add specialized dispute letters" },
  { id: 7, title: "Inquiries Dispute", icon: Search, description: "Select inquiries to dispute" },
  { id: 8, title: "FTC Report", icon: Shield, description: "Build FTC Identity Theft Report" },
  { id: 9, title: "Bankruptcy", icon: Scale, description: "Dispute fraudulent bankruptcy" },
  { id: 10, title: "Supporting Documents", icon: IdCard, description: "Upload ID and SSN card" },
  { id: 11, title: "Final Review", icon: ClipboardCheck, description: "Review all documents" },
  { id: 12, title: "Mail Packets", icon: Truck, description: "Mail dispute packets to bureaus" },
];

// ============ FRAUD CATEGORIES - 11 FTC Categories ============
const FRAUD_CATEGORIES = [
  { value: "CREDIT_CARD", label: "Credit Card" },
  { value: "LOAN_LEASE", label: "Loan/Lease" },
  { value: "AUTO", label: "Auto" },
  { value: "MOBILE", label: "Mobile/Phone" },
  { value: "UTILITY", label: "Utility" },
  { value: "RENTAL", label: "Rental" },
  { value: "MEDICAL", label: "Medical" },
  { value: "STUDENT_LOAN", label: "Student Loan" },
  { value: "REAL_ESTATE", label: "Real Estate" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "OTHER", label: "Other" }
];

// ============ DISPUTE LETTER TYPES ============
const LETTER_TYPES = [
  { value: "REGULAR", label: "Regular Dispute Letter", description: "Standard dispute for fraudulent accounts (listed by category)" },
  { value: "VERIFIED", label: "Verified Accounts Letter", description: "Round 2/3 - Accounts previously verified but still disputing" },
  { value: "RE_ADDED", label: "Re-Added Accounts Letter", description: "Accounts that were removed but re-added to report" },
  { value: "STUDENT_LOAN", label: "Student Loans Letter", description: "Specialized letter for student loan disputes" },
  { value: "CHILD_SUPPORT", label: "Child Support Letter", description: "Specialized letter for child support disputes" },
  { value: "LATE_PAYMENTS", label: "Late Payments Only Letter", description: "Dispute only late payment marks, not the account" }
];

export default function DisputeWizard() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  
  // ============ STATE PERSISTENCE - Get saved step from localStorage ============
  const getSavedStep = () => {
    if (!clientId || clientId === 'new') return 1;
    try {
      const saved = localStorage.getItem(`wizard_step_${clientId}`);
      return saved ? parseInt(saved, 10) : 1;
    } catch {
      return 1;
    }
  };
  
  const [currentStep, setCurrentStep] = useState(getSavedStep);
  
  // Save step to localStorage whenever it changes
  useEffect(() => {
    if (clientId && clientId !== 'new' && currentStep > 0) {
      localStorage.setItem(`wizard_step_${clientId}`, currentStep.toString());
    }
  }, [currentStep, clientId]);
  
  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // Go to previous step instead of leaving the page
      if (currentStep > 1) {
        event.preventDefault();
        setCurrentStep(prev => Math.max(1, prev - 1));
        window.history.pushState({ step: currentStep - 1 }, '');
      }
    };
    
    // Push initial state
    window.history.pushState({ step: currentStep }, '');
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentStep]);
  
  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentStep > 1) {
        e.preventDefault();
        e.returnValue = 'You have unsaved progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep]);
  
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [excludedAccounts, setExcludedAccounts] = useState(new Set()); // Track unchecked accounts
  const [inquiries, setInquiries] = useState([]);
  const [letters, setLetters] = useState([]);
  const [documents, setDocuments] = useState({ id_document: null, ssn_card: null });
  const [ftcReportGenerated, setFtcReportGenerated] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const [packageReady, setPackageReady] = useState({ equifax: false, experian: false, transunion: false });
  const [downloadingBureau, setDownloadingBureau] = useState(null);
  
  // Client History Selector State - for loading previous dispute wizard clients
  const [showClientSelector, setShowClientSelector] = useState(false);
  
  // Dispute Type Selection - MUST be declared early as it's used in validation functions
  // Options: 'identity_theft', 'inquiry_only', 'inaccuracies', 'late_payments'
  const [disputeType, setDisputeType] = useState(null);
  
  // Signature style selection (1-5) for cover letters and declaration
  const [signatureStyle, setSignatureStyle] = useState(1);
  const [signatureStyleName, setSignatureStyleName] = useState('Elegant Script');
  const [signatureImageData, setSignatureImageData] = useState(null);
  
  // Email sending states
  const [sendingEmail, setSendingEmail] = useState(null); // 'report', 'portal', 'status'
  const [emailSent, setEmailSent] = useState({ report: false, portal: false, status: false });
  
  // Document preview states
  const [idPreview, setIdPreview] = useState(null);
  const [ssnPreview, setSsnPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingSsn, setUploadingSsn] = useState(false);
  
  // Personal info extracted from credit report for PII removal
  const [reportPersonalInfo, setReportPersonalInfo] = useState({
    names_on_file: [],
    addresses_on_file: [],
    phones_on_file: [],
    employers_on_file: []
  });
  
  // FTC Report date selection
  const [ftcReportDate, setFtcReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [useCustomDate, setUseCustomDate] = useState(false);
  
  // Credit report upload state
  const [reportUploading, setReportUploading] = useState(false);
  const [reportText, setReportText] = useState("");
  const creditReportInputRef = useRef(null);
  
  // Upload mode: 'separate' = upload each bureau separately, 'single' = use one report for all
  const [uploadMode, setUploadMode] = useState(null); // null = not selected yet
  const singleReportInputRef = useRef(null);
  // Track the source bureau when using single report mode (for filtering inquiries)
  const [singleReportBureau, setSingleReportBureau] = useState(null); // e.g., 'experian', 'transunion', 'equifax'
  
  // Bureau-by-bureau upload state
  const [currentBureauStep, setCurrentBureauStep] = useState(0); // 0=Experian, 1=TransUnion, 2=Equifax
  const [bureauUploadStatus, setBureauUploadStatus] = useState({
    experian: { uploaded: false, skipped: false, score: null, accountCount: 0 },
    transunion: { uploaded: false, skipped: false, score: null, accountCount: 0 },
    equifax: { uploaded: false, skipped: false, score: null, accountCount: 0 },
  });
  // Manual credit score input for each bureau
  const [manualScores, setManualScores] = useState({
    experian: '',
    transunion: '',
    equifax: '',
  });
  
  // NEW: Selected accounts state for modular flow
  const [selectedAccountsForDispute, setSelectedAccountsForDispute] = useState([]);
  
  // Memoized callback to prevent infinite re-renders in DisputeAccountsPageNew
  const handleAccountsChange = useCallback((accts) => {
    setSelectedAccountsForDispute(accts);
  }, []);
  
  // NEW: State for new wizard steps (Messages 423 & 426)
  const [selectedInquiriesForDispute, setSelectedInquiriesForDispute] = useState([]);
  const [hasBankruptcy, setHasBankruptcy] = useState(null);
  const [bankruptcyLetterGenerated, setBankruptcyLetterGenerated] = useState(false);
  const [idDocumentUploaded, setIdDocumentUploaded] = useState(false);
  const [ssnCardUploaded, setSsnCardUploaded] = useState(false);
  const [disputeLettersGenerated, setDisputeLettersGenerated] = useState(false);
  const [inquiryLettersGenerated, setInquiryLettersGenerated] = useState(false);
  // Feb 2026 — after dispute letters are generated, prompt to also
  // pre-fill the OFFICIAL bureau paper forms (TransUnion Investigation
  // Request + Equifax Dispute Request).  The user chooses One or Both
  // via a modal, we call POST /api/bureau-dispute-forms/generate, and
  // download whatever comes back.
  const [bureauFormsPromptOpen, setBureauFormsPromptOpen] = useState(false);
  const [bureauFormsBusy, setBureauFormsBusy] = useState(false);
  // Which bureau(s) the user picked in the redesigned prompt.  Default
  // to BOTH so the primary "Generate & Download" click Just Works.
  const [bureauFormsChoice, setBureauFormsChoice] = useState({
    id: 'both', label: 'Both', bureaus: ["transunion", "equifax"],
    activeCls: 'bg-emerald-600/25 border-emerald-500 text-white',
  });
  // Feb 2026 — free-form editor state.  When the user picks "Edit before
  // download" from the download-choice modal, we open a full-page editor
  // that shows the pre-filled PDF and lets them nudge/erase/add anything.
  // For multi-bureau (BOTH) generation we queue up the second bureau
  // once the first editor closes so the user gets to edit each one.
  const [bureauEditorOpen, setBureauEditorOpen] = useState(false);
  const [bureauEditorPdf, setBureauEditorPdf] = useState(null);       // data URL
  const [bureauEditorLabel, setBureauEditorLabel] = useState("");
  const [bureauEditorFilename, setBureauEditorFilename] = useState("");
  const [bureauEditorQueue, setBureauEditorQueue] = useState([]);     // remaining [{data_url,label,filename}]

  // Preview hook — appending `?previewBureauModal=1` to the URL force-
  // opens the paper-form prompt so you can see it without having to
  // regenerate a full dispute-letter run.  No-op unless the flag is
  // present, so it never fires in normal usage.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("previewBureauModal") === "1") {
      setBureauFormsPromptOpen(true);
    }
  }, []);
  
  // Supplemental letters state
  const [supplementalLettersGenerated, setSupplementalLettersGenerated] = useState(false);
  const [generatedSupplementalLetters, setGeneratedSupplementalLetters] = useState({});
  
  // Score input modal state
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [missingScoreBureaus, setMissingScoreBureaus] = useState([]);
  
  const bureauOrder = ['experian', 'transunion', 'equifax'];
  const bureauInputRefs = {
    experian: useRef(null),
    transunion: useRef(null),
    equifax: useRef(null),
  };
  
  const [incorrectNames, setIncorrectNames] = useState([]);
  const [incorrectAddresses, setIncorrectAddresses] = useState([]);
  const [incorrectPhones, setIncorrectPhones] = useState([]);
  const [incorrectEmployers, setIncorrectEmployers] = useState([]);
  const [newNameInput, setNewNameInput] = useState("");
  const [newAddressInput, setNewAddressInput] = useState("");
  const [newPhoneInput, setNewPhoneInput] = useState("");
  const [newEmployerInput, setNewEmployerInput] = useState("");
  const [bulkPasteInput, setBulkPasteInput] = useState("");
  const [bulkPasteType, setBulkPasteType] = useState("addresses"); // addresses, names, phones, employers
  
  // FTC Report Theme: 'bw' (black & white) or 'color' (official FTC colors - DEFAULT)
  const [ftcTheme, setFtcTheme] = useState('color');
  
  // Manual Inquiry Dispute state (for pasting inquiries to add to dispute letters)
  const [manualInquiries, setManualInquiries] = useState([]);
  const [inquiryPasteText, setInquiryPasteText] = useState("");
  const [inquiryPasteBureau, setInquiryPasteBureau] = useState("equifax");
  
  // Mailing state - One-click mail to bureaus
  const [mailingBureau, setMailingBureau] = useState(null);
  const [mailSent, setMailSent] = useState({ equifax: false, experian: false, transunion: false });
  const [selectedMailType, setSelectedMailType] = useState("usps_first_class");
  const [useCertified, setUseCertified] = useState(false);
  
  // Custom files to include in mailed package
  const [customMailFiles, setCustomMailFiles] = useState([]);
  const [uploadingCustomFile, setUploadingCustomFile] = useState(false);
  
  // Package Full SSN for final package cover letter
  const [packageFullSSN, setPackageFullSSN] = useState('');
  
  // Package Preview state
  const [previewingPackage, setPreviewingPackage] = useState(null); // 'equifax', 'experian', 'transunion'
  const [packagePreviewUrl, setPackagePreviewUrl] = useState(null);
  
  // Package Review Editor state
  const [showPackageEditor, setShowPackageEditor] = useState(false);
  
  // ============ DRAG & DROP MAIL INTERFACE STATE ============
  const [draggedPackage, setDraggedPackage] = useState(null); // Which bureau package is being dragged
  const [dropTargetBureau, setDropTargetBureau] = useState(null); // Which drop zone is active
  const [showMailPreview, setShowMailPreview] = useState(null); // Show preview modal for bureau
  const [packageContents, setPackageContents] = useState({
    equifax: { cover: true, declaration: true, pii_removal: false, dispute_letter: true, inquiry_letter: true, ftc_report: true, id_copy: false, ssn_copy: false },
    experian: { cover: true, declaration: true, pii_removal: false, dispute_letter: true, inquiry_letter: true, ftc_report: true, id_copy: false, ssn_copy: false },
    transunion: { cover: true, declaration: true, pii_removal: false, dispute_letter: true, inquiry_letter: true, ftc_report: true, id_copy: false, ssn_copy: false },
  }); // Track which documents to include in each package - PII REMOVAL REMOVED
  
  // ============ BUREAU ACCOUNT SELECTION STATE ============
  // Per-bureau account selection for dispute letters
  const [bureauAccountLogs, setBureauAccountLogs] = useState({ EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] });
  const [bureauSelections, setBureauSelections] = useState({ EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] });
  const [bureauSelectionsLoading, setBureauSelectionsLoading] = useState(false);
  const [bureauSelectionsSaved, setBureauSelectionsSaved] = useState({ EXPERIAN: false, EQUIFAX: false, TRANSUNION: false });
  const [showAllAccountsFilter, setShowAllAccountsFilter] = useState('all'); // 'all', 'negative', 'positive'
  
  // ============ UNIFIED ACCOUNT SELECTION STATE (Step 4) ============
  // Per-account, per-bureau selections for dispute letters
  const [unifiedBureauSelections, setUnifiedBureauSelections] = useState({});
  // Per-account FTC section assignments (stores Section 1-5 number)
  const [unifiedFtcAssignments, setUnifiedFtcAssignments] = useState({});
  // Per-account FTC category (Credit Card, Auto Loan, etc.)
  const [unifiedFtcCategories, setUnifiedFtcCategories] = useState({});
  
  // ============ ACCOUNT AUTHORIZATION & EXCLUSION STATE ============
  // Note: excludedAccounts is already defined earlier in the component
  // Per-account authorization flags
  const [accountAuthorizations, setAccountAuthorizations] = useState({});
  // Format: { accountId: { includeInLetters: true, includeInFtc: true, bureauOverrides: ['EQUIFAX', 'EXPERIAN'] } }
  
  // Loading states
  const [generatingLetters, setGeneratingLetters] = useState(false);
  const [generatingFtc, setGeneratingFtc] = useState(false);
  
  // ============ HEALTH DASHBOARD & FTC PREVIEW STATE ============
  const [showHealthDashboard, setShowHealthDashboard] = useState(false);
  const [showFtcPreview, setShowFtcPreview] = useState(false);
  
  // ============ LETTER TYPE SELECTION STATE ============
  // Which letter types to include in the dispute package
  const [selectedLetterTypes, setSelectedLetterTypes] = useState(['REGULAR']); // Default to regular dispute letter
  // Per-account letter type assignments: { accountId: ['REGULAR', 'STUDENT_LOAN'], ... }
  const [accountLetterAssignments, setAccountLetterAssignments] = useState({});
  // Per-account fraud category: { accountId: 'CREDIT_CARD', ... }
  const [accountFraudCategories, setAccountFraudCategories] = useState({});
  
  // ============ UNIFIED MANUAL ACCOUNT ENTRY STATE ============
  const [showUnifiedManualEntry, setShowUnifiedManualEntry] = useState(false);
  const [unifiedManualForm, setUnifiedManualForm] = useState({
    creditor_name: '',
    balance: '',
    date_opened: '',
    account_number: '',
    account_type: 'credit_card',
    bureaus: { EQUIFAX: false, EXPERIAN: false, TRANSUNION: false }
  });
  
  // ============ MANUAL ACCOUNT ENTRY STATE ============
  // Allow users to manually add accounts that were missed by extraction
  const [manualAccounts, setManualAccounts] = useState({ EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] });
  const [showManualEntryForm, setShowManualEntryForm] = useState({ EXPERIAN: false, EQUIFAX: false, TRANSUNION: false });
  const [manualFormData, setManualFormData] = useState({ creditor_name: '', date_opened: '', balance: '' });
  const [addingManualAccount, setAddingManualAccount] = useState(false);
  
  // ============ FTC BUILDER STATE ============
  // FTC Report Builder - section-first selection approach
  const [ftcAvailableAccounts, setFtcAvailableAccounts] = useState([]);
  const [ftcSections, setFtcSections] = useState({});
  const [ftcStatement, setFtcStatement] = useState({ mode: 'AUTO', content: '' });
  const [ftcBuilderLoading, setFtcBuilderLoading] = useState(false);
  const [ftcAssignmentComplete, setFtcAssignmentComplete] = useState(false);
  const [activeFtcTab, setActiveFtcTab] = useState('assign');
  const [selectedFtcSection, setSelectedFtcSection] = useState(null); // Currently selected section
  const [ftcTempSelections, setFtcTempSelections] = useState([]); // Temp account selections for current section
  const MAX_FTC_SECTIONS = 5; // FTC allows max 5 sections
  
  // ============ FTC SLOTS MODE STATE ============
  // New 5-slot workflow for easier FTC report building
  const [ftcSlotsMode, setFtcSlotsMode] = useState(false); // Toggle between modes
  const [ftcSlots, setFtcSlots] = useState([
    { slot: 1, category: '', account_ids: [] },
    { slot: 2, category: '', account_ids: [] },
    { slot: 3, category: '', account_ids: [] },
    { slot: 4, category: '', account_ids: [] },
    { slot: 5, category: '', account_ids: [] },
  ]);
  
  // FTC Display Format (Full/Compact/Minimal)
  const [ftcDisplayFormat, setFtcDisplayFormat] = useState('full'); // 'full' | 'compact' | 'minimal'
  
  // FTC Name Abbreviation for clients with many accounts
  const [ftcAbbreviateNames, setFtcAbbreviateNames] = useState(false);
  
  // ============ UDC SNAPSHOT STATE (SINGLE SOURCE OF TRUTH) ============
  const [udcLocked, setUdcLocked] = useState(false);
  const [udcSnapshotId, setUdcSnapshotId] = useState(null);
  const [lockingUdc, setLockingUdc] = useState(false);
  const [udcSnapshotData, setUdcSnapshotData] = useState(null);
  
  // ============ ARTIFACT REGISTRY STATE ============
  const [artifactRegistry, setArtifactRegistry] = useState(null);
  const [downloadingBundle, setDownloadingBundle] = useState(null);
  
  // ============ MANDATORY SAVE FLOW STATE ============
  // Track which steps have unsaved changes and which have been saved
  const [stepSaveStatus, setStepSaveStatus] = useState({
    1: { isDirty: false, isSaved: true },   // Client Info - pre-filled from DB
    2: { isDirty: false, isSaved: false },  // Signature Selection
    3: { isDirty: false, isSaved: false },  // Handwritten Declaration
    4: { isDirty: false, isSaved: false },  // Import Credit Report
    5: { isDirty: false, isSaved: false },  // Dispute Accounts
    6: { isDirty: false, isSaved: false },  // Supplemental Letters
    7: { isDirty: false, isSaved: false },  // Inquiries Dispute
    8: { isDirty: false, isSaved: false },  // FTC Report
    9: { isDirty: false, isSaved: false },  // Bankruptcy
    10: { isDirty: false, isSaved: false }, // Supporting Documents (conditional on step 9)
    11: { isDirty: false, isSaved: true },  // Final Review - read-only
    12: { isDirty: false, isSaved: true },  // Mail Packets - action step
  });
  const [savingStep, setSavingStep] = useState(false);
  
  // Mark current step as dirty when data changes
  const markStepDirty = useCallback((stepNum = null) => {
    const step = stepNum || currentStep;
    setStepSaveStatus(prev => ({
      ...prev,
      [step]: { ...prev[step], isDirty: true, isSaved: false }
    }));
  }, [currentStep]);
  
  // Mark step as saved
  const markStepSaved = useCallback((stepNum = null) => {
    const step = stepNum || currentStep;
    setStepSaveStatus(prev => ({
      ...prev,
      [step]: { isDirty: false, isSaved: true }
    }));
  }, [currentStep]);
  
  // Check if current step can proceed to next
  const canProceedToNext = useCallback(() => {
    const status = stepSaveStatus[currentStep];
    // Steps 1, 11, 12 are always allowed (client info pre-loaded, review/mail are actions)
    if ([1, 11, 12].includes(currentStep)) return true;
    // If step has no unsaved changes OR has been saved, allow proceed
    return !status.isDirty || status.isSaved;
  }, [currentStep, stepSaveStatus]);
  
  // Save current step progress
  const saveCurrentStep = useCallback(async () => {
    setSavingStep(true);
    try {
      const payload = {
        client_id: clientId,
        step: currentStep,
        data: {}
      };
      
      // Build step-specific data
      switch(currentStep) {
        case 2: // Signature Selection
          payload.data = {
            signatureStyle: signatureStyle,
            signatureStyleName: signatureStyleName,
            signatureImageData: signatureImageData
          };
          break;
        case 3: // Import Credit Report
          payload.data = {
            accounts: accounts,
            bureauUploadStatus: bureauUploadStatus,
            manualScores: manualScores,
            reportPersonalInfo: reportPersonalInfo
          };
          break;
        case 4: // Handwritten Declaration
          payload.data = {
            signatureData: signatureData,
            signatureStyle: signatureStyle
          };
          break;
        case 6: // Dispute Accounts
          payload.data = {
            selectedAccounts: selectedAccountsForDispute,
            bureauSelections: bureauSelections,
            excludedAccounts: Array.from(excludedAccounts)
          };
          break;
        case 7: // Supplemental Letters
          payload.data = {
            supplementalLettersGenerated: supplementalLettersGenerated,
            generatedSupplementalLetters: generatedSupplementalLetters
          };
          break;
        case 8: // Inquiries
          payload.data = {
            selectedInquiries: selectedInquiriesForDispute,
            manualInquiries: manualInquiries
          };
          break;
        case 9: // FTC Report
          payload.data = {
            ftcSlots: ftcSlots,
            ftcStatement: ftcStatement,
            ftcReportDate: ftcReportDate,
            ftcTheme: ftcTheme
          };
          break;
        case 10: // Bankruptcy
          payload.data = {
            hasBankruptcy: hasBankruptcy,
            bankruptcyLetterGenerated: bankruptcyLetterGenerated
          };
          break;
        case 11: // Supporting Documents
          payload.data = {
            idDocumentUploaded: idDocumentUploaded,
            ssnCardUploaded: ssnCardUploaded,
            documents: documents
          };
          break;
        default:
          break;
      }
      
      // Save to backend
      const response = await fetch(`${API}/wizard/save-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        markStepSaved();
        toast.success(`Step ${currentStep} saved successfully`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save step');
      }
    } catch (error) {
      console.error('Save step error:', error);
      toast.error('Failed to save progress');
    } finally {
      setSavingStep(false);
    }
  }, [clientId, currentStep, accounts, bureauUploadStatus, manualScores, reportPersonalInfo, 
      signatureData, signatureStyle, selectedAccountsForDispute, bureauSelections, excludedAccounts,
      selectedInquiriesForDispute, manualInquiries, ftcSlots, ftcStatement, ftcReportDate, ftcTheme,
      hasBankruptcy, bankruptcyLetterGenerated, idDocumentUploaded, ssnCardUploaded, documents, markStepSaved]);
  
  // ============ DOCUMENT HUB STATE ============
  const [downloadedDocuments, setDownloadedDocuments] = useState([]);
  const [draggedDocument, setDraggedDocument] = useState(null);
  const [documentToMail, setDocumentToMail] = useState(null);
  const [showMailOptions, setShowMailOptions] = useState(false);
  
  // ============ BUREAU FOLDERS (Auto-Add System) ============
  // Documents are automatically added to folders when downloaded
  // Same exact blob goes to both: 1) User's computer 2) Bureau folder
  const [bureauFolders, setBureauFolders] = useState({
    EQUIFAX: [],
    EXPERIAN: [],
    TRANSUNION: []
  });
  
  // Load saved documents from final_package_artifacts into bureauFolders on mount
  // This ensures Mail Packets page has documents even after page refresh
  useEffect(() => {
    const loadSavedArtifacts = async () => {
      if (!clientId) return;
      
      try {
        const response = await axios.get(`${API}/final-package/artifacts/${clientId}`);
        const artifacts = response.data.artifacts || [];
        
        console.log(`[BUREAU FOLDERS] Loading ${artifacts.length} artifacts from server`);
        
        // Group artifacts by bureau
        const newFolders = {
          EQUIFAX: [],
          EXPERIAN: [],
          TRANSUNION: []
        };
        
        for (const artifact of artifacts) {
          const bureau = artifact.bureau?.toUpperCase();
          const docType = artifact.doc_type;
          const fileName = artifact.file_name;
          const fileUrl = artifact.file_url;
          
          // Skip empty doc types or ALL/UNIVERSAL (they apply to all bureaus)
          if (!docType || bureau === 'ALL' || bureau === 'UNIVERSAL') {
            // Add ALL/UNIVERSAL to each bureau
            if (bureau === 'ALL' || bureau === 'UNIVERSAL') {
              ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(b => {
                // Check if already exists
                const exists = newFolders[b].some(d => d.docType === docType);
                if (!exists) {
                  newFolders[b].push({
                    id: artifact.id || `${docType}_${Date.now()}`,
                    docType: docType,
                    fileName: fileName,
                    fileUrl: fileUrl,
                    displayOrder: artifact.display_order || 99,
                    source: 'server'
                  });
                }
              });
            }
            continue;
          }
          
          if (newFolders[bureau]) {
            // Check if already exists
            const exists = newFolders[bureau].some(d => d.docType === docType);
            if (!exists) {
              newFolders[bureau].push({
                id: artifact.id || `${docType}_${Date.now()}`,
                docType: docType,
                fileName: fileName,
                fileUrl: fileUrl,
                displayOrder: artifact.display_order || 99,
                source: 'server'
              });
            }
          }
        }
        
        // Sort each bureau's documents by display order
        Object.keys(newFolders).forEach(bureau => {
          newFolders[bureau].sort((a, b) => (a.displayOrder || 99) - (b.displayOrder || 99));
        });
        
        console.log(`[BUREAU FOLDERS] Loaded: EQUIFAX=${newFolders.EQUIFAX.length}, EXPERIAN=${newFolders.EXPERIAN.length}, TRANSUNION=${newFolders.TRANSUNION.length}`);
        
        setBureauFolders(newFolders);
      } catch (error) {
        console.error('[BUREAU FOLDERS] Error loading artifacts:', error);
      }
    };
    
    loadSavedArtifacts();
  }, [clientId]);
  
  // Add a document to a bureau folder (called automatically on download)
  const addToFolder = useCallback((bureau, document) => {
    setBureauFolders(prev => {
      // Check if this EXACT file (by fileName) already exists
      const existingByFileName = prev[bureau].find(d => d.fileName === document.fileName);
      if (existingByFileName) {
        // Don't add duplicate - same file
        console.log(`[FOLDER] File "${document.fileName}" already in ${bureau}, skipping`);
        return prev;
      }
      
      // For FTC_REPORT specifically, only allow ONE per bureau (replace if exists)
      if (document.docType === 'FTC_REPORT') {
        const existingFTC = prev[bureau].find(d => d.docType === 'FTC_REPORT');
        if (existingFTC) {
          console.log(`[FOLDER] Replacing existing FTC Report in ${bureau} folder`);
          const filtered = prev[bureau].filter(d => d.docType !== 'FTC_REPORT');
          return {
            ...prev,
            [bureau]: [...filtered, {
              ...document,
              id: document.id || `FTC_REPORT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            }]
          };
        }
      }
      
      // Add as new document (allow multiple docs of same type for non-FTC)
      const newDoc = {
        ...document,
        id: document.id || `${document.docType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      
      console.log(`[FOLDER] Added "${document.fileName}" to ${bureau} folder (${prev[bureau].length + 1} total)`);
      return {
        ...prev,
        [bureau]: [...prev[bureau], newDoc]
      };
    });
  }, []);
  
  // Remove a document from a bureau folder by ID or fileName
  const removeFromFolder = useCallback((bureau, identifier) => {
    setBureauFolders(prev => ({
      ...prev,
      [bureau]: prev[bureau].filter(d => d.id !== identifier && d.fileName !== identifier && d.docType !== identifier)
    }));
    toast.info(`Removed document from ${bureau} folder`);
  }, []);
  
  // Legacy compatibility - kept for any old code that references these
  const registerDocument = useCallback((bureau, docType, blob, fileName) => {
    // Auto-add to folder when registerDocument is called
    addToFolder(bureau.toUpperCase(), { 
      blob, 
      fileName, 
      docType, 
      addedAt: new Date().toISOString(),
      id: `${docType}_${Date.now()}`
    });
  }, [addToFolder]);
  
  const isDocRegistered = useCallback((docType, bureau) => {
    return bureauFolders[bureau.toUpperCase()]?.some(d => d.docType === docType) || false;
  }, [bureauFolders]);
  
  const getDocumentsForBureau = useCallback((bureau) => {
    return bureauFolders[bureau.toUpperCase()] || [];
  }, [bureauFolders]);
  
  const getRegistryCounts = useCallback(() => {
    return {
      EQUIFAX: bureauFolders.EQUIFAX.length,
      EXPERIAN: bureauFolders.EXPERIAN.length,
      TRANSUNION: bureauFolders.TRANSUNION.length
    };
  }, [bureauFolders]);
  
  // ============ SAVE WIZARD PROGRESS ============
  // Saves step completion and documents to backend for persistence
  const saveWizardProgress = useCallback(async (step, stepName, documents = []) => {
    if (!clientId || clientId === "new") return;
    
    try {
      await axios.post(`${API}/wizard-progress/${clientId}/save`, {
        step: step,
        step_name: stepName,
        completed: true,
        documents_generated: documents,
        form_data: null
      });
      console.log(`[WIZARD] Saved progress for step ${step} (${stepName})`);
    } catch (error) {
      console.error("[WIZARD] Failed to save progress:", error);
    }
  }, [clientId]);
  
  // ============ LOAD WIZARD PROGRESS ============
  // Loads saved progress when entering wizard
  const loadWizardProgress = useCallback(async () => {
    if (!clientId || clientId === "new") return null;
    
    try {
      const response = await axios.get(`${API}/wizard-progress/${clientId}`);
      console.log(`[WIZARD] Loaded progress:`, response.data);
      return response.data;
    } catch (error) {
      console.error("[WIZARD] Failed to load progress:", error);
      return null;
    }
  }, [clientId]);
  
  // ============ STEP VALIDATION FUNCTION ============
  // Validates if current step is complete before allowing progression
  // Returns { valid: boolean, message: string }
  const validateCurrentStep = useCallback((step) => {
    // For inquiry_only type, certain steps are skipped
    if (disputeType === 'inquiry_only' && [4, 5, 8].includes(step)) {
      return { valid: true };
    }
    
    switch (step) {
      case 1: // Client Information
        if (!client?.name || !client?.email) {
          return { valid: false, message: "Please complete client name and email before proceeding." };
        }
        return { valid: true };
        
      case 2: // Signature Selection
        if (!signatureStyle) {
          return { valid: false, message: "Please select a signature style before proceeding." };
        }
        return { valid: true };
        
      case 3: // Handwritten Declaration
        // Declaration is recommended but not strictly required to proceed
        return { valid: true };
        
      case 4: // Import & Paste Accounts (unified step)
        // At least some accounts or scores should be entered
        return { valid: true }; // Optional - users can skip if no accounts
        
      case 5: // Dispute Accounts
        if (selectedAccountsForDispute.length === 0 && accounts.length > 0) {
          return { valid: false, message: "Please select at least one account to dispute, or confirm no disputes needed." };
        }
        if (!disputeLettersGenerated && selectedAccountsForDispute.length > 0) {
          return { valid: false, message: "Please generate dispute letters for the selected accounts." };
        }
        return { valid: true };
        
      case 6: // Supplemental Letters (Optional)
        return { valid: true };
        
      case 7: // Inquiries Dispute
        // For inquiry_only, inquiries are required
        if (disputeType === 'inquiry_only') {
          if (selectedInquiriesForDispute.length === 0) {
            return { valid: false, message: "Please paste and select at least one inquiry to dispute." };
          }
          if (!inquiryLettersGenerated) {
            return { valid: false, message: "Please generate inquiry dispute letters." };
          }
        } else {
          // Inquiries are optional for other types, but if selected, letters should be generated
          if (selectedInquiriesForDispute.length > 0 && !inquiryLettersGenerated) {
            return { valid: false, message: "Please generate inquiry dispute letters for the selected inquiries." };
          }
        }
        return { valid: true };
        
      case 8: // FTC Report
        // FTC is optional but strongly recommended for identity theft cases
        return { valid: true };
        
      case 9: // Bankruptcy
        // Bankruptcy is conditional - if they have bankruptcy, they need to generate letters
        if (hasBankruptcy === true && !bankruptcyLetterGenerated) {
          return { valid: false, message: "Please generate bankruptcy dispute letter." };
        }
        return { valid: true };
        
      case 10: // Supporting Documents
        // Documents are optional but recommended for a stronger dispute
        return { valid: true };
        
      case 11: // Final Review
        return { valid: true };
        
      case 12: // Mail Packets
        return { valid: true };
        
      default:
        return { valid: true };
    }
  }, [client, bureauUploadStatus, signatureData, selectedAccountsForDispute, accounts, 
      disputeLettersGenerated, selectedInquiriesForDispute, inquiryLettersGenerated,
      hasBankruptcy, bankruptcyLetterGenerated, idDocumentUploaded, ssnCardUploaded, 
      disputeType, signatureStyle]);
  
  // Get completion status for a step (used for checklist display)
  const getStepCompletionStatus = useCallback((step) => {
    // For inquiry_only type, skipped steps are auto-complete
    if (disputeType === 'inquiry_only' && [4, 5, 9].includes(step)) {
      return true;
    }
    
    switch (step) {
      case 1: return !!(client?.name && client?.email);
      case 2: return signatureStyle > 0; // Signature Selection
      case 3: return true; // Handwritten Declaration - optional
      case 4: return accounts.length > 0 || Object.values(bureauUploadStatus).some(b => b.score); // Import & Paste
      case 5: return disputeLettersGenerated || (accounts.length === 0); // Dispute Accounts
      case 6: return supplementalLettersGenerated || true; // Supplemental Letters (optional)
      case 7: return inquiryLettersGenerated || (disputeType !== 'inquiry_only' && selectedInquiriesForDispute.length === 0);
      case 8: return ftcReportGenerated;
      case 9: return hasBankruptcy === false || (hasBankruptcy === true && bankruptcyLetterGenerated);
      case 10: return true; // Supporting Documents - optional but always accessible
      case 11: return true; // Final Review
      case 12: return true; // Mail Packets
      default: return false;
    }
  }, [client, bureauUploadStatus, signatureData, disputeLettersGenerated, accounts,
      inquiryLettersGenerated, selectedInquiriesForDispute, ftcReportGenerated,
      hasBankruptcy, bankruptcyLetterGenerated, idDocumentUploaded, ssnCardUploaded, 
      disputeType, signatureStyle, signatureImageData, supplementalLettersGenerated]);
  
  // FTC section definitions - 8 sections, max 5 can be used
  const FTC_SECTIONS = [
    { key: 'Credit Card Opened by the Thief', label: '1. Credit Card', fullLabel: 'Credit Card Opened by the Thief', color: 'blue', description: 'Credit cards, store cards, charge cards' },
    { key: 'Fraudulent Auto Loan or Lease', label: '2. Auto Loan', fullLabel: 'Fraudulent Auto Loan or Lease', color: 'green', description: 'Auto loans, vehicle leases, motorcycle loans' },
    { key: 'Fraudulent Loan', label: '3. Loan', fullLabel: 'Fraudulent Loan', color: 'purple', description: 'Personal loans, installment loans, payday loans' },
    { key: 'Fraudulent Mobile Phone or Utility Account', label: '4. Mobile/Utility', fullLabel: 'Fraudulent Mobile Phone or Utility Account', color: 'yellow', description: 'Cell phone, internet, cable, utilities' },
    { key: 'Debt Collection Account', label: '5. Collection', fullLabel: 'Debt Collection Account', color: 'red', description: 'Any account now in collections' },
    { key: 'Bankruptcy Filed by the Thief', label: '6. Bankruptcy', fullLabel: 'Bankruptcy Filed by the Thief', color: 'orange', description: 'Bankruptcy you did NOT file' },
    { key: 'Eviction or Rental Fraud', label: '7. Rental/Eviction', fullLabel: 'Eviction or Rental Fraud', color: 'teal', description: 'Evictions, rental debts' },
    { key: 'Other Fraudulent Account Type', label: '8. Other', fullLabel: 'Other Fraudulent Account Type', color: 'gray', description: 'Fallback for other account types' },
  ];
  
  // ============ AUTO-CATEGORIZATION FUNCTION ============
  // Determines the correct FTC category based on account data
  const autoDetectCategory = (account) => {
    const name = (account.creditor_name || '').toUpperCase();
    const type = (account.account_type || '').toLowerCase();
    const status = (account.status || '').toLowerCase();
    
    // Auto Loan patterns
    const autoPatterns = ['EXETER', 'SANTANDER', 'CHRYSLER', 'YAMAHA', 'CAPITAL ONE AUTO', 'AUTO', 'VEHICLE', 'CAR', 'MOTORCYCLE', 'ALLY FINANCIAL', 'GM FINANCIAL', 'TOYOTA FINANCIAL', 'HONDA FINANCIAL', 'FORD CREDIT'];
    for (const pattern of autoPatterns) {
      if (name.includes(pattern) || type.includes('auto') || type.includes('vehicle')) {
        return 'Auto Loan';
      }
    }
    
    // Credit Card patterns
    const cardPatterns = ['AMEX', 'AMERICAN EXPRESS', 'CAPITAL ONE', 'DISCOVER', 'CHASE', 'CITI', 'FIRST PREMIER', 'CREDIT ONE', 'SYNCHRONY', 'BARCLAYS'];
    for (const pattern of cardPatterns) {
      if (name.includes(pattern) || type.includes('credit') || type.includes('card') || type.includes('revolving')) {
        return 'Credit Card';
      }
    }
    
    // Collection patterns
    if (status.includes('collection') || type.includes('collection') || name.includes('COLLECTION') || name.includes('LVNV') || name.includes('JEFFERSON CAPITAL') || name.includes('MIDLAND') || name.includes('PORTFOLIO')) {
      return 'Collection';
    }
    
    // Loan patterns
    const loanPatterns = ['LOAN', 'LENDING', 'AIDVANTAGE', 'DEPT OF ED', 'NAVIENT', 'NELNET', 'SOFI', 'UPSTART', 'PROSPER'];
    for (const pattern of loanPatterns) {
      if (name.includes(pattern) || type.includes('loan') || type.includes('installment')) {
        return 'Loan';
      }
    }
    
    // Mobile/Utility patterns
    const utilityPatterns = ['VERIZON', 'AT&T', 'T-MOBILE', 'SPRINT', 'COMCAST', 'SPECTRUM', 'ELECTRIC', 'GAS', 'WATER', 'UTILITY'];
    for (const pattern of utilityPatterns) {
      if (name.includes(pattern) || type.includes('mobile') || type.includes('utility')) {
        return 'Mobile/Utility';
      }
    }
    
    return 'Other';
  };
  
  // ============ EXCLUDE/DELETE ACCOUNT HANDLER ============
  const handleExcludeAccount = async (accountId, reason = 'USER_DELETED_FROM_UDC') => {
    // Add to excluded set
    setExcludedAccounts(prev => new Set([...prev, accountId]));
    
    // Remove from all selections
    setUnifiedBureauSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[accountId];
      return newSelections;
    });
    
    setUnifiedFtcAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[accountId];
      return newAssignments;
    });
    
    setUnifiedFtcCategories(prev => {
      const newCategories = { ...prev };
      delete newCategories[accountId];
      return newCategories;
    });
    
    // Persist to backend
    try {
      await axios.post(`${API}/accounts/${accountId}/exclude`, {
        excluded_reason: reason,
        client_id: clientId
      });
      toast.success('Account excluded from all outputs');
    } catch (error) {
      console.error('Failed to persist exclusion:', error);
      // Continue anyway - local state is updated
    }
  };
  
  // ============ RESTORE EXCLUDED ACCOUNT ============
  const handleRestoreAccount = (accountId) => {
    setExcludedAccounts(prev => {
      const newSet = new Set(prev);
      newSet.delete(accountId);
      return newSet;
    });
    toast.success('Account restored');
  };
  
  // Get active FTC sections count
  const activeFtcSectionsCount = Object.entries(ftcSections).filter(([_, ids]) => ids && ids.length > 0).length;
  const canAddMoreFtcSections = activeFtcSectionsCount < MAX_FTC_SECTIONS;
  
  // Mail type options with pricing
  const mailTypeOptions = [
    { value: "usps_standard", label: "USPS Standard (5-10 days)", price: "$0.63" },
    { value: "usps_first_class", label: "USPS First Class (3-5 days)", price: "$1.00" },
  ];

  // ============ WORKFLOW INTELLIGENCE LAYER ============
  // Track step timing and activity for learning system
  const stepStartTime = useRef(Date.now());
  const activityBuffer = useRef([]);
  const [showProgressPrompt, setShowProgressPrompt] = useState(false);
  const [previousCyclesCount, setPreviousCyclesCount] = useState(0);
  const [currentCycleId, setCurrentCycleId] = useState(null);
  const [workflowSuggestions, setWorkflowSuggestions] = useState([]);

  // Log workflow activity (batched for efficiency)
  const logActivity = (actionType, actionTarget, actionValue = null, metadata = {}) => {
    activityBuffer.current.push({
      client_id: clientId,
      action_type: actionType,
      action_target: actionTarget,
      action_value: actionValue,
      metadata: {
        ...metadata,
        step: currentStep,
        step_name: STEPS[currentStep - 1]?.title
      },
      timestamp: Date.now()
    });
    
    // Flush buffer if it gets large
    if (activityBuffer.current.length >= 10) {
      flushActivityBuffer();
    }
  };

  // Flush activity buffer to server
  const flushActivityBuffer = async () => {
    if (activityBuffer.current.length === 0) return;
    
    const activities = [...activityBuffer.current];
    activityBuffer.current = [];
    
    try {
      await axios.post(`${API}/workflow/log-batch`, activities);
    } catch (error) {
      console.error('[WORKFLOW] Failed to log activities:', error);
      // Put activities back in buffer for retry
      activityBuffer.current = [...activities, ...activityBuffer.current];
    }
  };

  // Track step duration when step changes
  useEffect(() => {
    const duration = Date.now() - stepStartTime.current;
    if (duration > 1000) { // Only log if spent more than 1 second
      logActivity('time_spent', `step_${currentStep}`, null, { duration_ms: duration });
    }
    stepStartTime.current = Date.now();
    
    // Flush buffer on step change
    flushActivityBuffer();
  }, [currentStep]);

  // Check for progress report prompt on mount
  useEffect(() => {
    const checkProgressPrompt = async () => {
      try {
        const response = await axios.get(`${API}/progress-report/${clientId}/prompt`);
        if (response.data.should_prompt) {
          setPreviousCyclesCount(response.data.previous_cycles);
          setShowProgressPrompt(true);
        }
      } catch (error) {
        console.error('[PROGRESS] Failed to check prompt:', error);
      }
    };
    
    if (clientId && clientId !== "new") {
      checkProgressPrompt();
    }
  }, [clientId]);

  // Fetch workflow suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await axios.get(`${API}/workflow/suggestions/${clientId}`);
        setWorkflowSuggestions(response.data.suggestions || []);
      } catch (error) {
        console.error('[WORKFLOW] Failed to fetch suggestions:', error);
      }
    };
    
    if (clientId) {
      fetchSuggestions();
    }
  }, [clientId]);

  // Start dispute cycle
  const startDisputeCycle = async () => {
    try {
      const formData = new FormData();
      formData.append('client_id', clientId);
      const response = await axios.post(`${API}/dispute-cycles/start`, formData);
      setCurrentCycleId(response.data.cycle_id);
      toast.success(`Started Dispute Cycle #${response.data.cycle_number}`);
      setShowProgressPrompt(false);
      logActivity('cycle_start', 'dispute_cycle', response.data.cycle_number);
    } catch (error) {
      console.error('[CYCLE] Failed to start cycle:', error);
      toast.error('Failed to start dispute cycle');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      flushActivityBuffer();
    };
  }, []);
  const [piiLetterGenerated, setPiiLetterGenerated] = useState(false);
  
  // Bankruptcy / Public Records Removal state (using hasBankruptcy and bankruptcyLetterGenerated from above)
  const [bankruptcyInfo, setBankruptcyInfo] = useState({
    type: "Chapter 7",
    caseNumber: "",
    filingDate: "",
    courtName: "",
  });
  const [bankruptcyFtcGenerated, setBankruptcyFtcGenerated] = useState(false);
  
  // Dispute Type is now declared early in the component (line ~632) as it's used in validation functions
  // Options: 'identity_theft', 'inquiry_only', 'inaccuracies', 'late_payments'
  
  // Late payments specific state
  const [latePaymentAccounts, setLatePaymentAccounts] = useState([]);
  const [latePaymentExplanations, setLatePaymentExplanations] = useState({}); // {accountId: "explanation text"}
  const [latePaymentDueToFraud, setLatePaymentDueToFraud] = useState(null); // null, true, or false
  const [generatedLatePaymentLetters, setGeneratedLatePaymentLetters] = useState({}); // {accountId: letterContent}
  
  // Late payment dispute reasons - COMPLETE LIST per spec
  const [latePaymentReasons, setLatePaymentReasons] = useState({}); // {accountId: reasonCode}
  const [latePaymentReasonDetails, setLatePaymentReasonDetails] = useState({}); // {accountId: customText} for OTHER_CUSTOM
  const [lateDisputeMonths, setLateDisputeMonths] = useState({}); // {accountId: ["Jan 2024", "Feb 2024"]}
  
  const LATE_PAYMENT_REASON_OPTIONS = [
    { value: 'ON_TIME_INACCURATE', label: 'Inaccurate delinquency (payment was on time)' },
    { value: 'MISAPPLIED_PAYMENT', label: 'Payment misapplied / posted to wrong account' },
    { value: 'SERVICER_REPORTING_ERROR', label: 'Servicer/system reporting error' },
    { value: 'NOT_30_DAYS_LATE', label: 'Not 30 days late (cannot report 1–29 days delinquent)' },
    { value: 'STATEMENT_ADDRESS_ERROR', label: 'Statement/address issue (did not receive billing)' },
    { value: 'BANK_PROCESSING_DELAY', label: 'Bank processing delay (ACH/mail delay)' },
    { value: 'HARDSHIP_EVENT', label: 'Hardship/disruption event (temporary)' },
    { value: 'GOODWILL_REQUEST', label: 'Goodwill adjustment request' },
    { value: 'OTHER_CUSTOM', label: 'Other (custom explanation)' },
  ];
  
  // Hard Inquiries - OPTIONAL add to package (user charges extra for this)
  const [includeInquiriesInPackage, setIncludeInquiriesInPackage] = useState(false);
  const [selectedInquiries, setSelectedInquiries] = useState(new Set()); // Which inquiries to include
  // Note: inquiryLettersGenerated is already declared above
  
  // NEW: Track per-account dispute strategy for identity theft mode
  // Key: account.id, Value: 'full_removal' (default) or 'late_payments_only'
  const [accountDisputeStrategy, setAccountDisputeStrategy] = useState({});
  
  // Paste option for manual account entry
  const [showPasteOption, setShowPasteOption] = useState(false);
  const [pastedAccountText, setPastedAccountText] = useState("");
  
  // Smart Paste for client info
  const [showSmartPaste, setShowSmartPaste] = useState(false);
  const [pastedClientInfo, setPastedClientInfo] = useState("");
  const [parsingClientInfo, setParsingClientInfo] = useState(false);
  
  // NEW: Track when accounts are being loaded to prevent race condition
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [pendingNavigateToStep3, setPendingNavigateToStep3] = useState(false);

  // ============ PREVIEW QA MODE STATE ============
  const [preflightData, setPreflightData] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState(null);
  const [showPreflightQA, setShowPreflightQA] = useState(true); // Show by default

  // Fetch preflight validation data
  const fetchPreflightValidation = async () => {
    if (!clientId) return;
    
    setPreflightLoading(true);
    setPreflightError(null);
    
    try {
      const response = await axios.post(`${API}/preflight/validate`, {
        client_id: clientId,
        check_letters: true,
        check_ftc: true
      });
      setPreflightData(response.data);
      console.log('[PREFLIGHT] Validation data:', response.data);
    } catch (error) {
      console.error('[PREFLIGHT] Validation failed:', error);
      setPreflightError(error.response?.data?.detail || 'Failed to validate data');
    } finally {
      setPreflightLoading(false);
    }
  };

  // Fetch preflight data when entering Generate Letters step (NOW STEP 9)
  useEffect(() => {
    if (currentStep === 10 && clientId) {
      fetchPreflightValidation();
    }
  }, [currentStep, clientId]);

  // ============ UPDATE PACKAGE CONTENTS BASED ON DISPUTE TYPE ============
  // Different dispute types require different documents in the final package
  // NOTE: PII Removal letter is REMOVED from all packages
  useEffect(() => {
    if (!disputeType) return;
    
    const updateContents = (bureau) => {
      switch (disputeType) {
        case 'inquiry_only':
          // Inquiry Removal Only: Blank page + Cover + Declaration + Inquiry Letters + FTC Report + ID + SSN
          // NO dispute_letter (account disputes), NO PII removal
          return {
            cover: true,
            declaration: true,
            pii_removal: false,
            dispute_letter: false,  // No account disputes
            inquiry_letter: true,   // Main focus
            ftc_report: true,       // FTC report included
            id_copy: true,
            ssn_copy: true
          };
        case 'late_payments':
          // Late Payment Only: Blank page + Cover + Declaration + Late Payment Letters + ID + SSN
          // NO FTC Report, NO PII removal, NO Inquiry letters
          return {
            cover: true,
            declaration: true,
            pii_removal: false,  // REMOVED
            dispute_letter: true,  // Will be late payment specific
            inquiry_letter: false,
            ftc_report: false,
            id_copy: true,
            ssn_copy: true
          };
        case 'inaccuracies':
          // Account Inaccuracies: Blank page + Cover + Declaration + Dispute Letters + Inquiry Letters + ID + SSN
          // NO FTC Report, NO PII Removal
          return {
            cover: true,
            declaration: true,
            pii_removal: false,  // REMOVED
            dispute_letter: true,
            inquiry_letter: true,
            ftc_report: false,
            id_copy: true,
            ssn_copy: true
          };
        case 'identity_theft':
        default:
          // Identity Theft: FULL package with FTC Report (but NO PII Removal)
          return {
            cover: true,
            declaration: true,
            pii_removal: false,  // REMOVED - No PII removal in any package
            dispute_letter: true,
            inquiry_letter: true,
            ftc_report: true,
            id_copy: true,
            ssn_copy: true
          };
      }
    };
    
    setPackageContents({
      equifax: updateContents('equifax'),
      experian: updateContents('experian'),
      transunion: updateContents('transunion'),
    });
    
    console.log(`[PACKAGE CONTENTS] Updated for dispute type: ${disputeType}`);
  }, [disputeType]);

  // Reload accounts when entering Step 9 if accounts array is empty
  useEffect(() => {
    if (currentStep === 10 && clientId && (!accounts || accounts.length === 0)) {
      console.log('[STEP 10] Accounts empty, reloading...');
      const reloadAccounts = async () => {
        try {
          const accountsRes = await axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`);
          setAccounts(deduplicateAccounts(accountsRes.data || []));
          console.log(`[STEP 10] Loaded ${accountsRes.data?.length || 0} accounts`);
        } catch (error) {
          console.error('[STEP 10] Error loading accounts:', error);
        }
      };
      reloadAccounts();
    }
  }, [currentStep, clientId, accounts]);

  // Toggle account inclusion/exclusion
  const toggleAccountExclusion = (accountId) => {
    setExcludedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };
  
  // Check if account is included (not excluded)
  const isAccountIncluded = (accountId) => !excludedAccounts.has(accountId);
  
  // Delete account permanently
  const deleteAccount = async (accountId, creditorName) => {
    if (!window.confirm(`Are you sure you want to permanently remove "${creditorName}" from this client's accounts?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/accounts/${accountId}`);
      setAccounts(prev => prev.filter(a => a.id !== accountId));
      // Also remove from excluded if it was there
      setExcludedAccounts(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
      toast.success(`"${creditorName}" removed successfully`);
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to remove account. Please try again.");
    }
  };
  
  // ============ BUREAU ACCOUNT SELECTION FUNCTIONS ============
  
  // Fetch audit logs for bureau account selection (Step 8)
  const fetchBureauAuditLogs = useCallback(async () => {
    if (!clientId) return;
    
    setBureauSelectionsLoading(true);
    try {
      const response = await axios.get(`${API}/audit/accounts/${clientId}`);
      const logs = response.data.logs || [];
      
      // Group by bureau
      const grouped = { EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] };
      for (const log of logs) {
        const bureau = log.bureau?.toUpperCase();
        if (bureau && grouped[bureau]) {
          grouped[bureau].push(log);
        }
      }
      
      setBureauAccountLogs(grouped);
      
      // Set default selections (only INCLUDED accounts)
      const defaultSelections = { EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] };
      for (const [bureau, bLogs] of Object.entries(grouped)) {
        defaultSelections[bureau] = bLogs
          .filter(log => log.decision === 'INCLUDED' || log.decision === 'INCLUDED_MANUAL_OVERRIDE')
          .map(log => log.log_id);
      }
      
      setBureauSelections(defaultSelections);
      
    } catch (error) {
      console.error('[SELECT_ACCOUNTS] Failed to fetch audit logs:', error);
      // On error, just set empty - user can still proceed
      setBureauAccountLogs({ EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] });
      setBureauSelections({ EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] });
    }
    setBureauSelectionsLoading(false);
  }, [clientId]); // Only depend on clientId
  
  // Handle checkbox change for bureau account selection
  const handleBureauAccountCheckbox = (bureau, logId, checked) => {
    setBureauSelections(prev => {
      const newSelections = { ...prev };
      if (checked) {
        newSelections[bureau] = [...prev[bureau], logId];
      } else {
        newSelections[bureau] = prev[bureau].filter(id => id !== logId);
      }
      return newSelections;
    });
    // Mark as unsaved
    setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: false }));
  };
  
  // Save bureau selection
  const saveBureauSelection = async (bureau) => {
    try {
      const allLogIds = bureauAccountLogs[bureau].map(a => a.log_id);
      const selectedIds = bureauSelections[bureau];
      const deselectedIds = allLogIds.filter(id => !selectedIds.includes(id));

      await axios.post(`${API}/audit/bureau-selection`, {
        client_id: clientId,
        bureau: bureau,
        selected_account_log_ids: selectedIds,
        deselected_account_log_ids: deselectedIds
      });

      toast.success(`${bureau} selection saved (${selectedIds.length} accounts)`);
      setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: true }));
    } catch (error) {
      console.error('Failed to save selection:', error);
      toast.error('Failed to save selection');
    }
  };
  
  // Save all bureau selections
  const saveAllBureauSelections = async () => {
    for (const bureau of ['EXPERIAN', 'EQUIFAX', 'TRANSUNION']) {
      await saveBureauSelection(bureau);
    }
  };
  
  // Select all accounts for a bureau
  const selectAllBureauAccounts = (bureau) => {
    const allIds = bureauAccountLogs[bureau].map(a => a.log_id);
    setBureauSelections(prev => ({ ...prev, [bureau]: allIds }));
    setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: false }));
  };
  
  // Select only negative accounts for a bureau
  const selectNegativeOnlyBureauAccounts = (bureau) => {
    const negativeIds = bureauAccountLogs[bureau]
      .filter(a => a.decision === 'INCLUDED' || a.decision === 'INCLUDED_MANUAL_OVERRIDE')
      .map(a => a.log_id);
    setBureauSelections(prev => ({ ...prev, [bureau]: negativeIds }));
    setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: false }));
  };
  
  // Clear all selections for a bureau
  const clearBureauSelections = (bureau) => {
    setBureauSelections(prev => ({ ...prev, [bureau]: [] }));
    setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: false }));
  };
  
  // ============ MANUAL ACCOUNT HANDLERS ============
  
  // Add a manually entered account
  const addManualAccount = async (bureau) => {
    if (!manualFormData.creditor_name.trim()) {
      toast.error('Please enter an account name');
      return;
    }
    
    setAddingManualAccount(true);
    
    try {
      const manualId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const newAccount = {
        id: manualId,
        log_id: manualId,
        client_id: clientId,
        creditor_name: manualFormData.creditor_name.trim().toUpperCase(),
        canonical_creditor_name: manualFormData.creditor_name.trim().toUpperCase(),
        date_opened: manualFormData.date_opened || 'Unknown',
        open_date: manualFormData.date_opened || 'Unknown',
        balance: parseFloat(manualFormData.balance) || 0,
        bureau: bureau,
        is_manual: true,
        classification: 'MANUAL',
        decision: 'INCLUDED_MANUAL'
      };
      
      // Save to backend
      const response = await axios.post(`${API}/manual-accounts`, {
        client_id: clientId,
        account: newAccount
      });
      
      if (response.data.success) {
        // Add to local state
        setManualAccounts(prev => ({
          ...prev,
          [bureau]: [...(prev[bureau] || []), newAccount]
        }));
        
        // Auto-select the new account
        setBureauSelections(prev => ({
          ...prev,
          [bureau]: [...(prev[bureau] || []), manualId]
        }));
        
        // Reset form
        setManualFormData({ creditor_name: '', date_opened: '', balance: '' });
        setShowManualEntryForm(prev => ({ ...prev, [bureau]: false }));
        setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: false }));
        
        toast.success(`Added manual account: ${newAccount.creditor_name}`);
      }
    } catch (error) {
      console.error('Failed to add manual account:', error);
      toast.error('Failed to add manual account');
    }
    
    setAddingManualAccount(false);
  };
  
  // Remove a manually entered account
  const removeManualAccount = async (bureau, accountId) => {
    try {
      await axios.delete(`${API}/manual-accounts/${accountId}`);
      
      setManualAccounts(prev => ({
        ...prev,
        [bureau]: prev[bureau].filter(a => a.id !== accountId)
      }));
      
      setBureauSelections(prev => ({
        ...prev,
        [bureau]: prev[bureau].filter(id => id !== accountId)
      }));
      
      setBureauSelectionsSaved(prev => ({ ...prev, [bureau]: false }));
      toast.success('Manual account removed');
    } catch (error) {
      console.error('Failed to remove manual account:', error);
      // Still remove from local state
      setManualAccounts(prev => ({
        ...prev,
        [bureau]: prev[bureau].filter(a => a.id !== accountId)
      }));
    }
  };
  
  // Fetch manual accounts for a client
  const fetchManualAccounts = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await axios.get(`${API}/manual-accounts/${clientId}`);
      const grouped = { EXPERIAN: [], EQUIFAX: [], TRANSUNION: [] };
      
      for (const acc of response.data.accounts || []) {
        const bureau = acc.bureau?.toUpperCase();
        if (bureau && grouped[bureau]) {
          grouped[bureau].push(acc);
        }
      }
      
      setManualAccounts(grouped);
    } catch (error) {
      console.log('No manual accounts found');
    }
  }, [clientId]);
  
  // Fetch manual accounts when entering Step 8
  useEffect(() => {
    if (currentStep === 9 && clientId) {
      fetchManualAccounts();
    }
  }, [currentStep, clientId, fetchManualAccounts]);
  
  // Fetch bureau audit logs when entering Step 8 (Select Accounts)
  useEffect(() => {
    if (currentStep === 9 && clientId) {
      fetchBureauAuditLogs();
    }
  }, [currentStep, clientId, fetchBureauAuditLogs]);
  
  // ============ FTC BUILDER FUNCTIONS ============
  
  // Fetch FTC available accounts and sections
  const fetchFtcBuilderData = useCallback(async () => {
    if (!clientId) return;
    
    setFtcBuilderLoading(true);
    try {
      // Fetch available accounts
      const accountsRes = await axios.get(`${API}/ftc-builder/${clientId}/available-accounts`);
      setFtcAvailableAccounts(accountsRes.data.accounts || []);

      // Fetch current sections/assignments
      const sectionsRes = await axios.get(`${API}/ftc-builder/${clientId}/sections`);
      setFtcSections(sectionsRes.data.sections || {});
      
      if (sectionsRes.data.statement_mode) {
        setFtcStatement(prev => ({ ...prev, mode: sectionsRes.data.statement_mode }));
      }
      
      // Check if assignment is complete
      const totalAssigned = Object.values(sectionsRes.data.sections || {}).reduce(
        (sum, accts) => sum + accts.length, 0
      );
      setFtcAssignmentComplete(totalAssigned > 0 && accountsRes.data.accounts?.length === totalAssigned);
      
    } catch (error) {
      console.error('Failed to fetch FTC data:', error);
      // On error, just set empty
      setFtcAvailableAccounts([]);
      setFtcSections({});
    }
    setFtcBuilderLoading(false);
  }, [clientId]); // Only depend on clientId
  
  // Get suggested FTC section based on account type
  const getSuggestedFtcSection = (account) => {
    const creditor = (account.creditor_name || '').toUpperCase();
    const type = (account.account_type || '').toLowerCase();
    
    const autoKeywords = ['EXETER', 'SANTANDER', 'CHRYSLER', 'TOYOTA', 'HONDA', 'FORD', 'BMW', 'NISSAN'];
    if (autoKeywords.some(kw => creditor.includes(kw)) || type.includes('auto')) {
      return 'Fraudulent Auto Loan or Lease';
    }
    
    const ccKeywords = ['AMEX', 'AMERICAN EXPRESS', 'CAPITAL ONE', 'DISCOVER', 'CHASE', 'CITI'];
    if (ccKeywords.some(kw => creditor.includes(kw)) || type.includes('credit')) {
      return 'Credit Card Opened by the Thief';
    }
    
    if (type.includes('collection')) {
      return 'Debt Collection Account';
    }
    
    const mobileKeywords = ['SPRINT', 'T-MOBILE', 'VERIZON', 'AT&T'];
    if (mobileKeywords.some(kw => creditor.includes(kw))) {
      return 'Fraudulent Mobile Phone or Utility Account';
    }
    
    return 'Fraudulent Loan';
  };
  
  // Assign account to FTC section
  const assignAccountToFtcSection = async (accountLogId, section) => {
    try {
      await axios.post(`${API}/ftc-builder/assign`, {
        client_id: clientId,
        account_log_id: accountLogId,
        section: section
      });
      
      toast.success('Account assigned to section');
      fetchFtcBuilderData(); // Refresh
    } catch (error) {
      console.error('Failed to assign account:', error);
      toast.error('Failed to assign account');
    }
  };
  
  // Remove FTC assignment
  const removeFtcAssignment = async (accountLogId) => {
    try {
      await axios.delete(`${API}/ftc-builder/${clientId}/assignment/${accountLogId}`);
      toast.success('Assignment removed');
      fetchFtcBuilderData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };
  
  // Generate FTC auto statement
  const generateFtcAutoStatement = async () => {
    try {
      const response = await axios.get(`${API}/ftc-builder/${clientId}/generate-statement`);
      setFtcStatement({ mode: 'AUTO', content: response.data.content });
      toast.success('Statement generated');
    } catch (error) {
      toast.error('Failed to generate statement');
    }
  };
  
  // Save FTC statement
  const saveFtcStatement = async () => {
    try {
      await axios.post(`${API}/ftc-builder/statement`, {
        client_id: clientId,
        content: ftcStatement.content,
        mode: ftcStatement.mode
      });
      toast.success('Statement saved');
    } catch (error) {
      toast.error('Failed to save statement');
    }
  };
  
  // ============ NEW: Section-First FTC Handlers ============
  
  // Handle FTC section selection (select section first)
  const handleSelectFtcSection = (sectionKey) => {
    if (selectedFtcSection === sectionKey) {
      // Deselect
      setSelectedFtcSection(null);
      setFtcTempSelections([]);
    } else {
      // Check if we can add a new section
      const existingAssignments = ftcSections[sectionKey] || [];
      if (existingAssignments.length === 0 && !canAddMoreFtcSections) {
        toast.error(`Maximum ${MAX_FTC_SECTIONS} sections allowed. Remove accounts from another section first.`);
        return;
      }
      
      setSelectedFtcSection(sectionKey);
      // Load existing assignments for this section
      const existingIds = (ftcSections[sectionKey] || []).map(a => a.account_log_id || a);
      setFtcTempSelections(existingIds);
    }
  };
  
  // Handle account checkbox toggle for current FTC section
  const handleFtcAccountToggle = (accountId, checked) => {
    if (checked) {
      // Check if account is already in another section
      for (const [sectionKey, accounts] of Object.entries(ftcSections)) {
        if (sectionKey !== selectedFtcSection) {
          const ids = accounts.map(a => a.account_log_id || a);
          if (ids.includes(accountId)) {
            toast.error(`This account is already assigned to "${FTC_SECTIONS.find(s => s.key === sectionKey)?.label}". Remove it first.`);
            return;
          }
        }
      }
      setFtcTempSelections(prev => [...prev, accountId]);
    } else {
      setFtcTempSelections(prev => prev.filter(id => id !== accountId));
    }
  };
  
  // Save FTC section assignments
  const saveFtcSection = async () => {
    if (!selectedFtcSection) return;
    
    try {
      await axios.post(`${API}/ftc-builder/save-section`, {
        client_id: clientId,
        section_key: selectedFtcSection,
        account_ids: ftcTempSelections
      });
      
      // Update local state with creditor name for display
      const accountsWithNames = ftcTempSelections.map(id => {
        const account = ftcAvailableAccounts.find(a => a.log_id === id);
        return {
          account_log_id: id,
          creditor: account?.canonical_creditor_name || account?.raw_creditor_name || 'Unknown'
        };
      });
      
      setFtcSections(prev => ({
        ...prev,
        [selectedFtcSection]: accountsWithNames
      }));
      
      toast.success(`Section "${FTC_SECTIONS.find(s => s.key === selectedFtcSection)?.label}" saved (${ftcTempSelections.length} accounts)`);
      setSelectedFtcSection(null);
      setFtcTempSelections([]);
    } catch (error) {
      console.error('Failed to save section:', error);
      toast.error('Failed to save section');
    }
  };
  
  // Clear a FTC section
  const clearFtcSection = (sectionKey) => {
    setFtcSections(prev => ({
      ...prev,
      [sectionKey]: []
    }));
    
    if (selectedFtcSection === sectionKey) {
      setFtcTempSelections([]);
    }
    
    toast.success('Section cleared');
  };
  
  // Check if account is assigned to any FTC section
  const isAccountInAnyFtcSection = (accountId) => {
    for (const accounts of Object.values(ftcSections)) {
      const ids = accounts.map(a => a.account_log_id || a);
      if (ids.includes(accountId)) return true;
    }
    return false;
  };
  
  // Get which section an account is assigned to
  const getAccountFtcSection = (accountId) => {
    for (const [sectionKey, accounts] of Object.entries(ftcSections)) {
      const ids = accounts.map(a => a.account_log_id || a);
      if (ids.includes(accountId)) return sectionKey;
    }
    return null;
  };
  
  // Get assigned account IDs for FTC
  const getFtcAssignedAccountIds = () => {
    const ids = new Set();
    Object.values(ftcSections).forEach(accounts => {
      accounts.forEach(acc => ids.add(acc.account_log_id));
    });
    return ids;
  };
  
  // Get unassigned FTC accounts
  const getFtcUnassignedAccounts = () => {
    const assignedIds = getFtcAssignedAccountIds();
    return ftcAvailableAccounts.filter(acc => !assignedIds.has(acc.log_id));
  };
  
  // ============ FTC SLOTS MODE HANDLERS ============
  // Update slot category
  const handleSlotCategoryChange = (slotIndex, category) => {
    setFtcSlots(prev => prev.map((slot, idx) => 
      idx === slotIndex ? { ...slot, category } : slot
    ));
  };
  
  // Toggle account in slot - WITH FTC SLOT RULE ENFORCEMENT
  const handleSlotAccountToggle = (slotIndex, accountId) => {
    // Check if account is already in another slot
    const existingSlotIndex = ftcSlots.findIndex(slot => 
      slot.account_ids.includes(accountId) && slot.slot !== slotIndex + 1
    );
    
    if (existingSlotIndex !== -1) {
      toast.error(`Account already assigned to Section ${existingSlotIndex + 1}`);
      return;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // FTC SLOT RULE ENFORCEMENT
    // ≤5 accounts = 1 account per slot, ALWAYS (never combine)
    // ═══════════════════════════════════════════════════════════════════════════
    const currentSlot = ftcSlots[slotIndex];
    const isAdding = !currentSlot.account_ids.includes(accountId);
    const totalCurrentAccounts = ftcSlots.flatMap(s => s.account_ids).length;
    
    // Only enforce when adding (not removing)
    if (isAdding) {
      // If adding and current slot already has 1 account, check total
      if (currentSlot.account_ids.length >= 1) {
        // After adding this would be totalCurrentAccounts + 1
        const newTotal = totalCurrentAccounts + 1;
        if (newTotal <= 5) {
          toast.error(`FTC Rule: With ≤5 accounts, each account needs its own section. Use a different slot for this account.`);
          return;
        }
      }
    }
    
    setFtcSlots(prev => prev.map((slot, idx) => {
      if (idx !== slotIndex) return slot;
      
      const isSelected = slot.account_ids.includes(accountId);
      return {
        ...slot,
        account_ids: isSelected 
          ? slot.account_ids.filter(id => id !== accountId)
          : [...slot.account_ids, accountId]
      };
    }));
  };
  
  // Check if account is assigned in any slot
  const isAccountInAnySlot = (accountId) => {
    return ftcSlots.some(slot => slot.account_ids.includes(accountId));
  };
  
  // Get slot that contains an account
  const getAccountSlot = (accountId) => {
    return ftcSlots.find(slot => slot.account_ids.includes(accountId))?.slot || null;
  };
  
  // Validate slots for download - ENFORCES FTC SLOT RULE
  const validateFtcSlots = () => {
    const populatedSlots = ftcSlots.filter(slot => slot.category && slot.account_ids.length > 0);
    if (populatedSlots.length === 0) {
      return { valid: false, error: 'Please assign at least one account to a section' };
    }
    
    // Check for duplicate account IDs across slots
    const allAccountIds = ftcSlots.flatMap(slot => slot.account_ids);
    const uniqueIds = new Set(allAccountIds);
    if (uniqueIds.size !== allAccountIds.length) {
      return { valid: false, error: 'Some accounts are assigned to multiple sections' };
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // FTC SLOT RULE ENFORCEMENT (NON-NEGOTIABLE)
    // ≤5 accounts = 1 account per slot, ALWAYS (never combine)
    // >5 accounts = Multiple accounts per slot allowed
    // ═══════════════════════════════════════════════════════════════════════════
    const totalAccounts = allAccountIds.length;
    if (totalAccounts <= 5) {
      // Check that no slot has more than 1 account
      const violatingSlot = populatedSlots.find(slot => slot.account_ids.length > 1);
      if (violatingSlot) {
        return { 
          valid: false, 
          error: `FTC Rule Violation: With ${totalAccounts} accounts or fewer, each account must have its own section. Section ${violatingSlot.slot} has ${violatingSlot.account_ids.length} accounts.`
        };
      }
    }
    
    return { valid: true, error: null };
  };
  
  // Save FTC slots to backend
  const saveFtcSlots = async () => {
    const validation = validateFtcSlots();
    if (!validation.valid) {
      toast.error(validation.error);
      return false;
    }
    
    try {
      // Convert slots to sections format for backward compatibility
      const sectionsFromSlots = {};
      ftcSlots.forEach(slot => {
        if (slot.category && slot.account_ids.length > 0) {
          const accountsWithNames = slot.account_ids.map(id => {
            const account = ftcAvailableAccounts.find(a => a.log_id === id);
            return {
              account_log_id: id,
              creditor: account?.canonical_creditor_name || account?.raw_creditor_name || 'Unknown'
            };
          });
          sectionsFromSlots[slot.category] = accountsWithNames;
        }
      });
      
      // Save to backend
      await axios.post(`${API}/ftc-builder/save-slots`, {
        client_id: clientId,
        slots: ftcSlots,
        sections: sectionsFromSlots
      });
      
      // Also update local ftcSections state for compatibility
      setFtcSections(sectionsFromSlots);
      
      toast.success('FTC sections saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to save FTC slots:', error);
      toast.error('Failed to save FTC sections');
      return false;
    }
  };
  
  // ============ QUICK ASSIGN FEATURE ============
  // Automatically distribute accounts across slots based on category
  // RESPECTS FTC SLOT RULE: ≤5 accounts = 1 per slot
  const quickAssignAccounts = () => {
    if (ftcAvailableAccounts.length === 0) {
      toast.error('No accounts available to assign');
      return;
    }
    
    const totalAccounts = ftcAvailableAccounts.length;
    
    // Category mapping based on account type/status
    const categoryMap = {
      'credit card': 'Credit Card Opened by the Thief',
      'card': 'Credit Card Opened by the Thief',
      'amex': 'Credit Card Opened by the Thief',
      'visa': 'Credit Card Opened by the Thief',
      'mastercard': 'Credit Card Opened by the Thief',
      'discover': 'Credit Card Opened by the Thief',
      'auto': 'Fraudulent Auto Loan or Lease',
      'vehicle': 'Fraudulent Auto Loan or Lease',
      'car': 'Fraudulent Auto Loan or Lease',
      'exeter': 'Fraudulent Auto Loan or Lease',
      'santander': 'Fraudulent Auto Loan or Lease',
      'chrysler': 'Fraudulent Auto Loan or Lease',
      'yamaha': 'Fraudulent Auto Loan or Lease',
      'motorcycle': 'Fraudulent Auto Loan or Lease',
      'loan': 'Fraudulent Loan',
      'personal': 'Fraudulent Loan',
      'installment': 'Fraudulent Loan',
      'navy federal': 'Fraudulent Loan',
      'student': 'Fraudulent Loan',
      'dept of ed': 'Fraudulent Loan',
      'aidvantage': 'Fraudulent Loan',
      'mobile': 'Fraudulent Mobile Phone or Utility Account',
      'phone': 'Fraudulent Mobile Phone or Utility Account',
      'utility': 'Fraudulent Mobile Phone or Utility Account',
      'electric': 'Fraudulent Mobile Phone or Utility Account',
      'gas': 'Fraudulent Mobile Phone or Utility Account',
      'collection': 'Debt Collection Account',
      'collections': 'Debt Collection Account',
      'charge off': 'Debt Collection Account',
      'chargeoff': 'Debt Collection Account',
    };
    
    // ═══════════════════════════════════════════════════════════════════════════
    // FTC SLOT RULE ENFORCEMENT
    // ≤5 accounts = 1 account per slot, ALWAYS (never combine)
    // >5 accounts = Multiple accounts per slot allowed (distribute evenly)
    // ═══════════════════════════════════════════════════════════════════════════
    
    if (totalAccounts <= 5) {
      // ≤5 accounts: Each account gets its own slot
      const newSlots = [
        { slot: 1, category: '', account_ids: [] },
        { slot: 2, category: '', account_ids: [] },
        { slot: 3, category: '', account_ids: [] },
        { slot: 4, category: '', account_ids: [] },
        { slot: 5, category: '', account_ids: [] },
      ];
      
      ftcAvailableAccounts.forEach((account, index) => {
        if (index < 5) {
          const name = (account.canonical_creditor_name || account.raw_creditor_name || '').toLowerCase();
          const type = (account.account_type || '').toLowerCase();
          const status = (account.status || '').toLowerCase();
          const combined = `${name} ${type} ${status}`;
          
          // Find matching category
          let assignedCategory = 'Other Fraudulent Account Type';
          for (const [keyword, category] of Object.entries(categoryMap)) {
            if (combined.includes(keyword)) {
              assignedCategory = category;
              break;
            }
          }
          
          newSlots[index] = {
            slot: index + 1,
            category: assignedCategory,
            account_ids: [account.log_id] // Only 1 account per slot
          };
        }
      });
      
      setFtcSlots(newSlots);
      toast.success(`FTC Rule Applied: ${totalAccounts} accounts assigned to ${totalAccounts} individual sections (1 per slot)`);
      return;
    }
    
    // >5 accounts: Categorize and distribute across slots
    const categorizedAccounts = {};
    ftcAvailableAccounts.forEach(account => {
      const name = (account.canonical_creditor_name || account.raw_creditor_name || '').toLowerCase();
      const type = (account.account_type || '').toLowerCase();
      const status = (account.status || '').toLowerCase();
      const combined = `${name} ${type} ${status}`;
      
      // Find matching category
      let assignedCategory = 'Other Fraudulent Account Type';
      for (const [keyword, category] of Object.entries(categoryMap)) {
        if (combined.includes(keyword)) {
          assignedCategory = category;
          break;
        }
      }
      
      if (!categorizedAccounts[assignedCategory]) {
        categorizedAccounts[assignedCategory] = [];
      }
      categorizedAccounts[assignedCategory].push(account.log_id);
    });
    
    // Distribute across 5 slots
    const newSlots = [
      { slot: 1, category: '', account_ids: [] },
      { slot: 2, category: '', account_ids: [] },
      { slot: 3, category: '', account_ids: [] },
      { slot: 4, category: '', account_ids: [] },
      { slot: 5, category: '', account_ids: [] },
    ];
    
    // Sort categories by number of accounts (descending) and assign to slots
    const sortedCategories = Object.entries(categorizedAccounts)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 5); // Max 5 categories
    
    sortedCategories.forEach(([category, accountIds], index) => {
      newSlots[index] = {
        slot: index + 1,
        category: category,
        account_ids: accountIds
      };
    });
    
    setFtcSlots(newSlots);
    
    const assignedCount = sortedCategories.reduce((sum, [_, ids]) => sum + ids.length, 0);
    toast.success(`Quick Assign: ${assignedCount} accounts distributed across ${sortedCategories.length} categories`);
  };
  
  // Clear all FTC slots
  const clearAllSlots = () => {
    setFtcSlots([
      { slot: 1, category: '', account_ids: [] },
      { slot: 2, category: '', account_ids: [] },
      { slot: 3, category: '', account_ids: [] },
      { slot: 4, category: '', account_ids: [] },
      { slot: 5, category: '', account_ids: [] },
    ]);
    toast.success('All slots cleared');
  };
  
  // Fetch FTC builder data when entering Step 11 (FTC Builder)
  useEffect(() => {
    if (currentStep === 11 && clientId) {
      fetchFtcBuilderData();
    }
  }, [currentStep, clientId, fetchFtcBuilderData]);
  
  // Form states
  const [clientForm, setClientForm] = useState({
    name: "",
    suffix: "",  // NEW: Name suffix (II, III, Jr., Sr., etc.)
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    ssn_last4: "",
    ssn_full: "",
    date_of_birth: "",
  });
  
  const [selectedBureau, setSelectedBureau] = useState("all_bureaus");

  // CRITICAL FIX: Handle navigation to Step 3 AFTER accounts are loaded
  // This prevents the race condition that was causing blank screens
  useEffect(() => {
    if (pendingNavigateToStep3 && !accountsLoading && accounts.length >= 0) {
      console.log("Navigating to Step 3 - accounts loaded:", accounts.length);
      setCurrentStep(3);
      setPendingNavigateToStep3(false);
    }
  }, [pendingNavigateToStep3, accountsLoading, accounts]);

  // Load client data if editing existing client
  useEffect(() => {
    if (clientId && clientId !== "new") {
      loadClientData();
    }
  }, [clientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const [clientRes, accountsRes, inquiriesRes, lettersRes, docsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`),
        axios.get(`${API}/clients/${clientId}/inquiries`),
        axios.get(`${API}/letters?client_id=${clientId}`),
        axios.get(`${API}/clients/${clientId}/documents`),
      ]);
      
      setClient(clientRes.data);
      setClientForm({
        name: clientRes.data.name || "",
        suffix: clientRes.data.suffix || "",  // Load suffix from database
        email: clientRes.data.email || "",
        phone: clientRes.data.phone || "",
        address: clientRes.data.address || "",
        city: clientRes.data.city || "",
        state: clientRes.data.state || "",
        zip_code: clientRes.data.zip_code || "",
        ssn_last4: clientRes.data.ssn_last4 || "",
        ssn_full: clientRes.data.ssn_full || clientRes.data.ssn || "",
        // Convert date format: MM/DD/YYYY -> YYYY-MM-DD for HTML date input
        date_of_birth: (() => {
          const dob = clientRes.data.date_of_birth || "";
          if (!dob) return "";
          // If already in YYYY-MM-DD format, use as-is
          if (dob.match(/^\d{4}-\d{2}-\d{2}/)) return dob.split('T')[0];
          // If in MM/DD/YYYY format, convert
          const parts = dob.split('/');
          if (parts.length === 3) {
            return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
          return dob;
        })(),
      });
      
      // Load personal info from credit report if available
      if (clientRes.data.report_names || clientRes.data.report_addresses) {
        setReportPersonalInfo({
          names_on_file: clientRes.data.report_names || [],
          addresses_on_file: clientRes.data.report_addresses || [],
          phones_on_file: clientRes.data.report_phones || [],
          employers_on_file: clientRes.data.report_employers || []
        });
      }
      
      setAccounts(deduplicateAccounts(accountsRes.data));
      setInquiries(deduplicateInquiries(inquiriesRes.data.inquiries || []));
      setLetters(lettersRes.data);
      setDocuments(docsRes.data);
      
      // Set document previews if documents exist
      if (docsRes.data.id_document && docsRes.data.id_document.filename) {
        setIdPreview(`${API}/uploads/${docsRes.data.id_document.filename}`);
      }
      if (docsRes.data.ssn_card && docsRes.data.ssn_card.filename) {
        setSsnPreview(`${API}/uploads/${docsRes.data.ssn_card.filename}`);
      }
      
      // Load saved signature if exists
      if (clientRes.data.signature_data) {
        setSignatureData(clientRes.data.signature_data);
      }
    } catch (error) {
      console.error("Error loading client data:", error);
      toast.error("Failed to load client information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Save signature to backend
  const saveSignature = async (signatureDataUrl) => {
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        ...clientForm,
        signature_data: signatureDataUrl
      });
      setSignatureData(signatureDataUrl);
      toast.success("Signature saved successfully!");
    } catch (error) {
      toast.error("Error saving signature");
      console.error("Error saving signature:", error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // UDC SNAPSHOT - LOCK & FINALIZE (SINGLE SOURCE OF TRUTH)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Check for existing UDC snapshot on load
  const checkExistingUdcSnapshot = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await axios.get(`${API}/udc/snapshot/${clientId}`);
      if (response.data.exists) {
        setUdcLocked(true);
        setUdcSnapshotId(response.data.snapshot.snapshot_id);
        setUdcSnapshotData(response.data.snapshot);
        console.log('[UDC SNAPSHOT] Found existing locked snapshot:', response.data.snapshot.snapshot_id);
      } else {
        setUdcLocked(false);
        setUdcSnapshotId(null);
        setUdcSnapshotData(null);
      }
    } catch (error) {
      console.log('[UDC SNAPSHOT] No existing snapshot found');
      setUdcLocked(false);
    }
  }, [clientId]);

  // Lock & Finalize UDC - Create authoritative snapshot
  const lockAndFinalizeUdc = async () => {
    if (!clientId || !accounts || accounts.length === 0) {
      toast.error('No accounts to lock. Please load accounts first.');
      return;
    }
    
    setLockingUdc(true);
    
    try {
      // Build the accounts array with all UDC settings
      const snapshotAccounts = accounts
        .filter(acc => !excludedAccounts.has(acc.id))
        .map(acc => {
          const bureauSel = unifiedBureauSelections[acc.id] || {};
          const selectedBureaus = [];
          if (bureauSel.EQUIFAX) selectedBureaus.push('EQUIFAX');
          if (bureauSel.EXPERIAN) selectedBureaus.push('EXPERIAN');
          if (bureauSel.TRANSUNION) selectedBureaus.push('TRANSUNION');
          
          return {
            id: acc.id,
            account_id: acc.id,
            creditor_name: acc.creditor_name,
            balance: acc.balance || 0,
            date_opened: acc.date_opened || '',
            bureaus: selectedBureaus,
            bureaus_selected: selectedBureaus,
            category: unifiedFtcCategories[acc.id] || autoDetectCategory(acc),
            ftc_section: unifiedFtcAssignments[acc.id] || null,
            include_in_dispute_letters: selectedBureaus.length > 0,
            include_in_ftc_report: !!unifiedFtcAssignments[acc.id]
          };
        });
      
      if (snapshotAccounts.length === 0) {
        toast.error('No accounts selected. Please select at least one account.');
        setLockingUdc(false);
        return;
      }
      
      // Create the snapshot
      const response = await axios.post(`${API}/udc/snapshot`, {
        client_id: clientId,
        accounts: snapshotAccounts,
        ftc_statement: ftcStatement.content || null,
        signature_data: signatureData || null
      });
      
      if (response.data.success) {
        setUdcLocked(true);
        setUdcSnapshotId(response.data.snapshot_id);
        toast.success(`UDC Locked! ${response.data.total_account_count} accounts finalized.`);
        console.log('[UDC SNAPSHOT] Created:', response.data);
        
        // Refresh snapshot data
        await checkExistingUdcSnapshot();
      }
    } catch (error) {
      console.error('[UDC SNAPSHOT] Error creating snapshot:', error);
      toast.error('Failed to lock UDC: ' + (error.response?.data?.detail || error.message));
    }
    
    setLockingUdc(false);
  };

  // Unlock UDC - Allow re-editing
  const unlockUdc = async () => {
    if (!clientId) return;
    
    try {
      await axios.delete(`${API}/udc/snapshot/${clientId}`);
      setUdcLocked(false);
      setUdcSnapshotId(null);
      setUdcSnapshotData(null);
      toast.success('UDC unlocked. You can now edit selections.');
    } catch (error) {
      toast.error('Failed to unlock UDC');
    }
  };

  // Check for UDC snapshot when entering Step 9
  useEffect(() => {
    if (currentStep === 10 && clientId) {
      checkExistingUdcSnapshot();
      loadArtifactRegistry();
    }
  }, [currentStep, clientId, checkExistingUdcSnapshot]);

  // Load artifact registry for deterministic bundling
  const loadArtifactRegistry = async () => {
    if (!clientId) return;
    try {
      const response = await axios.get(`${API}/artifacts/registry/${clientId}`);
      setArtifactRegistry(response.data);
      console.log('[ARTIFACT REGISTRY] Loaded:', response.data);
    } catch (error) {
      console.log('[ARTIFACT REGISTRY] No artifacts registered yet');
      setArtifactRegistry({ registry: { equifax: [], experian: [], transunion: [], all: [] }, counts: {}, total_artifacts: 0 });
    }
  };

  // Download bureau-ready bundle (deterministic - no regeneration)
  const downloadBureauBundle = async (bureau) => {
    if (!clientId) return;
    setDownloadingBundle(bureau);
    
    try {
      const response = await axios.post(
        `${API}/artifacts/bundle/${clientId}/${bureau}`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bureau.toUpperCase()}_Dispute_Package.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${bureau.toUpperCase()} package downloaded!`);
    } catch (error) {
      console.error('Bundle download error:', error);
      if (error.response?.status === 400) {
        toast.error(`No artifacts registered for ${bureau}. Please complete previous steps first.`);
      } else {
        toast.error(`Failed to download ${bureau} bundle`);
      }
    }
    
    setDownloadingBundle(null);
  };

  // Download bureau-specific package
  const downloadBureauPackage = async (bureau) => {
    try {
      setDownloadingBureau(bureau);
      
      // Safety check for accounts array
      if (!accounts || !Array.isArray(accounts) || accounts.length === 0) {
        toast.error('No accounts loaded. Please go back to Step 4 and ensure accounts are loaded.');
        setDownloadingBureau(null);
        return;
      }
      
      // Get accounts for this bureau - check BOTH bureau field AND bureaus_present array
      const bureauLower = bureau.toLowerCase();
      const bureauAccounts = accounts.filter(a => {
        const accBureau = (a.bureau || '').toLowerCase();
        const accBureausPresent = (a.bureaus_present || []).map(b => b.toLowerCase());
        
        // Match if bureau is in comma-separated string OR in bureaus_present array
        return accBureau.includes(bureauLower) || accBureausPresent.includes(bureauLower);
      });
      
      console.log(`[BUREAU FILTER] ${bureau}: Found ${bureauAccounts.length} accounts from ${accounts.length} total`);
      
      // Get the correct account IDs for this specific bureau
      // Use _merged_ids if available (from deduplication), otherwise fall back to main id
      const accountIds = bureauAccounts
        .filter(a => isAccountIncluded(a.id))
        .map(a => {
          // If this account was merged from multiple bureaus, get the ID for THIS bureau
          if (a._merged_ids && a._merged_ids[bureauLower]) {
            return a._merged_ids[bureauLower];
          }
          // If we have _all_ids, use the first one (there's at least one for this bureau)
          if (a._all_ids && a._all_ids.length > 0) {
            return a._all_ids[0];
          }
          return a.id;
        });
      
      console.log(`[BUREAU PACKAGE] ${bureau}: Sending ${accountIds.length} account IDs:`, accountIds);
      
      if (accountIds.length === 0) {
        toast.warning(`No accounts found for ${bureau}`);
        setDownloadingBureau(null);
        return;
      }
      
      toast.loading(`Generating ${bureau} package with all documents...`, { id: 'bundle-toast' });
      
      // Get package contents for this bureau
      const contents = packageContents[bureau.toLowerCase()] || {};
      
      // Use the NEW complete bundle endpoint that includes perfect FTC
      const response = await axios.post(
        `${API}/dispute-package/download-complete-bundle`,
        { 
          client_id: clientId, 
          bureau: bureau.toLowerCase(),
          account_ids: accountIds,
          signature_data: signatureData,
          include_ftc: contents.ftc_report !== false,
          include_declaration: contents.declaration !== false,
          include_pii_removal: false,  // REMOVED - Never include PII removal letter
          include_inquiries: contents.inquiry_letter !== false,
          include_cover: contents.cover !== false,
          include_dispute_letter: contents.dispute_letter !== false,
          include_inquiry_letter: contents.inquiry_letter !== false,
          include_id_copy: contents.id_copy !== false,
          include_ssn_copy: contents.ssn_copy !== false,
          full_ssn: packageFullSSN || undefined  // Include full SSN for cover page
        },
        { responseType: "blob", timeout: 300000 }
      );
      
      toast.dismiss('bundle-toast');
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const clientName = (clientForm?.name || client?.name || 'Client').replace(/\s+/g, '_');
      link.setAttribute("download", `${bureau}_Complete_Package_${clientName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setPackageReady(prev => ({ ...prev, [bureau.toLowerCase()]: true }));
      toast.success(`${bureau} complete package downloaded!`);
    } catch (error) {
      toast.dismiss('bundle-toast');
      toast.error(`Error downloading ${bureau} package`);
      console.error(`Error downloading ${bureau} package:`, error);
    } finally {
      setDownloadingBureau(null);
    }
  };

  // Download PII Removal Letter
  // Download PII Removal Letter for a specific bureau (or all)
  // Download UNIVERSAL PII Removal Letter (ONE letter for ALL bureaus)
  const downloadUniversalPIILetter = async () => {
    try {
      setLoading(true);
      toast.info("Generating Universal PII Removal Letter (for ALL bureaus)...");
      
      // Compute additional items to include
      const clientAddress = clientForm.address ? 
        `${clientForm.address}${clientForm.city ? ', ' + clientForm.city : ''}${clientForm.state ? ', ' + clientForm.state : ''} ${clientForm.zip_code || ''}`.trim() 
        : null;
      
      const additionalItems = [];
      
      // Add manually entered items
      incorrectNames.forEach(name => additionalItems.push({ value: name, type: 'NAME' }));
      incorrectAddresses.forEach(addr => additionalItems.push({ value: addr, type: 'ADDRESS' }));
      incorrectPhones.forEach(phone => additionalItems.push({ value: phone, type: 'PHONE' }));
      incorrectEmployers.forEach(emp => additionalItems.push({ value: emp, type: 'EMPLOYER' }));
      
      // Call new universal PII letter endpoint
      const response = await axios.post(
        `${API}/pii/${clientId}/generate-universal-letter`,
        {
          signature_data: signatureData,
          additional_items: additionalItems
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `#6_Universal_PII_Removal_Letter_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // Get doc ID from response header if available
      const docId = response.headers['x-document-id'];
      
      // Add to ALL bureau folders (same document)
      ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(bureau => {
        addToFolder(bureau, {
          blob,
          fileName,
          docType: 'PERSONAL_INFO_REMOVAL',
          addedAt: new Date().toISOString(),
          id: docId || `UNIVERSAL_PII_${Date.now()}`,
          isUniversal: true
        });
      });
      
      // Download to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Universal PII Removal Letter generated and added to ALL bureau folders!");
    } catch (error) {
      console.error("Error generating universal PII letter:", error);
      toast.error("Error generating PII removal letter");
    } finally {
      setLoading(false);
    }
  };

  // Legacy: Download bureau-specific PII letter (kept for compatibility)
  const downloadPIIRemovalLetter = async (bureau = null) => {
    // For universal letter (no bureau specified), use new endpoint
    if (!bureau) {
      return downloadUniversalPIILetter();
    }
    
    try {
      setLoading(true);
      toast.info(`Generating PII Removal Letter for ${bureau.toUpperCase()}...`);
      
      // Calculate incorrect items from report at download time
      const clientAddress = clientForm.address ? 
        `${clientForm.address}${clientForm.city ? ', ' + clientForm.city : ''}${clientForm.state ? ', ' + clientForm.state : ''} ${clientForm.zip_code || ''}`.trim() 
        : null;
      
      // Compute mismatched addresses from report
      const addressesFromReport = reportPersonalInfo.addresses_on_file.filter(addr => {
        const normalizedAddr = addr.toLowerCase().replace(/[,.\s]+/g, ' ').trim();
        const normalizedClientAddr = clientAddress?.toLowerCase().replace(/[,.\s]+/g, ' ').trim() || '';
        return !normalizedAddr.includes(normalizedClientAddr) && !normalizedClientAddr.includes(normalizedAddr);
      });
      
      // Compute mismatched names from report
      const namesFromReport = reportPersonalInfo.names_on_file.filter(name => {
        const normalizedName = name.toLowerCase().trim();
        const normalizedClientName = clientForm.name?.toLowerCase().trim() || '';
        return normalizedName !== normalizedClientName && !normalizedName.includes(normalizedClientName);
      });
      
      // Compute mismatched phones from report
      const phonesFromReport = reportPersonalInfo.phones_on_file.filter(phone => {
        const normalizedPhone = phone.replace(/\D/g, '');
        const normalizedClientPhone = clientForm.phone?.replace(/\D/g, '') || '';
        return normalizedPhone !== normalizedClientPhone;
      });
      
      const response = await axios.post(
        `${API}/dispute-package/download-pii-removal-letter`,
        { 
          client_id: clientId, 
          bureau: bureau,
          signature_data: signatureData,
          incorrect_names: [...namesFromReport, ...incorrectNames],
          incorrect_addresses: [...addressesFromReport, ...incorrectAddresses],
          incorrect_phones: [...phonesFromReport, ...incorrectPhones],
          incorrect_employers: [...(reportPersonalInfo.employers_on_file || []), ...incorrectEmployers]
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `3_PII_Removal_${bureau.toUpperCase()}_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // AUTO-ADD to bureau folder
      addToFolder(bureau.toUpperCase(), {
        blob,
        fileName,
        docType: 'PERSONAL_INFO_REMOVAL_LETTER',
        addedAt: new Date().toISOString(),
        id: `PERSONAL_INFO_REMOVAL_LETTER_${bureau.toUpperCase()}_${Date.now()}`
      });
      
      // Download to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${bureau.toUpperCase()} PII Removal Letter downloaded & added to folder!`);
    } catch (error) {
      toast.error("Error downloading PII removal letter");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Download individual bureau dispute letter
  const downloadBureauLetter = async (bureau) => {
    try {
      setLoading(true);
      toast.info(`Generating ${bureau.toUpperCase()} dispute letter...`);
      
      // Get ALL accounts that appear on this bureau - check BOTH bureau field AND bureaus_present array
      const bureauLower = bureau.toLowerCase();
      
      // Filter accounts for this bureau - only include if not excluded
      const bureauAccounts = accounts.filter(a => {
        const accBureau = (a.bureau || '').toLowerCase();
        const accBureausPresent = (a.bureaus_present || []).map(b => b.toLowerCase());
        
        // Match if bureau is in comma-separated string OR in bureaus_present array
        const bureauMatch = accBureau.includes(bureauLower) || accBureausPresent.includes(bureauLower);
        return bureauMatch && isAccountIncluded(a.id);
      });
      
      console.log(`[LETTER FILTER] ${bureau}: Found ${bureauAccounts.length} accounts`);
      
      // Get ALL account IDs for this bureau - including merged IDs from deduplication
      // This ensures we get EVERY account from the database that belongs to this bureau
      const accountIdsForBureau = [];
      bureauAccounts.forEach(a => {
        // Add all merged IDs for this bureau
        if (a._merged_ids) {
          Object.entries(a._merged_ids).forEach(([b, id]) => {
            if (b.toLowerCase() === bureauLower && id && !accountIdsForBureau.includes(id)) {
              accountIdsForBureau.push(id);
            }
          });
        }
        // Add all IDs from _all_ids as fallback
        if (a._all_ids && a._all_ids.length > 0) {
          a._all_ids.forEach(id => {
            if (id && !accountIdsForBureau.includes(id)) {
              accountIdsForBureau.push(id);
            }
          });
        }
        // Add the main ID if not already included
        if (a.id && !accountIdsForBureau.includes(a.id)) {
          accountIdsForBureau.push(a.id);
        }
      });
      
      console.log(`[LETTER FILTER] ${bureau}: Sending ${accountIdsForBureau.length} account IDs to backend`);
      
      const response = await axios.post(
        `${API}/dispute/bureau-letter`,
        { 
          client_id: clientId, 
          bureau: bureau,
          account_ids: accountIdsForBureau,
          signature_data: signatureData
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `4_Dispute_Letter_${bureau.toUpperCase()}_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // AUTO-ADD to bureau folder (SAME blob that downloads)
      addToFolder(bureau.toUpperCase(), {
        blob,
        fileName: filename,
        docType: 'ACCOUNT_DISPUTE_LETTER',
        addedAt: new Date().toISOString(),
        id: `ACCOUNT_DISPUTE_LETTER_${bureau.toUpperCase()}_${Date.now()}`
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${bureau.toUpperCase()} dispute letter downloaded & added to folder!`);
    } catch (error) {
      toast.error(`Error downloading ${bureau} letter`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ONE-CLICK Mail to Bureau via Lob.com
  const mailToBureau = async (bureau) => {
    try {
      setMailingBureau(bureau);
      const mailTypeLabel = mailTypeOptions.find(m => m.value === selectedMailType)?.label || selectedMailType;
      toast.info(`Preparing to mail dispute to ${bureau} via ${mailTypeLabel}...`);
      
      const response = await axios.post(`${API}/mail/send-to-bureau`, {
        client_id: clientId,
        bureau: bureau.toLowerCase(),
        mail_type: selectedMailType,
        certified: useCertified,
        return_receipt: useCertified,
        custom_files: customMailFiles.map(f => f.id)
      });
      
      if (response.data.setup_required) {
        toast.error(
          <div>
            <p className="font-bold">Lob.com Setup Required</p>
            <p className="text-sm">{response.data.message}</p>
          </div>,
          { duration: 8000 }
        );
        return;
      }
      
      if (response.data.success) {
        setMailSent(prev => ({ ...prev, [bureau.toLowerCase()]: true }));
        toast.success(
          <div>
            <p className="font-bold">📬 Letter Mailed to {bureau}!</p>
            <p className="text-sm">Expected delivery: {response.data.expected_delivery}</p>
            {response.data.cost && <p className="text-sm">Cost: ${response.data.cost}</p>}
            {response.data.is_test_mode && (
              <p className="text-xs text-yellow-400 mt-1">⚠️ Test mode - no actual letter sent</p>
            )}
          </div>,
          { duration: 6000 }
        );
      }
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.message || "Failed to mail letter";
      toast.error(`Mail Error: ${errMsg}`);
      console.error(error);
    } finally {
      setMailingBureau(null);
    }
  };

  // Handle custom file upload for mailing
  const handleCustomMailFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type - PDF only for mailing
    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file only");
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }
    
    try {
      setUploadingCustomFile(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("client_id", clientId);
      formData.append("document_type", "custom_mail_attachment");
      
      const response = await axios.post(`${API}/clients/${clientId}/upload-custom-mail-file`, formData);
      
      if (response.data.success) {
        setCustomMailFiles(prev => [...prev, {
          id: response.data.file_id,
          name: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString()
        }]);
        toast.success(`Added "${file.name}" to mailing package`);
      }
    } catch (error) {
      toast.error("Error uploading file: " + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setUploadingCustomFile(false);
      event.target.value = ''; // Reset input
    }
  };

  // Remove custom file from mailing list
  const removeCustomMailFile = (fileId) => {
    setCustomMailFiles(prev => prev.filter(f => f.id !== fileId));
    toast.info("File removed from mailing package");
  };

  // Download Handwritten Declaration PDF
  const downloadHandwrittenDeclaration = async (bureau = null) => {
    try {
      setLoading(true);
      toast.info("Generating handwritten declaration...");
      
      const response = await axios.post(
        `${API}/dispute-package/download-handwritten-declaration`,
        { 
          client_id: clientId, 
          signature_data: signatureData
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `2_Handwritten_Declaration_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // AUTO-ADD to ALL 3 bureau folders (SAME blob)
      ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(b => {
        addToFolder(b, {
          blob,
          fileName: filename,
          docType: 'DECLARATION_PAGE',
          addedAt: new Date().toISOString(),
          id: `DECLARATION_PAGE_${b}_${Date.now()}`
        });
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Declaration downloaded & added to ALL 3 bureau folders!");
    } catch (error) {
      toast.error("Error downloading handwritten declaration");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Download Bankruptcy Removal Letter
  const downloadBankruptcyLetter = async (bureau) => {
    try {
      setLoading(true);
      toast.info(`Generating ${bureau.toUpperCase()} bankruptcy removal letter...`);
      
      const response = await axios.post(
        `${API}/dispute-package/bankruptcy-removal-letter`,
        { 
          client_id: clientId, 
          bureau: bureau,
          bankruptcy_type: bankruptcyInfo.type,
          case_number: bankruptcyInfo.caseNumber,
          filing_date: bankruptcyInfo.filingDate,
          court_name: bankruptcyInfo.courtName,
          signature_data: signatureData
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `Bankruptcy_Removal_${bureau.toUpperCase()}_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // AUTO-ADD to bureau folder (SAME blob that downloads)
      addToFolder(bureau.toUpperCase(), {
        blob,
        fileName,
        docType: 'BANKRUPTCY_REMOVAL_LETTER',
        addedAt: new Date().toISOString(),
        id: `BANKRUPTCY_REMOVAL_LETTER_${bureau.toUpperCase()}_${Date.now()}`
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setBankruptcyLetterGenerated(prev => ({...prev, [bureau]: true}));
      toast.success(`${bureau.toUpperCase()} bankruptcy letter downloaded & added to folder!`);
    } catch (error) {
      toast.error(`Error downloading ${bureau} bankruptcy letter`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Download FTC Bankruptcy Report
  const downloadBankruptcyFtcReport = async () => {
    try {
      setLoading(true);
      toast.info("Generating FTC Bankruptcy Identity Theft Report...");
      
      const response = await axios.post(
        `${API}/dispute/ftc-bankruptcy-report`,
        { 
          client_id: clientId, 
          bureau: "all",
          bankruptcy_type: bankruptcyInfo.type,
          case_number: bankruptcyInfo.caseNumber,
          filing_date: bankruptcyInfo.filingDate,
          court_name: bankruptcyInfo.courtName,
          signature_data: signatureData
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FTC_Bankruptcy_Report_${clientForm.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setBankruptcyFtcGenerated(true);
      toast.success("FTC Bankruptcy Report downloaded!");
    } catch (error) {
      toast.error("Error downloading FTC Bankruptcy Report");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // LATE PAYMENT SPECIFIC FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════
  
  // Filter for ACTIVE/OPEN accounts with late payments only
  const getActiveAccountsWithLatePayments = () => {
    return accounts.filter(acc => {
      // Check if account is OPEN/ACTIVE (not closed, charged-off, or collection)
      const status = (acc.status || '').toLowerCase();
      const accountType = (acc.account_type || '').toLowerCase();
      
      const isActive = 
        status.includes('open') || 
        status.includes('active') ||
        status.includes('current') ||
        status.includes('paying') ||
        (!status.includes('closed') && !status.includes('charged') && !status.includes('collection'));
      
      // Check for late payment indicators
      const hasLatePayment = 
        status.includes('late') ||
        status.includes('delinquent') ||
        status.includes('30 day') ||
        status.includes('60 day') ||
        status.includes('90 day') ||
        /\d+\s*(late|day)/i.test(status) ||
        accountType.includes('late');
      
      return isActive && hasLatePayment;
    });
  };

  // Generate custom late payment dispute letter based on explanation
  const generateLatePaymentLetter = async (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    const explanation = latePaymentExplanations[accountId] || '';
    
    if (!account || !explanation.trim()) {
      toast.error("Please provide an explanation for the late payment");
      return;
    }
    
    try {
      setLoading(true);
      toast.info("Generating customized late payment dispute letter...");
      
      const response = await axios.post(`${API}/dispute/generate-late-payment-letter`, {
        client_id: clientId,
        account_id: accountId,
        explanation: explanation,
        bureau: account.bureau || 'equifax'
      });
      
      setGeneratedLatePaymentLetters(prev => ({
        ...prev,
        [accountId]: response.data.letter_content
      }));
      
      toast.success("Late payment letter generated!");
    } catch (error) {
      console.error("Error generating late payment letter:", error);
      toast.error("Failed to generate letter. Using template instead.");
      // Fallback to basic template
      setGeneratedLatePaymentLetters(prev => ({
        ...prev,
        [accountId]: `Late Payment Dispute Letter for ${account.creditor_name}\n\nExplanation: ${explanation}`
      }));
    } finally {
      setLoading(false);
    }
  };

  // Download generated late payment letter as PDF
  const downloadLatePaymentLetter = async (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    const explanation = latePaymentExplanations[accountId] || '';
    
    try {
      setLoading(true);
      toast.info("Downloading late payment dispute letter...");
      
      const response = await axios.post(
        `${API}/dispute/download-late-payment-letter`,
        {
          client_id: clientId,
          account_id: accountId,
          explanation: explanation,
          bureau: account.bureau || 'equifax',
          signature_data: signatureData
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Late_Payment_Dispute_${account.creditor_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Late payment letter downloaded!");
    } catch (error) {
      toast.error("Error downloading late payment letter");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // HARD INQUIRIES FUNCTIONS (OPTIONAL - User charges extra for this)
  // ═══════════════════════════════════════════════════════════════════
  
  const toggleInquirySelection = (inquiryId) => {
    setSelectedInquiries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(inquiryId)) {
        newSet.delete(inquiryId);
      } else {
        newSet.add(inquiryId);
      }
      return newSet;
    });
  };

  const selectAllInquiries = () => {
    setSelectedInquiries(new Set(inquiries.map(i => i.id)));
  };

  const deselectAllInquiries = () => {
    setSelectedInquiries(new Set());
  };

  // ═══════════════════════════════════════════════════════════════════
  // DOWNLOAD COVER LETTER (ATTN: FRAUD DISPUTE DEPARTMENT)
  // ═══════════════════════════════════════════════════════════════════
  const downloadCoverLetter = async (bureau = 'equifax') => {
    try {
      setLoading(true);
      toast.info("Generating cover letter...");
      
      const accountIds = accounts
        .filter(a => isAccountIncluded(a.id))
        .map(a => a.id);
      
      const response = await axios.post(
        `${API}/dispute-package/download-cover-letter`,
        { 
          client_id: clientId, 
          bureau: bureau,
          account_ids: accountIds,
          dispute_type: disputeType || 'identity_theft',
          signature_style: signatureStyle || 1,
          signature_data: signatureData || null  // Pass the actual captured signature image
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `1_Cover_Letter_${bureau.toUpperCase()}_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // AUTO-ADD to bureau folder (SAME blob that downloads)
      addToFolder(bureau.toUpperCase(), {
        blob,
        fileName: filename,
        docType: 'COVER_PAGE',
        addedAt: new Date().toISOString(),
        id: `COVER_PAGE_${bureau.toUpperCase()}_${Date.now()}`
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Cover letter downloaded & added to ${bureau.toUpperCase()} folder!`);
    } catch (error) {
      toast.error("Error downloading cover letter");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // DOWNLOAD INQUIRY LETTER (SEPARATE FROM ACCOUNTS)
  // ═══════════════════════════════════════════════════════════════════
  const downloadInquiryLetter = async (bureau = 'equifax') => {
    try {
      setLoading(true);
      toast.info(`Generating ${bureau.toUpperCase()} inquiry dispute letter...`);
      
      const inquiryIds = Array.from(selectedInquiries);
      
      if (inquiryIds.length === 0) {
        toast.error("Please select at least one inquiry to include");
        setLoading(false);
        return;
      }
      
      const response = await axios.post(
        `${API}/dispute-package/download-inquiry-letter`,
        { 
          client_id: clientId, 
          bureau: bureau,
          inquiry_ids: inquiryIds,
          signature_data: signatureData,
          dispute_type: disputeType,  // Pass dispute type to indicate identity theft/fraud
          is_identity_theft: disputeType === 'inquiry_only' || disputeType === 'identity_theft'
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `5_Inquiry_Dispute_Letter_${bureau.toUpperCase()}_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
      
      // AUTO-ADD to bureau folder (SAME blob that downloads)
      addToFolder(bureau.toUpperCase(), {
        blob,
        fileName: filename,
        docType: 'INQUIRY_REMOVAL_LETTER',
        addedAt: new Date().toISOString(),
        id: `INQUIRY_REMOVAL_LETTER_${bureau.toUpperCase()}_${Date.now()}`
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${bureau.toUpperCase()} inquiry letter downloaded & added to folder!`);
    } catch (error) {
      toast.error("Error downloading inquiry letter");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Download all 3 bureau packages as a ZIP
  const downloadAllPackages = async () => {
    try {
      setLoading(true);
      toast.info("Preparing all 3 dispute packages... This may take a moment.");
      
      // Filter out excluded accounts
      const accountIds = accounts
        .filter(a => isAccountIncluded(a.id))
        .map(a => a.id);
      
      const response = await axios.post(
        `${API}/dispute-package/download-all-bureaus`,
        { 
          client_id: clientId, 
          account_ids: accountIds,
          signature_data: signatureData
        },
        { responseType: "blob", timeout: 300000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${clientForm.name.replace(/\s+/g, '_')}_All_Dispute_Packages.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("All dispute packages downloaded!");
    } catch (error) {
      toast.error("Error downloading packages");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Email sending functions
  const sendCreditRepairStepsEmail = async () => {
    try {
      setSendingEmail('report');
      await axios.post(`${API}/email/send-credit-repair-steps`, {
        client_id: clientId
      });
      setEmailSent(prev => ({ ...prev, report: true }));
      toast.success(`Credit Repair Steps email sent to ${client?.email}`);
    } catch (error) {
      toast.error("Failed to send email: " + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(null);
    }
  };

  const sendPortalLinkEmail = async () => {
    try {
      setSendingEmail('portal');
      await axios.post(`${API}/email/send-portal-link`, {
        client_id: clientId
      });
      setEmailSent(prev => ({ ...prev, portal: true }));
      toast.success(`Portal link email sent to ${client?.email}`);
    } catch (error) {
      toast.error("Failed to send email: " + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(null);
    }
  };

  const sendStatusUpdateEmail = async (updateType) => {
    try {
      setSendingEmail('status');
      await axios.post(`${API}/email/send-status-update`, {
        client_id: clientId,
        update_type: updateType
      });
      setEmailSent(prev => ({ ...prev, status: true }));
      toast.success(`Status update email sent to ${client?.email}`);
    } catch (error) {
      toast.error("Failed to send email: " + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(null);
    }
  };

  const handleNext = async () => {
    // Helper function to get next step based on dispute type
    const getNextStep = (current) => {
      // For inquiry_only: skip steps 2 (Import Credit Report), 4 (Dispute Accounts), 7 (Bankruptcy)
      if (disputeType === 'inquiry_only') {
        const skipSteps = [2, 4, 7];
        let next = current + 1;
        while (skipSteps.includes(next) && next <= STEPS.length) {
          next++;
        }
        return next;
      }
      return current + 1;
    };

    // Validate current step before proceeding
    if (currentStep === 1) {
      if (!clientForm.name || !clientForm.email) {
        toast.error("Please fill in at least name and email");
        return;
      }
      const success = await saveClientInfo();
      if (success) {
        // After saving client, go to next appropriate step
        const nextStep = getNextStep(1);
        setCurrentStep(nextStep);
        if (nextStep === 2) {
          setCurrentBureauStep(0);
        }
      }
      return;
    }
    
    // Step 2 has its own navigation via handleNextBureau
    if (currentStep === 2) {
      return; // Navigation handled by bureau buttons
    }
    
    // Validate the current step before allowing progression
    const validation = validateCurrentStep(currentStep);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    
    // Save progress for the completed step
    const stepInfo = STEPS[currentStep - 1];
    if (stepInfo) {
      const docs = Object.entries(bureauFolders).flatMap(([bureau, docs]) => 
        docs.map(d => ({ bureau, docType: d.docType, fileName: d.fileName, generatedAt: d.addedAt }))
      );
      saveWizardProgress(currentStep, stepInfo.title, docs);
    }
    
    if (currentStep < STEPS.length) {
      const nextStep = getNextStep(currentStep);
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = async () => {
    // Helper function to get previous step based on dispute type
    const getPrevStep = (current) => {
      // For inquiry_only: skip steps 2 (Import Credit Report), 4 (Dispute Accounts), 7 (Bankruptcy)
      if (disputeType === 'inquiry_only') {
        const skipSteps = [2, 4, 7];
        let prev = current - 1;
        while (skipSteps.includes(prev) && prev >= 1) {
          prev--;
        }
        return prev;
      }
      return current - 1;
    };

    // Save current page data before navigating back
    const stepInfo = STEPS[currentStep - 1];
    if (stepInfo && clientId && clientId !== "new") {
      const docs = Object.entries(bureauFolders).flatMap(([bureau, docs]) => 
        docs.map(d => ({ bureau, docType: d.docType, fileName: d.fileName, generatedAt: d.addedAt }))
      );
      await saveWizardProgress(currentStep, stepInfo.title, docs);
      
      // Also save client info if on step 1
      if (currentStep === 1 && clientForm.name) {
        await saveClientInfo();
      }
    }
    
    // Step 2 has its own back navigation via handlePrevBureau
    if (currentStep === 2) {
      return; // Navigation handled by bureau buttons
    }
    
    if (currentStep === 3 && disputeType !== 'inquiry_only') {
      // Go back to bureau upload, last bureau (only if not inquiry_only)
      setCurrentStep(2);
      setCurrentBureauStep(2);
      return;
    }
    
    if (currentStep > 1) {
      const prevStep = getPrevStep(currentStep);
      setCurrentStep(prevStep);
      toast.success("Progress saved");
    }
  };

  const saveClientInfo = async () => {
    try {
      setLoading(true);
      if (clientId && clientId !== "new") {
        await axios.put(`${API}/clients/${clientId}`, clientForm);
        toast.success("Client information updated");
        return true; // Return success
      } else {
        const response = await axios.post(`${API}/clients`, clientForm);
        setClient(response.data);
        toast.success("Client created successfully");
        // Navigate to the new client URL - will trigger useEffect to load client
        navigate(`/dispute-wizard/${response.data.id}`, { replace: true });
        return true; // Return success
      }
    } catch (error) {
      toast.error("Error saving client information");
      return false; // Return failure
    } finally {
      setLoading(false);
    }
  };

  // ============ CLIENT HISTORY SELECTOR HANDLERS ============
  // Handle selecting a previous client from the section log
  const handleSelectPreviousClient = (sectionClient) => {
    setShowClientSelector(false);
    
    // Pre-fill the client form with data from the section client
    setClientForm({
      name: sectionClient.client_name || '',
      suffix: sectionClient.client_suffix || '',
      email: sectionClient.client_email || '',
      phone: sectionClient.client_phone || '',
      address: sectionClient.client_address || '',
      city: sectionClient.client_city || '',
      state: sectionClient.client_state || '',
      zip_code: sectionClient.client_zip || '',
      dob: sectionClient.client_dob || '',
      ssn: sectionClient.client_ssn || ''
    });
    
    // If section data has saved dispute type, restore it
    if (sectionClient.section_data?.disputeType) {
      setDisputeType(sectionClient.section_data.disputeType);
    }
    
    toast.success(`Loaded client: ${sectionClient.client_name}`);
  };
  
  // Handle creating a new client (close selector and proceed)
  const handleCreateNewClient = () => {
    setShowClientSelector(false);
    // Form is already empty for new client
  };
  
  // Handle creating a new client with parsed data from Smart Paste
  const handleCreateWithParsedData = (parsedData) => {
    setShowClientSelector(false);
    
    // Pre-fill the client state with parsed data
    if (parsedData) {
      // Convert date from MM/DD/YYYY to YYYY-MM-DD for HTML date input
      let formattedDob = parsedData.date_of_birth || '';
      if (formattedDob && formattedDob.includes('/')) {
        const dobParts = formattedDob.split('/');
        if (dobParts.length === 3) {
          formattedDob = `${dobParts[2]}-${dobParts[0].padStart(2, '0')}-${dobParts[1].padStart(2, '0')}`;
        }
      }
      
      setClient(prev => ({
        ...prev,
        name: parsedData.name || prev.name || '',
        email: parsedData.email || prev.email || '',
        phone: parsedData.phone || prev.phone || '',
        address: parsedData.address || prev.address || '',
        city: parsedData.city || prev.city || '',
        state: parsedData.state || prev.state || '',
        zip_code: parsedData.zip_code || prev.zip_code || '',
        date_of_birth: formattedDob || prev.date_of_birth || '',
        ssn_last4: parsedData.ssn_last4 || prev.ssn_last4 || '',
        ssn_full: parsedData.ssn_full || prev.ssn_full || '',
      }));
      
      toast.success(`Client info loaded: ${parsedData.name || 'New Client'}`);
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("client_id", clientId);
    formData.append("document_type", type);

    try {
      setLoading(true);
      await axios.post(`${API}/clients/${clientId}/upload-document`, formData);
      toast.success(`${type === "id_document" ? "ID" : "SSN Card"} uploaded successfully`);
      
      // Refresh documents
      const docsRes = await axios.get(`${API}/clients/${clientId}/documents`);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error("Error uploading document");
    } finally {
      setLoading(false);
    }
  };

  // Improved document upload with preview
  const handleDocumentUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - now including iPhone HEIC/HEIF
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'image/heic', 'image/heif', 'image/webp'];
    const validExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'heic', 'heif', 'webp', 'gif', 'bmp'];
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
      toast.error("Please upload an image (JPG, PNG, HEIC) or PDF file");
      return;
    }

    // Validate file size (max 25MB to handle iPhone photos)
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File size must be less than 25MB");
      return;
    }

    const setUploading = type === "id_document" ? setUploadingId : setUploadingSsn;
    const setPreview = type === "id_document" ? setIdPreview : setSsnPreview;

    try {
      setUploading(true);
      toast.info("Uploading and optimizing image...");

      // Create preview for images (except HEIC which browser can't display)
      if (file.type.startsWith('image/') && !file.type.includes('heic') && !file.type.includes('heif')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (fileExt === 'heic' || fileExt === 'heif') {
        // For HEIC, show processing text - will update after backend processes it
        setPreview('processing');
      } else {
        // For PDFs, show PDF indicator - will update after backend processes it
        setPreview('pdf');
      }

      // Upload to backend (backend will convert to optimized JPEG)
      const formData = new FormData();
      formData.append("file", file);
      formData.append("client_id", clientId);
      formData.append("document_type", type);

      const response = await axios.post(`${API}/clients/${clientId}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 min timeout for large images
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      
      // Refresh documents state
      const docsRes = await axios.get(`${API}/clients/${clientId}/documents`);
      setDocuments(docsRes.data);
      
      // Update preview URL to use server URL (for HEIC/PDF files that were converted)
      let uploadedFileUrl = null;
      if (type === "id_document" && docsRes.data.id_document?.filename) {
        uploadedFileUrl = `${API}/uploads/${docsRes.data.id_document.filename}`;
        setIdPreview(uploadedFileUrl);
      } else if (type === "ssn_card" && docsRes.data.ssn_card?.filename) {
        uploadedFileUrl = `${API}/uploads/${docsRes.data.ssn_card.filename}`;
        setSsnPreview(uploadedFileUrl);
      }
      
      // Register to session for all 3 bureaus (ID/SSN go to all)
      if (uploadedFileUrl) {
        try {
          const docBlob = await axios.get(uploadedFileUrl, { responseType: 'blob' });
          const docType = type === "id_document" ? 'ID_DOC' : 'SOCIAL_SECURITY_CARD';
          const docName = type === "id_document" ? 'ID_Document' : 'SSN_Card';
          const fileName = `${docName}_${clientForm.name.replace(/\s+/g, '_')}.pdf`;
          
          // AUTO-ADD to ALL 3 bureau folders (SAME blob)
          ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(b => {
            addToFolder(b, {
              blob: docBlob.data,
              fileName,
              docType,
              addedAt: new Date().toISOString(),
              id: `${docType}_${b}_${Date.now()}`
            });
          });
          
          console.log(`[UPLOAD] ${docType} auto-added to ALL 3 bureau folders`);
        } catch (regErr) {
          console.warn('[UPLOAD] Could not auto-add uploaded doc:', regErr);
        }
      }
      
      // Show success with file size info if available
      const sizeKb = response.data.size_bytes ? Math.round(response.data.size_bytes / 1024) : null;
      const sizeMsg = sizeKb ? ` (optimized to ${sizeKb}KB)` : '';
      toast.success(`${type === "id_document" ? "ID" : "SSN Card"} uploaded & added to ALL 3 bureau folders!${sizeMsg}`);
    } catch (error) {
      console.error("Upload error:", error);
      setPreview(null);
      toast.error(`Error uploading ${type === "id_document" ? "ID" : "SSN Card"}. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  // Handle chunked upload completion (new reliable system)
  const handleChunkedUploadComplete = async (bureau, result) => {
    try {
      setAccountsLoading(true);
      
      // Refresh ALL accounts and inquiries from database
      const [accountsRes, inquiriesRes, clientRes] = await Promise.all([
        axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`),
        axios.get(`${API}/clients/${clientId}/inquiries`),
        axios.get(`${API}/clients/${clientId}`)
      ]);
      
      setAccounts(deduplicateAccounts(accountsRes.data || []));
      const inquiriesData = inquiriesRes.data?.inquiries || inquiriesRes.data || [];
      setInquiries(deduplicateInquiries(Array.isArray(inquiriesData) ? inquiriesData : []));
      setClient(clientRes.data);
      setAccountsLoading(false);
      
      // Update bureau status
      const newAccounts = result?.accounts || [];
      const score = result?.scores?.experian_score || result?.scores?.equifax_score || result?.scores?.transunion_score;
      
      setBureauUploadStatus(prev => ({
        ...prev,
        [bureau]: { uploaded: true, accountCount: newAccounts.length, score }
      }));
      
      // Move to next bureau or to review
      const bureauOrder = ['experian', 'transunion', 'equifax'];
      const currentIndex = bureauOrder.indexOf(bureau);
      
      if (currentIndex < 2) {
        setCurrentBureauStep(currentIndex + 1);
      } else {
        // All done - go to review accounts DIRECTLY
        setCurrentStep(3);
        toast.success("All reports processed! Moving to Review Accounts...");
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Upload succeeded but failed to refresh data. Please click Next.");
      setAccountsLoading(false);
    }
  };

  // Handle single bureau upload - SIMPLE DIRECT UPLOAD
  const handleBureauUpload = async (event, bureau) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input immediately for retry capability
    if (bureauInputRefs[bureau]?.current) {
      bureauInputRefs[bureau].current.value = "";
    }

    // Validate file
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }

    try {
      setReportUploading(true);
      setLoading(true);
      setAccountsLoading(true);
      
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.loading(`Uploading ${bureau} (${fileSizeMB}MB)...`, { id: 'upload-toast', duration: Infinity });
      
      // Simple direct upload
      const formData = new FormData();
      formData.append("client_id", clientId);
      formData.append("files", file);
      
      console.log('[Upload] Starting direct upload for', bureau);
      
      const response = await fetch(`${API}/import/pdf-async`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errText = await response.text();
        console.error('[Upload] Direct upload failed:', errText);
        throw new Error(`Upload failed: ${errText}`);
      }
      
      const { job_ids } = await response.json();
      const jobId = job_ids[0];
      console.log('[Upload] Job started:', jobId);
      
      // Poll for completion
      let attempts = 0;
      while (attempts < 180) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const statusRes = await fetch(`${API}/import/job-status/${jobId}`);
          if (statusRes.ok) {
            const status = await statusRes.json();
            toast.loading(status.message || 'Processing...', { id: 'upload-toast', duration: Infinity });
            console.log('[Upload] Status:', status.status, status.message);
            if (status.status === 'completed') {
              toast.dismiss('upload-toast');
              toast.success(`${bureau} uploaded successfully!`);
              break;
            }
            if (status.status === 'failed') {
              throw new Error(status.error || 'Processing failed');
            }
          }
        } catch (pollErr) {
          console.warn('[Upload] Poll error:', pollErr);
        }
        attempts++;
      }
      
      if (attempts >= 180) {
        throw new Error('Processing timed out');
      }
      
      // Refresh ALL accounts and inquiries from database
      const [accountsRes, inquiriesRes, clientRes] = await Promise.all([
        axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`),
        axios.get(`${API}/clients/${clientId}/inquiries`),
        axios.get(`${API}/clients/${clientId}`)
      ]);
      
      // Update state SYNCHRONOUSLY before navigation
      setAccounts(deduplicateAccounts(accountsRes.data || []));
      const inquiriesData = inquiriesRes.data?.inquiries || inquiriesRes.data || [];
      setInquiries(deduplicateInquiries(Array.isArray(inquiriesData) ? inquiriesData : []));
      setClient(clientRes.data);
      setAccountsLoading(false);
      
      // Load extracted personal info for PII removal
      if (clientRes.data.report_names || clientRes.data.report_addresses) {
        setReportPersonalInfo({
          names_on_file: clientRes.data.report_names || [],
          addresses_on_file: clientRes.data.report_addresses || [],
          phones_on_file: clientRes.data.report_phones || [],
          employers_on_file: clientRes.data.report_employers || []
        });
      }
      
      // Update bureau status - use manual score if provided, otherwise extracted score
      const extractedScore = clientRes.data?.[`score_${bureau}`];
      const manualScore = manualScores[bureau] ? parseInt(manualScores[bureau]) : null;
      const finalScore = manualScore || extractedScore;
      
      // If manual score was provided, save it to the backend
      if (manualScore && manualScore >= 300 && manualScore <= 850) {
        try {
          await axios.put(`${API}/clients/${clientId}`, {
            [`score_${bureau}`]: manualScore,
            [`starting_score_${bureau}`]: clientRes.data?.[`starting_score_${bureau}`] || manualScore
          });
        } catch (err) {
          console.error('Error saving manual score:', err);
        }
      }
      
      // Get new accounts count from the refreshed data
      const newAccountsCount = accountsRes.data?.length || 0;
      
      setBureauUploadStatus(prev => ({
        ...prev,
        [bureau]: {
          uploaded: true,
          skipped: false,
          score: finalScore,
          accountCount: newAccountsCount
        }
      }));
      
      let successMsg = `${bureau.charAt(0).toUpperCase() + bureau.slice(1)} processed!`;
      if (finalScore) {
        successMsg += ` Score: ${finalScore}`;
      }
      if (newAccountsCount > 0) successMsg += ` (${newAccountsCount} accounts)`;
      
      toast.success(successMsg);
      
      // Navigate based on which bureau was just uploaded
      if (currentBureauStep < 2) {
        // Move to next bureau immediately (no timeout)
        setCurrentBureauStep(prev => prev + 1);
      } else {
        // Last bureau (Equifax) done - navigate to Step 3 DIRECTLY
        console.log("Last bureau done, navigating to Step 3 directly");
        setCurrentStep(3);
        toast.success("All reports processed! Moving to Review Accounts...");
      }
      
    } catch (error) {
      console.error(`${bureau} upload error:`, error);
      toast.dismiss('upload-toast');
      
      let errorMsg = 'Upload failed. ';
      if (error.name === 'AbortError') {
        errorMsg += 'Request timed out. Try with a smaller file or better connection.';
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        errorMsg += 'Network error. Please check your WiFi/cellular connection and try again.';
      } else if (error.message) {
        errorMsg += error.message;
      } else {
        errorMsg += 'Please try again.';
      }
      
      toast.error(`${bureau}: ${errorMsg}`, { duration: 10000 });
      setAccountsLoading(false);
    } finally {
      setReportUploading(false);
      setLoading(false);
    }
  };

  // Skip a bureau
  const handleSkipBureau = (bureau) => {
    setBureauUploadStatus(prev => ({
      ...prev,
      [bureau]: { ...prev[bureau], skipped: true, uploaded: false }
    }));
    toast.info(`${bureau.charAt(0).toUpperCase() + bureau.slice(1)} skipped`);
    
    // Navigate immediately - no timeout needed for skip
    if (currentBureauStep < 2) {
      // Move to next bureau
      setCurrentBureauStep(prev => prev + 1);
    } else {
      // Last bureau (Equifax) skipped - navigate to Review Accounts
      // For skip, we can navigate directly since no accounts are loading
      setCurrentStep(3);
      toast.info("Moving to Review Accounts...");
    }
  };

  // Handle SINGLE report upload for ALL bureaus - SIMPLE DIRECT UPLOAD
  const handleSingleReportUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      if (singleReportInputRef.current) {
        singleReportInputRef.current.value = "";
      }
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 50MB.");
      if (singleReportInputRef.current) {
        singleReportInputRef.current.value = "";
      }
      return;
    }

    // Reset file input immediately
    if (singleReportInputRef.current) {
      singleReportInputRef.current.value = "";
    }

    // Set loading states FIRST
    setReportUploading(true);
    setLoading(true);
    setAccountsLoading(true);
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    toast.loading(`Uploading credit report (${fileSizeMB}MB)...`, { id: 'upload-progress', duration: Infinity });

    try {
      // ============ LATE PAYMENT MODE - Use AI extraction ============
      if (disputeType === 'late_payments') {
        console.log('[LatePaymentUpload] Using AI-powered late payment extraction...');
        toast.loading('🤖 AI analyzing credit report for late payments...', { id: 'upload-progress', duration: Infinity });
        
        const formData = new FormData();
        formData.append("client_id", clientId);
        formData.append("file", file);
        
        const response = await fetch(`${API}/import/extract-late-payments`, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Late payment extraction failed: ${errText}`);
        }
        
        const result = await response.json();
        console.log('[LatePaymentUpload] AI extraction result:', result);
        
        toast.dismiss('upload-progress');
        
        if (result.late_payment_accounts && result.late_payment_accounts.length > 0) {
          // Store late payment accounts
          setLatePaymentAccounts(result.late_payment_accounts);
          
          // Also set regular accounts for display in the wizard
          setAccounts(result.late_payment_accounts);
          
          // Update bureau status with scores
          if (result.scores) {
            setBureauUploadStatus({
              experian: { uploaded: true, skipped: false, score: result.scores.experian || null, accountCount: result.late_payment_accounts.length },
              transunion: { uploaded: true, skipped: false, score: result.scores.transunion || null, accountCount: result.late_payment_accounts.length },
              equifax: { uploaded: true, skipped: false, score: result.scores.equifax || null, accountCount: result.late_payment_accounts.length },
            });
          }
          
          const methodMsg = result.extraction_method === 'ai' ? '🤖 AI' : '📝 Pattern';
          toast.success(`${methodMsg} found ${result.late_payment_accounts.length} accounts with late payments!`);
          
          // Navigate to review accounts step
          setCurrentStep(4);
        } else {
          toast.info("No late payment accounts found in this report. The report may have no late payments, or try a different format.");
        }
        
        setAccountsLoading(false);
        setReportUploading(false);
        setLoading(false);
        return;
      }
      
      // ============ STANDARD MODE - Use async processing ============
      // Simple direct upload
      const formData = new FormData();
      formData.append("client_id", clientId);
      formData.append("files", file);
      formData.append("apply_to_all_bureaus", "true");
      
      console.log('[SingleUpload] Starting direct upload...');
      
      const response = await fetch(`${API}/import/pdf-async`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errText = await response.text();
        console.error('[SingleUpload] Upload failed:', errText);
        throw new Error(`Upload failed: ${errText}`);
      }
      
      const { job_ids } = await response.json();
      const jobId = job_ids[0];
      console.log('[SingleUpload] Job started:', jobId);
      
      // Poll for completion
      let attempts = 0;
      while (attempts < 180) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const statusRes = await fetch(`${API}/import/job-status/${jobId}`);
          if (statusRes.ok) {
            const status = await statusRes.json();
            toast.loading(status.message || 'Processing...', { id: 'upload-progress', duration: Infinity });
            console.log('[SingleUpload] Status:', status.status, status.message);
            if (status.status === 'completed') {
              toast.dismiss('upload-progress');
              toast.success(`Credit report processed successfully!`);
              break;
            }
            if (status.status === 'failed') {
              throw new Error(status.error || 'Processing failed');
            }
          }
        } catch (pollErr) {
          console.warn('[SingleUpload] Poll error:', pollErr);
        }
        attempts++;
      }
      
      if (attempts >= 180) {
        throw new Error('Processing timed out');
      }
      
      // Refresh ALL accounts and inquiries from database
      const [accountsRes, inquiriesRes, clientRes] = await Promise.all([
        axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`),
        axios.get(`${API}/clients/${clientId}/inquiries`),
        axios.get(`${API}/clients/${clientId}`)
      ]);
      
      // Update state SYNCHRONOUSLY
      setAccounts(deduplicateAccounts(accountsRes.data || []));
      const inquiriesData = inquiriesRes.data?.inquiries || inquiriesRes.data || [];
      setInquiries(deduplicateInquiries(Array.isArray(inquiriesData) ? inquiriesData : []));
      setClient(clientRes.data);
      
      // Load extracted personal info for PII removal
      if (clientRes.data.report_names || clientRes.data.report_addresses) {
        setReportPersonalInfo({
          names_on_file: clientRes.data.report_names || [],
          addresses_on_file: clientRes.data.report_addresses || [],
          phones_on_file: clientRes.data.report_phones || [],
          employers_on_file: clientRes.data.report_employers || []
        });
      }
      
      // Get counts from database
      const newAccountsCount = accountsRes.data?.length || 0;
      
      // Mark all bureaus as "uploaded" with scores from the client record
      setBureauUploadStatus({
        experian: { uploaded: true, skipped: false, score: clientRes.data?.score_experian || null, accountCount: newAccountsCount },
        transunion: { uploaded: true, skipped: false, score: clientRes.data?.score_transunion || null, accountCount: newAccountsCount },
        equifax: { uploaded: true, skipped: false, score: clientRes.data?.score_equifax || null, accountCount: newAccountsCount },
      });
      
      // Clear loading states BEFORE navigation
      setAccountsLoading(false);
      setReportUploading(false);
      setLoading(false);
      
      // Check if any scores were extracted - show modal if missing
      const missing = [];
      if (!clientRes.data?.score_equifax) missing.push('equifax');
      if (!clientRes.data?.score_experian) missing.push('experian');
      if (!clientRes.data?.score_transunion) missing.push('transunion');
      
      if (missing.length > 0) {
        setMissingScoreBureaus(missing);
        setShowScoreModal(true);
        toast.warning(`Credit scores not detected for ${missing.join(', ')}. Please enter manually.`, {
          duration: 6000
        });
      }
      
      toast.success(`Report processed! ${newAccountsCount} accounts found and will be disputed with ALL 3 bureaus.`);
      
      // Navigate to Step 3 DIRECTLY - no more pending state needed since accounts are loaded
      console.log("Single report done, navigating to Step 3 directly");
      setCurrentStep(3);
      
    } catch (error) {
      console.error("Single report upload error:", error);
      toast.dismiss('upload-progress');
      
      // Provide specific error message
      let errorMsg = "Upload failed. ";
      if (error.name === 'AbortError') {
        errorMsg += "Request timed out. Try with a smaller file or better WiFi connection.";
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        errorMsg += "Network error. Please check your WiFi/cellular and try again.";
      } else if (error.message) {
        errorMsg += error.message;
      } else {
        errorMsg += "Please try again.";
      }
      
      toast.error(errorMsg, { duration: 10000 });
      setAccountsLoading(false);
      setReportUploading(false);
      setLoading(false);
    }
    
    // Always clear the file input
    if (singleReportInputRef.current) {
      singleReportInputRef.current.value = "";
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // SMART PASTE: Parse pasted client info using AI
  // ═══════════════════════════════════════════════════════════════════
  const handleSmartPasteClientInfo = async () => {
    if (!pastedClientInfo.trim()) {
      toast.error("Please paste your client information first");
      return;
    }

    setParsingClientInfo(true);
    try {
      toast.info("Parsing client information...", { id: 'parsing-client' });
      
      const response = await axios.post(`${API}/smart-parse`, {
        text: pastedClientInfo
      }, { timeout: 30000 });
      
      const parsed = response.data.data || response.data;
      
      // Update the client form with parsed data
      setClientForm(prev => ({
        ...prev,
        name: parsed.name || prev.name || '',
        email: parsed.email || prev.email || '',
        phone: parsed.phone || prev.phone || '',
        date_of_birth: parsed.date_of_birth || prev.date_of_birth || '',
        address: parsed.address || prev.address || '',
        city: parsed.city || prev.city || '',
        state: parsed.state || prev.state || '',
        zip_code: parsed.zip_code || prev.zip_code || '',
        ssn_last4: parsed.ssn_last4 || prev.ssn_last4 || '',
        ssn_full: parsed.ssn_full || prev.ssn_full || '',
      }));
      
      toast.dismiss('parsing-client');
      toast.success("Client information parsed successfully!");
      setShowSmartPaste(false);
      setPastedClientInfo("");
      
      // If there were warnings, show them
      if (parsed._validation_warnings && parsed._validation_warnings.length > 0) {
        toast.warning(`Please verify: ${parsed._validation_warnings.join(', ')}`);
      }
      
    } catch (error) {
      console.error("Smart paste error:", error);
      toast.dismiss('parsing-client');
      toast.error(error.response?.data?.detail || "Failed to parse client info. Please enter manually.");
    } finally {
      setParsingClientInfo(false);
    }
  };

  // Handle pasted account text for manual entry
  const handlePastedAccountsSubmit = async () => {
    if (!pastedAccountText.trim()) {
      toast.error("Please paste your account information first");
      return;
    }

    try {
      setLoading(true);
      setAccountsLoading(true);
      toast.info("Parsing pasted accounts for ALL 3 bureaus...");
      
      // ==========================================
      // FRONTEND PARSING (same as FTCBuilderPage)
      // ==========================================
      const text = pastedAccountText;
      const parsedAccounts = [];
      
      // Helper to clean amount
      const cleanAmount = (amt) => {
        if (!amt) return '';
        return '$' + String(amt).replace(/[$,]/g, '').trim();
      };
      
      // Helper to format date
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.replace(/\//g, '-').split('-');
        if (parts.length >= 2) {
          return `${parts[0]}-${parts[parts.length - 1]}`;
        }
        return dateStr;
      };
      
      // Normalize line endings and split
      const lines = text.split(/\r\n|\r|\n/).map(line => line.trim()).filter(line => line.length > 0);
      
      console.log(`DisputeWizard: Parsing ${lines.length} lines`);
      
      lines.forEach((line, index) => {
        let trimmed = line.trim();
        console.log(`Parsing line ${index}: "${trimmed}"`);
        
        if (trimmed && trimmed.length > 2) {
          const lowerLine = trimmed.toLowerCase();
          // Skip header lines
          if (lowerLine.includes('creditor') && lowerLine.includes('name') || 
              lowerLine.includes('account name') || 
              lowerLine.includes('---')) {
            console.log(`  Skipping header line`);
            return;
          }
          
          let creditorName = '';
          let dateOpened = '';
          let fraudAmount = '';
          let accountNumber = '';
          
          // First, try to extract the dollar amount (always at the end)
          const amountMatch = trimmed.match(/\$[\d,]+(?:\.\d{2})?$/);
          if (amountMatch) {
            fraudAmount = cleanAmount(amountMatch[0]);
            // Remove the amount from the string
            trimmed = trimmed.substring(0, trimmed.lastIndexOf(amountMatch[0])).trim();
            // Remove trailing dash
            trimmed = trimmed.replace(/-+$/, '').trim();
          }
          
          // Now try to extract the date (MM-DD-YYYY or MM/DD/YYYY pattern at the end)
          const dateMatch = trimmed.match(/[-–]?(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
          if (dateMatch) {
            // Format as MM-YYYY (month-year only)
            const month = dateMatch[1];
            const year = dateMatch[3];
            dateOpened = `${month}-${year}`;
            // Remove the date from the string
            const dateIndex = trimmed.lastIndexOf(dateMatch[0].replace(/^[-–]/, ''));
            if (dateIndex > 0) {
              trimmed = trimmed.substring(0, dateIndex).trim();
            } else {
              trimmed = trimmed.replace(dateMatch[0], '').trim();
            }
            // Remove trailing dash
            trimmed = trimmed.replace(/[-–]+$/, '').trim();
          }
          
          // What's left is the creditor name
          creditorName = trimmed.replace(/[-–]+$/, '').trim();
          
          // Clean up creditor name
          creditorName = creditorName.replace(/\s*\$[\d,]+\s*$/, '').trim();
          
          // Only add if we have at least a creditor name
          if (creditorName) {
            const uniqueId = `parsed_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
            const balanceNum = parseFloat((fraudAmount || '0').replace(/[$,]/g, '')) || 0;
            
            parsedAccounts.push({
              id: uniqueId,
              creditor_name: creditorName.toUpperCase(),
              account_number: accountNumber,
              balance_owed: balanceNum,
              date_opened: dateOpened,
              bureau: 'equifax, experian, transunion',
              status: 'Open'
            });
            
            console.log(`  Parsed account: ${creditorName.toUpperCase()} | ${dateOpened} | $${balanceNum}`);
          }
        }
      });
      
      console.log(`DisputeWizard: Total parsed accounts: ${parsedAccounts.length}`);
      
      if (parsedAccounts.length === 0) {
        toast.error("No accounts could be parsed from the text. Please check the format.");
        setLoading(false);
        setAccountsLoading(false);
        return;
      }
      
      // ==========================================
      // SAVE TO DATABASE
      // ==========================================
      // Save each parsed account to the database
      const savedAccounts = [];
      for (const acc of parsedAccounts) {
        try {
          const response = await axios.post(`${API}/accounts`, {
            client_id: clientId,
            creditor_name: acc.creditor_name,
            account_number: acc.account_number || '',
            balance: acc.balance_owed,  // Use 'balance' field
            balance_owed: acc.balance_owed,
            original_balance: acc.balance_owed,
            date_opened: acc.date_opened,
            bureau: 'equifax, experian, transunion',
            status: 'Open',
            account_type: 'Unknown'
          });
          savedAccounts.push(response.data);
          console.log(`Saved account: ${acc.creditor_name} with balance ${acc.balance_owed}`);
        } catch (err) {
          console.error(`Failed to save account ${acc.creditor_name}:`, err);
        }
      }
      
      // Refresh accounts from database - DON'T deduplicate for pasted accounts
      // because we want to keep all accounts even if they have same creditor name
      const [accountsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`),  // CRITICAL: dedupe=false
        axios.get(`${API}/clients/${clientId}/inquiries`),
      ]);
      
      // DON'T deduplicate - keep all accounts as-is
      // The user explicitly wants each pasted line to be a separate account
      const allAccounts = accountsRes.data || [];
      console.log(`DisputeWizard: Retrieved ${allAccounts.length} accounts from database`);
      setAccounts(allAccounts);
      const inquiriesData = inquiriesRes.data?.inquiries || inquiriesRes.data || [];
      setInquiries(deduplicateInquiries(Array.isArray(inquiriesData) ? inquiriesData : []));
      setAccountsLoading(false);
      
      // Mark upload as complete
      setBureauUploadStatus({
        experian: { uploaded: true, skipped: false, score: null, accountCount: parsedAccounts.length },
        transunion: { uploaded: true, skipped: false, score: null, accountCount: parsedAccounts.length },
        equifax: { uploaded: true, skipped: false, score: null, accountCount: parsedAccounts.length },
      });
      
      setShowPasteOption(false);
      setPastedAccountText("");
      toast.success(`Added ${parsedAccounts.length} accounts from pasted text!`);
      
      // Navigate to Step 3 DIRECTLY
      console.log("Paste accounts done, navigating to Step 3 directly");
      setCurrentStep(3);
      
    } catch (error) {
      console.error("Paste accounts error:", error);
      toast.error("Error processing accounts. Please try again.");
      setAccountsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // Add incorrect name for PII letter
  const addIncorrectName = () => {
    if (newNameInput.trim()) {
      setIncorrectNames(prev => [...prev, newNameInput.trim()]);
      setNewNameInput("");
    }
  };

  // Add incorrect address for PII letter
  const addIncorrectAddress = () => {
    if (newAddressInput.trim()) {
      setIncorrectAddresses(prev => [...prev, newAddressInput.trim()]);
      setNewAddressInput("");
    }
  };

  // Add incorrect phone for PII letter
  const addIncorrectPhone = () => {
    if (newPhoneInput.trim()) {
      setIncorrectPhones(prev => [...prev, newPhoneInput.trim()]);
      setNewPhoneInput("");
    }
  };

  // Add incorrect employer for PII letter
  const addIncorrectEmployer = () => {
    if (newEmployerInput.trim()) {
      setIncorrectEmployers(prev => [...prev, newEmployerInput.trim()]);
      setNewEmployerInput("");
    }
  };

  // Remove incorrect name
  const removeIncorrectName = (index) => {
    setIncorrectNames(prev => prev.filter((_, i) => i !== index));
  };

  // Remove incorrect address
  const removeIncorrectAddress = (index) => {
    setIncorrectAddresses(prev => prev.filter((_, i) => i !== index));
  };

  // Remove incorrect phone
  const removeIncorrectPhone = (index) => {
    setIncorrectPhones(prev => prev.filter((_, i) => i !== index));
  };

  // Remove incorrect employer
  const removeIncorrectEmployer = (index) => {
    setIncorrectEmployers(prev => prev.filter((_, i) => i !== index));
  };

  // Bulk paste items (addresses, names, phones, employers)
  const handleBulkPaste = () => {
    if (!bulkPasteInput.trim()) return;
    
    // Split by newlines, filter empty lines
    const items = bulkPasteInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    if (items.length === 0) {
      toast.error("No items found to add");
      return;
    }
    
    switch (bulkPasteType) {
      case 'addresses':
        setIncorrectAddresses(prev => [...prev, ...items]);
        break;
      case 'names':
        setIncorrectNames(prev => [...prev, ...items]);
        break;
      case 'phones':
        setIncorrectPhones(prev => [...prev, ...items]);
        break;
      case 'employers':
        setIncorrectEmployers(prev => [...prev, ...items]);
        break;
      default:
        break;
    }
    
    setBulkPasteInput("");
    toast.success(`Added ${items.length} ${bulkPasteType} to remove`);
  };

  // Save PII data to client
  const savePiiData = async () => {
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        ...clientForm,
        incorrect_names: incorrectNames,
        incorrect_addresses: incorrectAddresses,
        incorrect_phones: incorrectPhones,
        incorrect_employers: incorrectEmployers,
      });
      setPiiLetterGenerated(true);
      toast.success("Personal information saved for removal letters!");
    } catch (error) {
      toast.error("Error saving personal information");
    }
  };

  // Go to next bureau
  const handleNextBureau = () => {
    if (currentBureauStep < 2) {
      setCurrentBureauStep(prev => prev + 1);
    } else {
      // All bureaus done, move to next wizard step
      setCurrentStep(3);
    }
  };

  // Go to previous bureau
  const handlePrevBureau = () => {
    if (currentBureauStep > 0) {
      setCurrentBureauStep(prev => prev - 1);
    } else {
      // Go back to upload mode selection
      setUploadMode(null);
    }
  };

  // Handle bulk paste of inquiries
  const handleInquiryPaste = () => {
    if (!inquiryPasteText.trim()) {
      toast.error("Please paste inquiry information");
      return;
    }
    
    // Parse pasted text - each line is a separate inquiry
    // Format: "Company Name - Date" or just "Company Name"
    const lines = inquiryPasteText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    if (lines.length === 0) {
      toast.error("No valid inquiries found");
      return;
    }
    
    const newInquiries = lines.map((line, idx) => {
      // Try to parse "Company Name - Date" format
      const parts = line.split(/[-–—]/);
      const companyName = parts[0]?.trim() || line;
      const date = parts[1]?.trim() || new Date().toLocaleDateString();
      
      return {
        id: `manual-${Date.now()}-${idx}`,
        company_name: companyName.toUpperCase(),
        inquiry_date: date,
        bureau: inquiryPasteBureau,
        bureaus: [inquiryPasteBureau],
        bureaus_present: [inquiryPasteBureau],
        manual: true, // Flag to identify manually added inquiries
        purpose: "Hard Inquiry"
      };
    });
    
    // Add to inquiries list
    setInquiries(prev => [...prev, ...newInquiries]);
    setManualInquiries(prev => [...prev, ...newInquiries]);
    setInquiryPasteText("");
    toast.success(`Added ${newInquiries.length} inquiries to ${inquiryPasteBureau.toUpperCase()} dispute`);
  };

  // Remove manual inquiry
  const removeManualInquiry = (inquiryId) => {
    setInquiries(prev => prev.filter(i => i.id !== inquiryId));
    setManualInquiries(prev => prev.filter(i => i.id !== inquiryId));
    toast.info("Inquiry removed");
  };

  // Save manual credit scores
  const saveManualScores = async () => {
    try {
      const updates = {};
      if (manualScores.equifax) updates.starting_score_equifax = parseInt(manualScores.equifax);
      if (manualScores.experian) updates.starting_score_experian = parseInt(manualScores.experian);
      if (manualScores.transunion) updates.starting_score_transunion = parseInt(manualScores.transunion);
      
      if (Object.keys(updates).length === 0) {
        toast.error("Please enter at least one credit score");
        return;
      }
      
      await axios.put(`${API}/clients/${clientId}`, {
        ...clientForm,
        ...updates
      });
      
      // Save to NEW score registry as MANUAL_OVERRIDE (single source of truth)
      for (const [bureau, score] of Object.entries(manualScores)) {
        if (score && parseInt(score) >= 300 && parseInt(score) <= 850) {
          try {
            await axios.post(`${API}/scores/${clientId}/manual`, {
              bureau: bureau.toUpperCase(),
              score_value: parseInt(score),
              score_date: new Date().toISOString().split('T')[0],
              score_model: "MANUAL_OVERRIDE"
            });
            console.log(`[SCORE] Manual override saved: ${bureau} = ${score}`);
          } catch (scoreErr) {
            console.warn(`Failed to save score to new registry: ${scoreErr}`);
          }
        }
      }
      
      // Also save to legacy score snapshots for backward compatibility
      for (const [bureau, score] of Object.entries(manualScores)) {
        if (score) {
          await axios.post(`${API}/scores/snapshot`, {
            client_id: clientId,
            bureau: bureau,
            score: parseInt(score),
            is_starting_score: true,
            source: "manual_entry"
          });
        }
      }
      
      // Refresh client data
      const clientRes = await axios.get(`${API}/clients/${clientId}`);
      setClient(clientRes.data);
      // Map client data to form fields properly
      setClientForm({
        name: clientRes.data.name || "",
        email: clientRes.data.email || "",
        phone: clientRes.data.phone || "",
        address: clientRes.data.address || "",
        city: clientRes.data.city || "",
        state: clientRes.data.state || "",
        zip_code: clientRes.data.zip_code || "",
        ssn_last4: clientRes.data.ssn_last4 || "",
        ssn_full: clientRes.data.ssn_full || clientRes.data.ssn || "",
        date_of_birth: clientRes.data.date_of_birth || "",
      });
      
      toast.success("Credit scores saved successfully (Manual Override)!");
    } catch (error) {
      console.error("Error saving scores:", error);
      toast.error("Error saving credit scores");
    }
  };

  // Re-extract data from credit report (for failed extractions)
  const [reextracting, setReextracting] = useState(false);
  const reExtractData = async (extractType = "all") => {
    try {
      setReextracting(true);
      toast.info(`Re-extracting ${extractType} data from credit report...`);
      
      const response = await axios.post(`${API}/extraction/re-extract/${clientId}?extract_type=${extractType}`);
      const results = response.data;
      
      // Show results
      if (results.messages && results.messages.length > 0) {
        results.messages.forEach(msg => {
          if (msg.toLowerCase().includes('error') || msg.toLowerCase().includes('no ')) {
            toast.warning(msg);
          } else {
            toast.success(msg);
          }
        });
      }
      
      // Refresh data after re-extraction
      if (extractType === "all" || extractType === "scores") {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        setClient(clientRes.data);
        // Map client data to form fields properly
        setClientForm({
          name: clientRes.data.name || "",
          email: clientRes.data.email || "",
          phone: clientRes.data.phone || "",
          address: clientRes.data.address || "",
          city: clientRes.data.city || "",
          state: clientRes.data.state || "",
          zip_code: clientRes.data.zip_code || "",
          ssn_last4: clientRes.data.ssn_last4 || "",
          ssn_full: clientRes.data.ssn_full || clientRes.data.ssn || "",
          date_of_birth: clientRes.data.date_of_birth || "",
        });
      }
      
      if (extractType === "all" || extractType === "inquiries") {
        const inquiriesRes = await axios.get(`${API}/clients/${clientId}/inquiries`);
        setInquiries(inquiriesRes.data.inquiries || []);
      }
      
      if (extractType === "all" || extractType === "pii") {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        setReportPersonalInfo({
          names_on_file: clientRes.data.report_names || [],
          addresses_on_file: clientRes.data.report_addresses || [],
          phones_on_file: clientRes.data.report_phones || [],
          employers_on_file: clientRes.data.report_employers || []
        });
      }
      
      toast.success("Re-extraction complete!");
    } catch (error) {
      console.error("Re-extraction error:", error);
      toast.error(error.response?.data?.detail || "Re-extraction failed. Please try manual input.");
    } finally {
      setReextracting(false);
    }
  };

  // Download inquiry dispute letter for pasted inquiries
  const downloadPastedInquiryLetter = async (bureau) => {
    const bureauInquiries = manualInquiries.filter(i => i.bureau?.toLowerCase() === bureau.toLowerCase());
    
    if (bureauInquiries.length === 0) {
      toast.error(`No pasted inquiries for ${bureau.toUpperCase()}`);
      return;
    }
    
    try {
      setLoading(true);
      toast.loading(`Generating ${bureau.toUpperCase()} inquiry letter...`, { id: 'inq-letter' });
      
      const response = await axios.post(
        `${API}/dispute-package/download-inquiry-letter`,
        { 
          client_id: clientId, 
          bureau: bureau,
          inquiry_ids: bureauInquiries.map(i => i.id),
          manual_inquiries: bureauInquiries.map(i => ({
            company_name: i.company_name,
            inquiry_date: i.inquiry_date
          })),
          signature_data: signatureData,
          dispute_type: disputeType,  // Pass dispute type to indicate identity theft/fraud
          is_identity_theft: disputeType === 'inquiry_only' || disputeType === 'identity_theft'
        },
        { responseType: "blob", timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const today = new Date().toISOString().split('T')[0];
      const filename = `5_${today}_${bureau.charAt(0).toUpperCase() + bureau.slice(1)}_Inquiry_Dispute.pdf`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss('inq-letter');
      toast.success(`Downloaded ${bureau.toUpperCase()} inquiry letter with ${bureauInquiries.length} inquiries!`);
    } catch (error) {
      toast.dismiss('inq-letter');
      toast.error("Error downloading inquiry letter");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Check if current bureau is done (uploaded or skipped)
  const isBureauDone = (bureau) => {
    return bureauUploadStatus[bureau].uploaded || bureauUploadStatus[bureau].skipped;
  };

  // Upload PDF to server with progress tracking
  const uploadPdfToServer = async (formData, onProgress) => {
    return axios.post(`${API}/import/pdf-multiple`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          if (onProgress) onProgress(percent);
        }
      },
      timeout: 300000 // 5 minute timeout for large PDFs
    });
  };

  const handleCreditReportUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append("client_id", clientId);
    const fileNames = [];
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
      fileNames.push(files[i].name);
    }

    try {
      setReportUploading(true);
      setLoading(true);
      toast.info(`Processing ${files.length} credit report(s)... This may take 1-2 minutes. Please wait.`);
      
      // Upload with progress feedback - Safari optimized
      const response = await uploadPdfToServer(formData, (percent) => {
        if (percent < 100) {
          toast.loading(`Uploading: ${percent}%`, { id: 'upload-progress' });
        } else {
          toast.loading('Processing report with AI... Please wait.', { id: 'upload-progress' });
        }
      });
      toast.dismiss('upload-progress');
      
      const newAccounts = response.data.accounts || [];
      const newInquiries = response.data.inquiries || [];
      const scoresExtracted = response.data.scores_extracted || {};
      const deduplicationStats = response.data.deduplication_stats || {};
      
      // Refresh ALL accounts and inquiries from database to show cumulative data
      const [accountsRes, inquiriesRes, clientRes] = await Promise.all([
        axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`),
        axios.get(`${API}/clients/${clientId}/inquiries`),
        axios.get(`${API}/clients/${clientId}`)
      ]);
      
      setAccounts(deduplicateAccounts(accountsRes.data || []));
      // CRITICAL FIX: inquiries endpoint returns {inquiries: [...]} not just an array
      const inquiriesData = inquiriesRes.data?.inquiries || inquiriesRes.data || [];
      setInquiries(deduplicateInquiries(Array.isArray(inquiriesData) ? inquiriesData : []));
      setClient(clientRes.data);
      
      const totalAccounts = accountsRes.data?.length || 0;
      const totalInquiries = Array.isArray(inquiriesData) ? inquiriesData.length : 0;
      
      // Build success message with scores
      let successMessage = `Processed report! Total: ${totalAccounts} accounts, ${totalInquiries} inquiries.`;
      
      if (newAccounts.length > 0) {
        successMessage = `Added ${newAccounts.length} new accounts. Total: ${totalAccounts} accounts, ${totalInquiries} inquiries.`;
      }
      
      // Show extracted scores in toast
      const scoreKeys = Object.keys(scoresExtracted).filter(k => !k.includes('starting'));
      if (scoreKeys.length > 0) {
        const scoreList = scoreKeys.map(k => {
          const bureau = k.replace('score_', '').charAt(0).toUpperCase() + k.replace('score_', '').slice(1);
          return `${bureau}: ${scoresExtracted[k]}`;
        }).join(', ');
        successMessage += ` Scores: ${scoreList}`;
      }
      
      if (totalAccounts > 0 || totalInquiries > 0 || scoreKeys.length > 0) {
        toast.success(successMessage);
      } else {
        toast.warning("No derogatory accounts or inquiries found. Try uploading different reports or check the file format.");
      }
    } catch (error) {
      console.error("Credit report upload error:", error);
      // Provide more detailed error messages
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Upload timed out. Please try again with smaller files or one at a time.");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "Invalid file format. Please upload valid PDF credit reports.");
      } else if (error.response?.status === 404) {
        toast.error("Client not found. Please refresh and try again.");
      } else {
        toast.error("Error processing credit reports. Please try again.");
      }
    } finally {
      setReportUploading(false);
      setLoading(false);
      // CRITICAL: Reset the file input so the same files can be selected again
      if (creditReportInputRef.current) {
        creditReportInputRef.current.value = "";
      }
    }
  };

  const generateAllLetters = async () => {
    try {
      setGeneratingLetters(true);
      toast.info("Generating dispute letters for all bureaus...");
      
      // Only include accounts that are selected (not excluded)
      const accountIds = accounts
        .filter(a => isAccountIncluded(a.id) && a.status !== "removed")
        .map(a => a.id);
      
      if (accountIds.length === 0) {
        toast.error("No accounts selected for dispute");
        setGeneratingLetters(false);
        return;
      }
      
      const response = await axios.post(`${API}/letters/generate-bulk`, {
        client_id: clientId,
        account_ids: accountIds,
        strategy: disputeType === 'late_payments' ? 'late_payments' : 'identity_theft',
        bureau: "all_bureaus",
      }, { timeout: 180000 });
      
      // Refresh letters
      const lettersRes = await axios.get(`${API}/letters?client_id=${clientId}`);
      setLetters(lettersRes.data);
      
      toast.success(`Generated ${response.data.letters?.length || 0} dispute letters! Downloading...`);
      
      // AUTO-DOWNLOAD: Download all bureau letters immediately after generation
      setTimeout(async () => {
        let anyDownloadFailed = false;
        try {
          // Download all 3 bureau letters
          for (const bureau of ['equifax', 'experian', 'transunion']) {
            try {
              await downloadBureauLetter(bureau);
            } catch (perLetterErr) {
              anyDownloadFailed = true;
              console.error(`Auto-download failed for ${bureau}:`, perLetterErr);
            }
            await new Promise(r => setTimeout(r, 500)); // Small delay between downloads
          }
          if (!anyDownloadFailed) {
            toast.success("All dispute letters downloaded!");
          } else {
            toast.warning("Some letters didn't auto-download — you can still generate the bureau forms below.");
          }
        } catch (downloadErr) {
          console.error("Auto-download error:", downloadErr);
        } finally {
          // Feb 2026 — offer official TransUnion + Equifax paper forms
          // right after the dispute letters download.  Fires from
          // `finally` so it always shows up even if a per-letter
          // download choked (e.g. browser blocked the second download).
          setTimeout(() => setBureauFormsPromptOpen(true), 700);
        }
      }, 1000);
      
    } catch (error) {
      console.error("Error generating letters:", error);
      toast.error(error.response?.data?.detail || "Error generating letters. Using fallback templates.");
    } finally {
      setGeneratingLetters(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Feb 2026 — bureau paper-form filler.  Pulls current client
  // identity, disputed accounts, and the wizard signature, hits
  // POST /api/bureau-dispute-forms/generate, and downloads whatever
  // the backend returns.  Supports "transunion", "equifax", or both.
  // TU auto-splits at 4 accounts / form; Equifax at 12 / form; the
  // backend returns one merged PDF per bureau either way.
  // ────────────────────────────────────────────────────────────────
  const runBureauFormsFill = async (bureaus, { editFirst = false } = {}) => {
    if (!bureaus || bureaus.length === 0) return;
    if (!client) {
      toast.error("Client data not loaded — refresh and try again");
      return;
    }
    try {
      setBureauFormsBusy(true);
      // Pick the accounts actually being disputed (fall back to all
      // accounts if the user never explicitly selected).
      const source = (selectedAccountsForDispute && selectedAccountsForDispute.length)
        ? selectedAccountsForDispute
        : accounts;
      const acctPayload = (source || []).map((a) => ({
        creditor_name: a.creditor_name || a.company_name || a.account_name || "",
        account_number: a.account_number || a.account_last4 || "",
        // 2026-09-26 — Pass date + balance so each dispute slot shows
        // "NAME - MM-YYYY - $BAL" (matches the dispute-letter format
        // and lets clients tell duplicate creditors apart on the form).
        date_opened: a.date_fraud_began || a.date_opened || a.date || "",
        balance: a.balance || a.amount || "",
        reason: "this_is_not_my_account",
      }));
      const client_info = {
        full_name: client.name || client.full_name || "",
        dob: client.dob || client.date_of_birth || "",
        ssn: client.ssn || "",
        address: client.address || client.street_address || "",
        city: client.city || "",
        state: client.state || "",
        zip: client.zip || client.zip_code || "",
        // Feb 2026 — some clients store the full address as one string
        // (e.g. "4294 SAGINAW AVE, SAN DIEGO, CA 92117") with empty
        // city/state/zip cols.  Ship the raw compound address too so
        // the backend can parse the tail if any of those slots is empty.
        full_address: [client.address, client.city, client.state, client.zip]
          .filter(Boolean).join(", ") || client.full_address || "",
        phone: client.phone || "",
        email: client.email || "",
      };
      toast.info(`Generating ${bureaus.length === 2 ? "TransUnion + Equifax" : bureaus[0]} form${bureaus.length > 1 ? "s" : ""}…`);
      const resp = await axios.post(`${API}/bureau-dispute-forms/generate`, {
        bureaus,
        client_info,
        accounts: acctPayload,
        signature_data: signatureData || null,
        single_file: true,
      }, { timeout: 90000 });
      const files = resp?.data?.files || [];
      const usable = files.filter((f) => f && f.data_url);
      const failed = files.filter((f) => f && !f.data_url);
      failed.forEach((f) => toast.error(`${f.bureau}: ${f.error || "generation failed"}`));

      if (editFirst && usable.length > 0) {
        // Open the free-form editor on the first file; queue the rest.
        const [first, ...rest] = usable;
        setBureauEditorPdf(first.data_url);
        setBureauEditorLabel(first.label || first.bureau);
        setBureauEditorFilename(first.filename || `${first.label}_Dispute_Form.pdf`);
        setBureauEditorQueue(rest.map((f) => ({
          data_url: f.data_url,
          label: f.label || f.bureau,
          filename: f.filename || `${f.label}_Dispute_Form.pdf`,
        })));
        setBureauEditorOpen(true);
      } else {
        // Auto-download path (unchanged behaviour).
        let downloaded = 0;
        for (const f of usable) {
          const link = document.createElement("a");
          link.href = f.data_url;
          link.download = f.filename || `${f.bureau}_dispute_form.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          downloaded += 1;
          await new Promise(r => setTimeout(r, 350));
        }
        if (downloaded > 0) {
          toast.success(`Downloaded ${downloaded} pre-filled bureau form${downloaded === 1 ? "" : "s"}`);
        } else if (!failed.length) {
          toast.error("No bureau forms were produced — check backend logs");
        }
      }
      setBureauFormsPromptOpen(false);
    } catch (e) {
      console.error("[bureau-forms] fill failed", e);
      toast.error(e?.response?.data?.detail || e.message || "Failed to generate bureau forms");
    } finally {
      setBureauFormsBusy(false);
    }
  };

  // Called when the editor modal closes — either advances to the next
  // queued bureau (BOTH flow) or fully closes the flow.
  const advanceBureauEditorQueue = () => {
    setBureauEditorOpen(false);
    setBureauEditorPdf(null);
    if (bureauEditorQueue.length > 0) {
      const [next, ...rest] = bureauEditorQueue;
      setBureauEditorPdf(next.data_url);
      setBureauEditorLabel(next.label);
      setBureauEditorFilename(next.filename);
      setBureauEditorQueue(rest);
      // Reopen on next tick so React commits the state changes first.
      setTimeout(() => setBureauEditorOpen(true), 40);
    }
  };

  const generateInquiryLetter = async () => {
    try {
      setGeneratingLetters(true);
      
      const response = await axios.post(`${API}/letters/generate-inquiry-dispute`, {
        client_id: clientId,
        bureau: "all_bureaus",
      }, { timeout: 180000 });
      
      // Refresh letters
      const lettersRes = await axios.get(`${API}/letters?client_id=${clientId}`);
      setLetters(lettersRes.data);
      
      toast.success("Inquiry dispute letter generated");
    } catch (error) {
      toast.error("Error generating inquiry letter");
    } finally {
      setGeneratingLetters(false);
    }
  };

  const generateFTCReport = async (customDate = null) => {
    try {
      setLoading(true);
      toast.info("Generating FTC Identity Theft Report...");
      
      // Get account IDs - filter for included accounts only
      const accountIds = accounts
        .filter(a => isAccountIncluded(a.id))
        .map(a => a.id);
      
      // VALIDATE: Must have at least one account
      if (accountIds.length === 0) {
        toast.error("No accounts found. Please import accounts first using Sniper Import or PDF upload.");
        setLoading(false);
        return;
      }
      
      console.log(`[FTC] Generating report with ${accountIds.length} accounts:`, accountIds.slice(0, 3));
      
      const response = await axios.post(
        `${API}/ftc-affidavit/download-pdf`,
        { 
          client_id: clientId, 
          account_ids: accountIds,
          custom_date: customDate // Pass custom date to backend
        },
        { responseType: "blob" }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Extract filename from Content-Disposition header (contains report number)
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'IDTheftReport.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // AUTO-ADD to ALL 3 bureau folders (SAME blob)
      ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(b => {
        addToFolder(b, {
          blob,
          fileName: filename,
          docType: 'FTC_REPORT',
          addedAt: new Date().toISOString(),
          id: `FTC_REPORT_${b}_${Date.now()}`
        });
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setFtcReportGenerated(true);
      toast.success("FTC Report downloaded & added to ALL 3 bureau folders!");
    } catch (error) {
      console.error("[FTC] Error:", error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || "Failed to generate FTC report";
      toast.error(`FTC Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle FTC download - ONE report (not per-bureau)
  const handleFTCDownload = async () => {
    try {
      setLoading(true);
      toast.info('Generating FTC Identity Theft Report...');
      
      // Support both 5-slots mode and classic mode
      let allAssignedIds = [];
      let sectionAssignments = {};
      
      if (ftcSlotsMode && ftcSlots.some(s => s.account_ids.length > 0)) {
        // 5-Slots Mode: Get IDs from ftcSlots
        allAssignedIds = ftcSlots.flatMap(slot => slot.account_ids);
        
        // Convert slots to section assignments format
        ftcSlots.forEach(slot => {
          if (slot.category && slot.account_ids.length > 0) {
            sectionAssignments[slot.category] = slot.account_ids;
          }
        });
      } else {
        // Classic Mode: Get IDs from ftcSections
        // Handle both old format (array of IDs) and new format (array of objects with account_log_id)
        Object.entries(ftcSections).forEach(([category, items]) => {
          const ids = items.map(item => {
            if (typeof item === 'string') return item;
            if (item && item.account_log_id) return item.account_log_id;
            return null;
          }).filter(Boolean);
          
          if (ids.length > 0) {
            sectionAssignments[category] = ids;
            allAssignedIds.push(...ids);
          }
        });
      }
      
      // Remove duplicates
      allAssignedIds = [...new Set(allAssignedIds)];
      
      if (allAssignedIds.length === 0) {
        toast.error('Please assign at least one account to a section');
        setLoading(false);
        return;
      }
      
      console.log('[FTC Download] Sending request:', {
        client_id: clientId,
        account_count: allAssignedIds.length,
        sections: Object.keys(sectionAssignments)
      });
      
      const response = await axios.post(
        `${API}/ftc-affidavit/download-pdf`,
        { 
          client_id: clientId,
          account_ids: allAssignedIds,
          section_assignments: sectionAssignments,
          statement: ftcStatement,
          display_format: ftcDisplayFormat || 'full'
        },
        { responseType: "blob", timeout: 120000 }
      );
      
      // Check if response is an error (JSON instead of PDF)
      if (response.data.type === 'application/json') {
        const errorText = await response.data.text();
        const errorJson = JSON.parse(errorText);
        toast.error(`FTC Generation Error: ${errorJson.detail || 'Unknown error'}`);
        setLoading(false);
        return;
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Extract filename from Content-Disposition header (contains report number)
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'IDTheftReport.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // AUTO-ADD to ALL 3 bureau folders (SAME blob)
      ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(b => {
        addToFolder(b, {
          blob,
          fileName: filename,
          docType: 'FTC_REPORT',
          addedAt: new Date().toISOString(),
          id: `FTC_REPORT_${b}_${Date.now()}`
        });
      });
      
      // Download SAME blob to computer
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('FTC Report downloaded & added to ALL 3 bureau folders!');
    } catch (error) {
      console.error('Error downloading FTC report:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Error downloading FTC report: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadFullPackage = async () => {
    try {
      setLoading(true);
      const accountIds = accounts.filter(a => a.status !== "removed").map(a => a.id);
      
      const response = await axios.post(
        `${API}/dispute-package/download-pdf`,
        { 
          client_id: clientId, 
          bureau: "equifax",
          account_ids: accountIds 
        },
        { responseType: "blob", timeout: 180000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Complete_Dispute_Package_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setPackageReady(true);
      toast.success("Complete dispute package downloaded!");
    } catch (error) {
      toast.error("Error downloading package");
    } finally {
      setLoading(false);
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Show dispute type selection first
        if (disputeType === null) {
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">What Type of Dispute?</h2>
                <p className="text-muted-foreground">Select the type of credit repair you need</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Option 1: Identity Theft / Full Removal */}
                <Card 
                  className="border-2 border-white/20 hover:border-red-500/50 cursor-pointer transition-all"
                  onClick={() => setDisputeType('identity_theft')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-8 h-8 text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Identity Theft / Full Removal</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Remove entire fraudulent accounts, collections, and unauthorized inquiries
                    </p>
                    <div className="text-left text-xs space-y-1">
                      <p className="flex items-center gap-2 text-red-400">
                        <CheckCircle2 className="w-3 h-3" /> Collections & Charge-offs
                      </p>
                      <p className="flex items-center gap-2 text-red-400">
                        <CheckCircle2 className="w-3 h-3" /> Fraudulent Accounts
                      </p>
                      <p className="flex items-center gap-2 text-red-400">
                        <CheckCircle2 className="w-3 h-3" /> Hard Inquiries
                      </p>
                      <p className="flex items-center gap-2 text-red-400">
                        <CheckCircle2 className="w-3 h-3" /> FTC Identity Theft Report
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 2: Inquiry Removal Only (NEW) */}
                <Card 
                  className="border-2 border-white/20 hover:border-purple-500/50 cursor-pointer transition-all"
                  onClick={() => setDisputeType('inquiry_only')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Inquiry Removal Only</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Remove unauthorized hard inquiries due to identity theft or fraud
                    </p>
                    <div className="text-left text-xs space-y-1">
                      <p className="flex items-center gap-2 text-purple-400">
                        <CheckCircle2 className="w-3 h-3" /> Hard Inquiry Removal
                      </p>
                      <p className="flex items-center gap-2 text-purple-400">
                        <CheckCircle2 className="w-3 h-3" /> Identity Theft Claims
                      </p>
                      <p className="flex items-center gap-2 text-purple-400">
                        <CheckCircle2 className="w-3 h-3" /> FTC Identity Theft Report
                      </p>
                      <p className="flex items-center gap-2 text-purple-400">
                        <CheckCircle2 className="w-3 h-3" /> Fast & Streamlined
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 3: Account Inaccuracies */}
                <Card 
                  className="border-2 border-white/20 hover:border-cyan-500/50 cursor-pointer transition-all"
                  onClick={() => setDisputeType('inaccuracies')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-cyan-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Account Inaccuracies</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Dispute any account using inaccuracies and consumer protection laws
                    </p>
                    <div className="text-left text-xs space-y-1">
                      <p className="flex items-center gap-2 text-cyan-400">
                        <CheckCircle2 className="w-3 h-3" /> FCRA Violations
                      </p>
                      <p className="flex items-center gap-2 text-cyan-400">
                        <CheckCircle2 className="w-3 h-3" /> Metro 2 Format Errors
                      </p>
                      <p className="flex items-center gap-2 text-cyan-400">
                        <CheckCircle2 className="w-3 h-3" /> Balance Inaccuracies
                      </p>
                      <p className="flex items-center gap-2 text-cyan-400">
                        <CheckCircle2 className="w-3 h-3" /> Date & Status Errors
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Option 4: Late Payments Only */}
                <Card 
                  className="border-2 border-white/20 hover:border-amber-500/50 cursor-pointer transition-all"
                  onClick={() => setDisputeType('late_payments')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Late Payment Removal Only</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Keep your accounts but remove late payment marks from history
                    </p>
                    <div className="text-left text-xs space-y-1">
                      <p className="flex items-center gap-2 text-amber-400">
                        <CheckCircle2 className="w-3 h-3" /> 30-Day Late Payments
                      </p>
                      <p className="flex items-center gap-2 text-amber-400">
                        <CheckCircle2 className="w-3 h-3" /> 60-Day Late Payments
                      </p>
                      <p className="flex items-center gap-2 text-amber-400">
                        <CheckCircle2 className="w-3 h-3" /> 90-Day Late Payments
                      </p>
                      <p className="flex items-center gap-2 text-amber-400">
                        <CheckCircle2 className="w-3 h-3" /> Payment History Errors
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          );
        }

        // Show client info form after dispute type selected
        return (
          <div className="space-y-6">
            {/* Dispute Type Badge */}
            <div className="flex justify-center">
              <Badge 
                className={
                  disputeType === 'identity_theft' ? 'bg-red-500/20 text-red-400' : 
                  disputeType === 'inquiry_only' ? 'bg-purple-500/20 text-purple-400' :
                  disputeType === 'inaccuracies' ? 'bg-cyan-500/20 text-cyan-400' :
                  'bg-amber-500/20 text-amber-400'
                }
                onClick={() => setDisputeType(null)}
                style={{ cursor: 'pointer' }}
              >
                {disputeType === 'identity_theft' ? '🛡️ Identity Theft Dispute' : 
                 disputeType === 'inquiry_only' ? '🔍 Inquiry Removal Only' :
                 disputeType === 'inaccuracies' ? '🔍 Account Inaccuracies Dispute' :
                 '📅 Late Payment Removal'}
                <span className="ml-2 text-xs">(click to change)</span>
              </Badge>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Enter Your Information</h2>
              <p className="text-muted-foreground">We need your details to create dispute letters</p>
            </div>
            
            {/* Smart Paste Button */}
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={() => setShowSmartPaste(!showSmartPaste)}
                className="w-full border-dashed border-2"
              >
                <FileText className="w-4 h-4 mr-2" />
                {showSmartPaste ? "Hide Paste Area" : "📋 Paste Client Info (Auto-Fill)"}
              </Button>
              
              {showSmartPaste && (
                <div className="mt-4 p-4 border rounded-lg bg-muted/30">
                  <Label className="mb-2 block">Paste client info in either format:</Label>
                  <div className="text-xs text-muted-foreground mb-2 bg-zinc-800 p-2 rounded">
                    <strong>Format 1 - With Labels (Preferred):</strong><br/>
                    NAME - John Doe<br/>
                    DATE OF BIRTH - 01/15/1985<br/>
                    SOCIAL SECURITY NUMBER - 123-45-6789<br/>
                    ADDRESS - 123 Main Street<br/>
                    City, ST 12345<br/>
                    PHONE NUMBER - 555-123-4567<br/>
                    EMAIL - john@example.com<br/><br/>
                    <strong>Format 2 - Line by Line:</strong><br/>
                    John Doe<br/>
                    01/15/1985<br/>
                    123-45-6789<br/>
                    123 Main Street<br/>
                    City, ST 12345<br/>
                    555-123-4567<br/>
                    john@example.com
                  </div>
                  <textarea
                    className="w-full p-3 border rounded-md bg-background min-h-[180px] font-mono text-sm"
                    placeholder={`NAME - Damien Salter
DATE OF BIRTH - 06/29/1978
SOCIAL SECURITY NUMBER - 561-53-6166
ADDRESS - 4202 S Victoria Ave
View Park, CA 90008
PHONE NUMBER - 323-209-8462
EMAIL - dsalterbeauty@aol.com`}
                    value={pastedClientInfo}
                    onChange={(e) => setPastedClientInfo(e.target.value)}
                    data-testid="smart-paste-textarea"
                  />
                  <div className="flex gap-2 mt-3">
                    <Button 
                      onClick={handleSmartPasteClientInfo}
                      disabled={parsingClientInfo || !pastedClientInfo.trim()}
                      className="flex-1"
                    >
                      {parsingClientInfo ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Parsing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Parse & Auto-Fill
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={() => {
                        setShowSmartPaste(false);
                        setPastedClientInfo("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <div className="flex gap-2">
                  <Input
                    id="name"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    placeholder="John Smith"
                    className="flex-1"
                  />
                  <Select 
                    value={clientForm.suffix || "none"} 
                    onValueChange={(val) => setClientForm({ ...clientForm, suffix: val === "none" ? "" : val })}
                  >
                    <SelectTrigger className="w-24" data-testid="suffix-select">
                      <SelectValue placeholder="Suffix" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="Jr.">Jr.</SelectItem>
                      <SelectItem value="Sr.">Sr.</SelectItem>
                      <SelectItem value="I">I</SelectItem>
                      <SelectItem value="II">II</SelectItem>
                      <SelectItem value="III">III</SelectItem>
                      <SelectItem value="IV">IV</SelectItem>
                      <SelectItem value="V">V</SelectItem>
                      <SelectItem value="VI">VI</SelectItem>
                      <SelectItem value="VII">VII</SelectItem>
                      <SelectItem value="VIII">VIII</SelectItem>
                      <SelectItem value="IX">IX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  type="text"
                  value={clientForm.date_of_birth}
                  onChange={(e) => setClientForm({ ...clientForm, date_of_birth: e.target.value })}
                  placeholder="MM/DD/YYYY"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={clientForm.address}
                  onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={clientForm.city}
                  onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                  placeholder="Los Angeles"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={clientForm.state}
                  onChange={(e) => setClientForm({ ...clientForm, state: e.target.value })}
                  placeholder="CA"
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={clientForm.zip_code}
                  onChange={(e) => setClientForm({ ...clientForm, zip_code: e.target.value })}
                  placeholder="90001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ssn">SSN (Last 4 digits)</Label>
                <Input
                  id="ssn"
                  value={clientForm.ssn_last4}
                  onChange={(e) => setClientForm({ ...clientForm, ssn_last4: e.target.value })}
                  placeholder="1234"
                  maxLength={4}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ssn_full" className="text-orange-400">Full SSN (For Declaration/FTC)</Label>
                <Input
                  id="ssn_full"
                  value={clientForm.ssn_full || ''}
                  onChange={(e) => {
                    // Format SSN as user types: XXX-XX-XXXX
                    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    if (value.length > 9) value = value.slice(0, 9);
                    if (value.length > 5) {
                      value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
                    } else if (value.length > 3) {
                      value = `${value.slice(0, 3)}-${value.slice(3)}`;
                    }
                    setClientForm({ ...clientForm, ssn_full: value });
                  }}
                  onBlur={async () => {
                    // Auto-save when user leaves the field
                    if (clientId && clientId !== "new" && clientForm.ssn_full) {
                      try {
                        await axios.put(`${API}/clients/${clientId}`, clientForm);
                        toast.success("SSN saved");
                      } catch (e) {
                        console.error("Failed to save SSN:", e);
                      }
                    }
                  }}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  className="border-orange-500/30"
                  data-testid="full-ssn-input"
                />
                <p className="text-xs text-zinc-500">Required for Declaration and FTC Report. Auto-saves when you click away.</p>
              </div>
            </div>
            
{/* Cover section removed - available in Mail Packets step */}
          </div>
        );

      // ═══════════════════════════════════════════════════════════════════
      // STEP 2: SIGNATURE SELECTION (Choose from 5 cursive signatures)
      // ═══════════════════════════════════════════════════════════════════
      case 2:
        return (
          <CursiveSignatureSelector
            clientName={client?.name || `${client?.first_name || ''} ${client?.last_name || ''}`.trim()}
            selectedStyle={signatureStyle}
            onStyleChange={(styleId) => setSignatureStyle(styleId)}
            onSignatureGenerated={(data) => {
              setSignatureStyleName(data.styleName);
              setSignatureImageData(data.imageData);
              setSignatureData(data.imageData);
            }}
            onComplete={(data) => {
              setSignatureStyle(data.styleId);
              setSignatureStyleName(data.styleName);
              setSignatureImageData(data.imageData);
              setSignatureData(data.imageData);
              setCurrentStep(3);
            }}
          />
        );

      // ═══════════════════════════════════════════════════════════════════
      // STEP 3: HANDWRITTEN DECLARATION + COVER LETTER (Now BEFORE Import)
      // ═══════════════════════════════════════════════════════════════════
      case 3:
        return (
          <SignDeclarationPage
            clientId={clientId}
            clientData={client}
            signatureStyle={signatureStyle}
            signatureImageData={signatureImageData}
            onSignatureChange={(sig) => setSignatureData(sig)}
            onComplete={() => setCurrentStep(4)}
          />
        );

      // ═══════════════════════════════════════════════════════════════════
      // STEP 4: UNIFIED IMPORT & PASTE (All credit data in ONE place)
      // This combines: Client Info Paste + Quick Paste Parser + Credit Scores + Manual Entry
      // ═══════════════════════════════════════════════════════════════════
      case 4:
        return (
          <CombinedPasteStep
            clientId={clientId}
            client={client}
            setClient={setClient}
            existingAccounts={accounts}
            showClientInfo={true}
            showAccountParser={true}
            manualScores={manualScores}
            setManualScores={setManualScores}
            bureauUploadStatus={bureauUploadStatus}
            onSaveScores={async (scores) => {
              try {
                await axios.patch(`${API}/clients/${clientId}`, {
                  starting_score_equifax: scores.equifax ? parseInt(scores.equifax) : null,
                  starting_score_experian: scores.experian ? parseInt(scores.experian) : null,
                  starting_score_transunion: scores.transunion ? parseInt(scores.transunion) : null
                });
                setBureauUploadStatus(prev => ({
                  equifax: { ...prev.equifax, score: scores.equifax || prev.equifax?.score },
                  experian: { ...prev.experian, score: scores.experian || prev.experian?.score },
                  transunion: { ...prev.transunion, score: scores.transunion || prev.transunion?.score },
                }));
                toast.success('Credit scores saved');
              } catch (error) {
                console.error('Error saving scores:', error);
                toast.error('Failed to save scores');
              }
            }}
            onAccountsParsed={async (newAccounts) => {
              // Refresh accounts list from server
              try {
                const accountsRes = await axios.get(`${API}/accounts?client_id=${clientId}&dedupe=false`);
                setAccounts(deduplicateAccounts(accountsRes.data || []));
                toast.success(`${newAccounts.length} accounts added`);
              } catch (error) {
                console.error('Error refreshing accounts:', error);
                toast.success(`${newAccounts.length} accounts parsed`);
              }
            }}
          />
        );

      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 5: DISPUTE ACCOUNTS (with bureau selection per account)
      // For Late Payments: Use dedicated LatePaymentRemovalPage (NEW PRD)
      // ═══════════════════════════════════════════════════════════════════
      case 5:
        // For late payments, show the NEW Late Payment Removal page with paste functionality
        if (disputeType === 'late_payments') {
          return (
            <LatePaymentRemovalPage
              clientId={clientId}
              clientData={client}
              signatureData={signatureData}
              onComplete={() => {
                setCurrentStep(6);
              }}
              onLettersGenerated={() => setDisputeLettersGenerated(true)}
            />
          );
        }
        
        // Default: Standard dispute accounts page
        return (
          <DisputeAccountsPageNew
            clientId={clientId}
            clientData={client}
            signatureData={signatureData}
            onComplete={() => {
              setCurrentStep(7);
            }}
            onAccountsChange={handleAccountsChange}
            onLettersGenerated={() => setDisputeLettersGenerated(true)}
          />
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 6: SUPPLEMENTAL LETTERS
      // Add specialized dispute letters based on pasted accounts
      // ═══════════════════════════════════════════════════════════════════
      case 6:
        return (
          <SupplementalLettersPage
            clientId={clientId}
            clientData={client}
            signatureData={signatureData}
            availableAccounts={selectedAccountsForDispute.length > 0 ? selectedAccountsForDispute : accounts}
            availableInquiries={selectedInquiriesForDispute}
            hasBankruptcy={hasBankruptcy}
            disputeType={disputeType}
            onComplete={() => {
              setSupplementalLettersGenerated(true);
              setCurrentStep(8);
            }}
            onLettersGenerated={(letterId, data) => {
              setGeneratedSupplementalLetters(prev => ({
                ...prev,
                [letterId]: data
              }));
            }}
          />
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 7: INQUIRIES DISPUTE
      // ═══════════════════════════════════════════════════════════════════
      case 7:
        return (
          <InquiriesDisputePageNew
            clientId={clientId}
            clientData={client}
            signatureData={signatureData}
            onComplete={() => {
              setCurrentStep(8);
            }}
            onInquiriesChange={(inqs) => setSelectedInquiriesForDispute(inqs)}
            onLettersGenerated={() => setInquiryLettersGenerated(true)}
          />
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 8: FTC REPORT BUILDER
      // ═══════════════════════════════════════════════════════════════════
      case 8:
        return (
          <div className="space-y-6">
            {/* Theme Toggle */}
            <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
              <label className="text-sm font-semibold text-zinc-300 mb-3 block">FTC Report Style</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ftcThemeWizard"
                    value="bw"
                    checked={ftcTheme === 'bw'}
                    onChange={(e) => setFtcTheme(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-zinc-300 text-sm">Black & White</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ftcThemeWizard"
                    value="color"
                    checked={ftcTheme === 'color'}
                    onChange={(e) => setFtcTheme(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-zinc-300 text-sm">Official Color - Default (FTC-style)</span>
                </label>
              </div>
            </div>
            
            <FTCReportBuilderNew
              clientId={clientId}
              clientData={client}
              signatureData={signatureData}
              selectedAccounts={selectedAccountsForDispute}
              ftcTheme={ftcTheme}
              onComplete={() => {
                setFtcReportGenerated(true);
                setCurrentStep(9);
              }}
            />
          </div>
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 9: BANKRUPTCY DISPUTE
      // ═══════════════════════════════════════════════════════════════════
      case 9:
        return (
          <BankruptcyDisputePage
            clientId={clientId}
            clientForm={clientForm}
            hasBankruptcy={hasBankruptcy}
            setHasBankruptcy={setHasBankruptcy}
            bankruptcyLetterGenerated={bankruptcyLetterGenerated}
            setBankruptcyLetterGenerated={setBankruptcyLetterGenerated}
            signatureData={signatureData}
            selectedBureaus={['experian', 'equifax', 'transunion']}
            onStepComplete={(complete) => {
              if (complete) {
                setBankruptcyLetterGenerated(true);
              }
            }}
          />
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 10: SUPPORTING DOCUMENTS (ID and SSN Card - Always shown)
      // ═══════════════════════════════════════════════════════════════════
      case 10:
        return (
          <SupportingDocumentsPage
            clientId={clientId}
            clientData={client}
            onIdUploaded={() => setIdDocumentUploaded(true)}
            onSsnUploaded={() => setSsnCardUploaded(true)}
            onComplete={() => setCurrentStep(11)}
            idDocumentUploaded={idDocumentUploaded}
            ssnCardUploaded={ssnCardUploaded}
          />
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 11: FINAL REVIEW
      // ═══════════════════════════════════════════════════════════════════
      case 11:
        return (
          <FinalReviewPage
            clientId={clientId}
            clientData={client}
            signatureData={signatureData}
            selectedAccounts={selectedAccountsForDispute}
            selectedInquiries={selectedInquiriesForDispute}
            ftcReportGenerated={ftcReportGenerated}
            ftcTheme={ftcTheme}
            hasBankruptcy={hasBankruptcy}
            bankruptcyLetterGenerated={bankruptcyLetterGenerated}
            idDocumentUploaded={idDocumentUploaded}
            ssnCardUploaded={ssnCardUploaded}
            disputeLettersGenerated={disputeLettersGenerated}
            inquiryLettersGenerated={inquiryLettersGenerated}
            supplementalLettersGenerated={supplementalLettersGenerated}
            onComplete={() => setCurrentStep(12)}
            onNavigateToStep={(step) => setCurrentStep(step)}
          />
        );
      
      // ═══════════════════════════════════════════════════════════════════
      // STEP 12: MAIL PACKETS (with Lob integration)
      // ═══════════════════════════════════════════════════════════════════
      case 12:
        return (
          <MailPacketsPage
            clientId={clientId}
            clientData={client}
            bureauFolders={bureauFolders}
            onRemoveDocument={removeFromFolder}
          />
        );

      default:
        return null;
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-background p-6 pb-24">
      {/* Mobile Navigation Menu */}
      <MobileNav />
      
      {/* Progress Report Prompt Modal */}
      {showProgressPrompt && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4 border-blue-500/30 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-t-lg">
              <CardTitle className="flex items-center gap-3 text-white">
                <FileText className="w-6 h-6 text-blue-400" />
                Progress Report Available
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-muted-foreground">
                You have <span className="text-blue-400 font-semibold">{previousCyclesCount} completed dispute cycle{previousCyclesCount > 1 ? 's' : ''}</span> for this client.
              </p>
              <p className="text-white">
                Would you like to generate a progress report comparing this dispute cycle to the previous one?
              </p>
              
              <div className="bg-white/5 p-4 rounded-lg space-y-2 text-sm">
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Compare credit scores before vs after</span>
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>See accounts removed, updated, remaining</span>
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Track disputes sent vs resolved</span>
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Visual progress indicators</span>
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={startDisputeCycle}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Yes, Start New Cycle
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowProgressPrompt(false)}
                  className="flex-1 border-white/20"
                >
                  Skip for Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Workflow Suggestions Banner */}
      {workflowSuggestions.length > 0 && currentStep === 5 && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm">Suggestions for this client</p>
              <ul className="mt-1 space-y-1">
                {workflowSuggestions.slice(0, 2).map((suggestion, idx) => (
                  <li key={idx} className="text-sm text-amber-200/80">
                    • {suggestion.message}
                  </li>
                ))}
              </ul>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWorkflowSuggestions([])}
              className="ml-auto text-amber-400 hover:text-amber-300"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header with Exit Button */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/images/clear-all-wizard-logo.png" 
              alt="Clear All Wizard" 
              className="w-20 h-20 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold mb-2">Clear All Wizard</h1>
              <p className="text-muted-foreground">Complete each step to create your dispute package</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-white/20 hover:bg-red-500/10 hover:border-red-500/30"
          >
            <Home className="w-4 h-4 mr-2" />
            Exit to Dashboard
          </Button>
        </div>

        {/* Credit Score Display Bar - Shows extracted scores prominently */}
        {(client?.score_equifax || client?.score_experian || client?.score_transunion || 
          bureauUploadStatus.equifax.score || bureauUploadStatus.experian.score || bureauUploadStatus.transunion.score) && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-white/10 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <span className="font-semibold text-white">Credit Scores</span>
              </div>
              <div className="flex gap-6">
                {/* Equifax */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">Equifax</p>
                  <p className={`text-xl font-bold ${getScoreColor(bureauUploadStatus.equifax.score || client?.score_equifax)}`}>
                    {bureauUploadStatus.equifax.score || client?.score_equifax || '—'}
                  </p>
                </div>
                {/* Experian */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">Experian</p>
                  <p className={`text-xl font-bold ${getScoreColor(bureauUploadStatus.experian.score || client?.score_experian)}`}>
                    {bureauUploadStatus.experian.score || client?.score_experian || '—'}
                  </p>
                </div>
                {/* TransUnion */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">TransUnion</p>
                  <p className={`text-xl font-bold ${getScoreColor(bureauUploadStatus.transunion.score || client?.score_transunion)}`}>
                    {bureauUploadStatus.transunion.score || client?.score_transunion || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-muted-foreground">Step {currentStep} of {STEPS.length}</span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((step) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isPast = currentStep > step.id;
            const isComplete = getStepCompletionStatus(step.id);
            
            // Check if step is skipped for inquiry_only type
            const isSkipped = disputeType === 'inquiry_only' && [2, 4, 7].includes(step.id);
            
            // Don't render skipped steps
            if (isSkipped) {
              return null;
            }
            
            // Allow clicking on past OR completed steps
            const isClickable = isPast || isComplete;
            
            return (
              <div
                key={step.id}
                className={`flex flex-col items-center min-w-[80px] ${
                  isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
                } ${
                  isActive ? "text-primary" : isComplete ? "text-green-500" : isPast ? "text-orange-400" : "text-muted-foreground"
                }`}
                onClick={() => isClickable && setCurrentStep(step.id)}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isComplete
                      ? "bg-green-500 text-white"
                      : isPast
                      ? "bg-orange-500 text-white"
                      : "bg-secondary"
                  }`}
                >
                  {isComplete ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                </div>
                <span className="text-xs text-center hidden md:block">{step.title}</span>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <Card className="border-white/10 shadow-xl mb-8">
          <CardContent className="p-8">
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>

      {/* Navigation Buttons - FIXED to bottom LEFT corner to avoid Emergent logo */}
      {/* Hide on Step 2 since bureau upload has its own navigation */}
      {currentStep !== 2 && (
        <div className="fixed bottom-6 left-6 flex gap-3 z-50">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="border-white/10 bg-background shadow-lg"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {/* MANDATORY SAVE BUTTON - Shows when step has unsaved changes */}
          {stepSaveStatus[currentStep]?.isDirty && !stepSaveStatus[currentStep]?.isSaved && (
            <Button 
              onClick={saveCurrentStep}
              disabled={savingStep}
              className="bg-yellow-600 hover:bg-yellow-700 shadow-lg animate-pulse"
            >
              {savingStep ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Progress
                </>
              )}
            </Button>
          )}
          
          {currentStep < STEPS.length ? (
            <Button 
              onClick={handleNext} 
              disabled={loading || !canProceedToNext()} 
              className={`shadow-lg ${canProceedToNext() ? 'glow-primary' : 'bg-gray-500 cursor-not-allowed'}`}
              title={!canProceedToNext() ? 'Save your progress before continuing' : ''}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={() => navigate(`/clients/${clientId}`)} 
              className="bg-green-600 hover:bg-green-700 shadow-lg"
            >
                <Check className="w-4 h-4 mr-2" />
                Finish & View Client
              </Button>
            )}
          </div>
      )}

      {/* Score Input Modal - appears when scores not detected */}
      <ScoreInputModal
        isOpen={showScoreModal}
        onClose={() => setShowScoreModal(false)}
        onSave={async (scores) => {
          try {
            // Save scores to backend
            await axios.patch(`${API}/clients/${clientId}`, {
              score_equifax: scores.equifax || null,
              score_experian: scores.experian || null,
              score_transunion: scores.transunion || null,
            });
            
            // Update local state
            setBureauUploadStatus(prev => ({
              equifax: { ...prev.equifax, score: scores.equifax || prev.equifax.score },
              experian: { ...prev.experian, score: scores.experian || prev.experian.score },
              transunion: { ...prev.transunion, score: scores.transunion || prev.transunion.score },
            }));
            
            setManualScores({
              equifax: scores.equifax?.toString() || '',
              experian: scores.experian?.toString() || '',
              transunion: scores.transunion?.toString() || '',
            });
            
            toast.success('Credit scores saved successfully');
          } catch (error) {
            console.error('Error saving scores:', error);
            toast.error('Failed to save scores');
          }
        }}
        currentScores={{
          equifax: bureauUploadStatus.equifax.score,
          experian: bureauUploadStatus.experian.score,
          transunion: bureauUploadStatus.transunion.score,
        }}
        missingBureaus={missingScoreBureaus}
      />

      {/* Client History Selector Modal */}
      <SectionClientSelector
        sectionType="dispute_wizard"
        isOpen={showClientSelector}
        onClose={() => setShowClientSelector(false)}
        onSelectClient={handleSelectPreviousClient}
        onCreateNew={handleCreateNewClient}
        onCreateWithData={handleCreateWithParsedData}
      />

      {/* Feb 2026 — Bureau Paper Form prompt.  Appears automatically
          after the dispute letters download.  Lets the user pre-fill
          the official TransUnion Investigation Request and/or the
          Equifax Dispute Request paper forms with the same identity
          and account data that just went out on the dispute letters. */}
      {bureauFormsPromptOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="bureau-forms-prompt"
          onClick={() => !bureauFormsBusy && setBureauFormsPromptOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Also generate the official bureau paper forms?</h3>
                <p className="text-sm text-zinc-400 mt-1">
                  We can pre-fill the TransUnion Investigation Request and the Equifax Dispute Request with the same client info and the accounts on your dispute letters.
                </p>
                <ul className="mt-3 text-xs text-zinc-500 list-disc list-inside space-y-1">
                  <li>Signature and dispute-details wording copied from your letter (varied per client)</li>
                  <li>Multi-form auto-split: TU fits 4 accounts / form, Equifax fits 12</li>
                  <li>"Account Not Mine" checked by default; Driver's License marked on Equifax</li>
                </ul>
              </div>
              {!bureauFormsBusy && (
                <button
                  onClick={() => setBureauFormsPromptOpen(false)}
                  className="text-zinc-400 hover:text-white ml-2"
                  data-testid="bureau-forms-close-btn"
                  aria-label="Close"
                >×</button>
              )}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Which bureau?</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'both', label: 'Both',       bureaus: ["transunion","equifax"], activeCls: 'bg-emerald-600/25 border-emerald-500 text-white' },
                  { id: 'tu',   label: 'TransUnion', bureaus: ["transunion"],           activeCls: 'bg-blue-600/25 border-blue-500 text-white' },
                  { id: 'eq',   label: 'Equifax',    bureaus: ["equifax"],              activeCls: 'bg-red-600/25 border-red-500 text-white' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setBureauFormsChoice(opt)}
                    disabled={bureauFormsBusy}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      bureauFormsChoice.id === opt.id
                        ? opt.activeCls
                        : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
                    }`}
                    data-testid={`bureau-forms-bureau-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                onClick={() => runBureauFormsFill(bureauFormsChoice.bureaus, { editFirst: true })}
                disabled={bureauFormsBusy}
                variant="outline"
                className="bg-zinc-800 border-fuchsia-500/60 text-fuchsia-200 hover:bg-fuchsia-500/20 h-11"
                data-testid="bureau-forms-preview-edit"
              >
                👁 Preview / Edit
              </Button>
              <Button
                onClick={() => runBureauFormsFill(bureauFormsChoice.bureaus)}
                disabled={bureauFormsBusy}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-11"
                data-testid="bureau-forms-generate"
              >
                {bureauFormsBusy ? "Working…" : "⬇ Generate & Download"}
              </Button>
            </div>
            <div className="mt-3 text-right">
              <button
                type="button"
                onClick={() => setBureauFormsPromptOpen(false)}
                disabled={bureauFormsBusy}
                className="text-xs text-zinc-500 hover:text-zinc-300 underline"
                data-testid="bureau-forms-skip-btn"
              >
                Skip — I don't need these forms
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 — Free-form editor for the generated bureau PDF.  Opens
          only when the user picked "Edit …" from the modal above.  When
          BOTH bureaus are picked, we queue up the second one so the user
          gets to edit each in turn. */}
      <BureauFormEditor
        open={bureauEditorOpen}
        onClose={advanceBureauEditorQueue}
        basePdfDataUrl={bureauEditorPdf}
        filename={bureauEditorFilename}
        bureauLabel={bureauEditorLabel}
      />
    </div>
  );
}
